import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Alert, Badge, Button, Card, Col, Container, Dropdown, Form, InputGroup, Modal, Row, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import API_BASE_URL from "../config/apiConfig";
import MyIncentiveCard from "./MyIncentiveCard";
import { calculateTeamTripQuote, fetchExtensionRoomAvailability, fetchTripQuoteOptions, fetchTripRoomAvailability } from "../services/priceService";
import { generatePaymentReceipt } from "../utils/generatePaymentReceipt";
import { generateTravelPolicy } from "../utils/generateTravelPolicy";
import { tripStayDates, vehicleExtensionDays, vehicleTripDates } from "../utils/vehicleTripDates";
import { combinedVehicle, vehicleNamesLabel, vehiclesForIds } from "../utils/quoteVehicles";
import { paymentSchedule } from "../utils/paymentSchedule";
import { stayInclusionLines } from "../utils/stayInclusions";
import { CallNeededModal, CallNeededTiming, CallRequestTimeField, NotYetCalledWaiting, statusFollowUpMinutes, useCallNeededReminderPopups } from "../components/leads/CallNeeded";
import { FlagCountsLine, FlagReminderBadge, FlagViewBar, LeadFlagModal, flagFilterLabel, flagSummaryText, isFlagFilter, myLeadFlag, toReminderInput, useFlagReminderPopups } from "../components/leads/LeadFlags";
import {
  assignCallTeamLead,
  createTeamLead,
  connectLeadEvents,
  clickToCall,
  getMyUnlinkedCalls,
  createLeadBooking,
  createTeamAttendanceRequest,
  convertLeadToBooking,
  uploadLeadItinerary,
  cancelTeamAttendanceRequest,
  endTeamBreak,
  endTeamDay,
  endLeadWork,
  flagLead,
  unflagLead,
  getCurrentTeamBreak,
  getCurrentTeamDay,
  getCurrentLeadWork,
  getTeamWorkStatus,
  getTeamBreaksToday,
  getTeamLeadChanges,
  getTeamLeadHistory,
  getTeamLeadCalls,
  getTeamLeadWorkSessionsToday,
  getTeamHotelImageCopyFile,
  getTeamHotelImageCopySourceFile,
  getTeamHotelImageFile,
  listTeamHotelImages,
  listTeamInterestStatuses,
  listLeadBookingOptions,
  listCallTeamMembers,
  listTeamConversionMembers,
  listTeamAttendanceRequests,
  listTeamLeadsPage,
  listTeamDailyUpdateSuggestions,
  loginLeadTeam,
  markManualCallOutcome,
  startTeamBreak,
  startTeamDay,
  startLeadWork,
  syncTeamLeads,
  updateLeadCallNeeded,
  updateTeamLead,
  validateLeadTeam,
} from "../services/leadTrackerApi";
import LeadWorkTimelineCard from "../components/leads/LeadWorkTimelineCard";
import StaffThemeSwitch, { useStaffTheme } from "../components/StaffThemeSwitch";
import { CallerName, NO_EXTENSION_MESSAGE, UnlinkedCallsPanel } from "../components/leads/UnlinkedCalls";

const chatFields = ["interestStatus", "nextFollowUpDateTime", "customerNumber", "customerLanguage", "categoryId", "destinationName", "plannedDates", "numberOfPersons", "numberOfKids", "pickupType", "addons", "dailyUpdates"];
const callFields = ["interestStatus", "assignedCallMemberId", "nextFollowUpDateTime", "customerNumber", "alternateNumber", "customerLanguage", "categoryId", "destinationName", "plannedDates", "numberOfPersons", "numberOfKids", "pickupType", "addons", "dailyUpdates"];
const leadDeviceStorageKey = "leadTrackerDeviceToken";

const labels = {
  staffName: "Staff Name", customerNumber: "Customer Number", alternateNumber: "Alternate Number", customerLanguage: "Customer Preferred Language", categoryId: "Category", destinationName: "Destination Name", pickupType: "Pickup Type", addons: "Add-ons", plannedDates: "Planned Date", numberOfPersons: "Number of Adults", numberOfKids: "Number of Kids", kidsAge: "Kids Age", detailsSent: "Details Sent", isCallDone: "Call Done", frequency: "Successful Calls", interestStatus: "Interest Status", finalStatus: "Final Status", dailyUpdates: "Daily Updates", followUpCategory: "Follow-up Category", nextFollowUpDateTime: "Next Follow-up Date/Time", assignedCallMemberId: "Assigned To",
};
const defaultInterest = ["Partial Data Got from Customer", "Waiting for Customer Reply", "Customisation Request"];
const languageOptionPromises = {};
const loadEditorLanguages = (team) => {
  if (!languageOptionPromises[team]) {
    const tokenKey = team === "ADMIN" ? "adminToken" : team === "CALL" ? "callTeamToken" : "chatTeamToken";
    const path = team === "ADMIN" ? "/api/admin/lead-tracker/languages" : team === "CALL" ? "/api/call-team/languages" : "/api/chat-team/languages";
    languageOptionPromises[team] = axios.get(`${API_BASE_URL}${path}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(tokenKey) || ""}` },
    }).then((response) => (response.data || []).filter((item) => item.active !== false)).catch(() => []);
  }
  return languageOptionPromises[team];
};
const blankLead = { staffName: "", customerNumber: "", alternateNumber: "", customerLanguage: "", categoryId: "", destinationName: "", pickupType: "", addons: "", plannedDates: "", numberOfPersons: "", numberOfKids: "0", kidsAge: "", detailsSent: "No", isCallDone: "No", frequency: 0, unresponsiveCallFrequency: 0, assignedCallMemberId: 0, bookingCustomerName: "", bookingCategoryId: "", bookingTripId: "", dynamicQuoteRequest: null, dynamicPackageTotal: "", customizationRequired: "No", customizationDescription: "", customizationPrice: "", customizationPriceAdjustment: "ADD", advancePayment: "", bookingNotes: "", pickupDateTime: "", dropDateTime: "", refreshmentNeeded: false, refreshmentRoomId: "", driverLanguages: [], conversionCredits: [], receiptNumber: "", receiptDownloadedFingerprint: "", paymentReceiptShared: false, policyDownloadedFingerprint: "", travelPolicyShared: false, cashbackAmountPerPerson: "", totalCashbackAmount: "", interestStatus: "Partial Data Got from Customer", finalStatus: "", dailyUpdates: "", chatTeamPersonNotes: "", followUpCategory: "Open", nextFollowUpDateTime: "", flagRequested: false, flagNote: "", flagRemindAt: "", callNeeded: false, callNeededNote: "" };

const DEFAULT_LEAD_PAGE_SIZE = 50;
const LEAD_PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const emptyLeadPage = () => ({ content: [], page: 0, size: DEFAULT_LEAD_PAGE_SIZE, totalElements: 0, totalPages: 0, summary: null });
// Built from local date parts, not toISOString(): the backend expects a plain yyyy-MM-dd, and an
// ISO timestamp both fails to parse and shifts the first of the month back a day in IST.
const toLocalDateValue = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const defaultAttendanceRange = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return { fromDate: toLocalDateValue(firstDay), toDate: toLocalDateValue(lastDay) };
};
const normalizeLeadPage = (data) => ({
  content: data?.content || [],
  page: Number(data?.page || 0),
  size: Number(data?.size || DEFAULT_LEAD_PAGE_SIZE),
  totalElements: Number(data?.totalElements || 0),
  totalPages: Number(data?.totalPages || 0),
  summary: data?.summary || null,
});

export default function LeadTeamPortal({ team }) {
  const storageKey = team === "CALL" ? "callTeamToken" : "chatTeamToken";
  const title = team === "CALL" ? "Call Team Lead Tracker" : "Chat Team Lead Tracker";
  const [theme, setTheme] = useStaffTheme();
  const [status, setStatus] = useState("checking");
  const [member, setMember] = useState(null);
  const [leadPage, setLeadPage] = useState(emptyLeadPage);
  const [leadPageIndex, setLeadPageIndex] = useState(0);
  const [leadPageSize, setLeadPageSize] = useState(DEFAULT_LEAD_PAGE_SIZE);
  const [leadTab, setLeadTab] = useState("workable");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [destinationFilter, setDestinationFilter] = useState([]);
  const [assignmentFilter, setAssignmentFilter] = useState("ALL");
  const [leadSort, setLeadSort] = useState({ key: "lastUpdated", dir: "desc" });
  const [categories, setCategories] = useState([]);
  const [interestStatuses, setInterestStatuses] = useState(defaultInterest);
  const [interestStatusOptions, setInterestStatusOptions] = useState([]);
  const [callMembers, setCallMembers] = useState([]);
  const [conversionMembers, setConversionMembers] = useState([]);
  const [dailyUpdateSuggestions, setDailyUpdateSuggestions] = useState([]);
  const [bookingOptions, setBookingOptions] = useState([]);
  const [bookingOptionsLoaded, setBookingOptionsLoaded] = useState(false);
  const [bookingOptionsLoading, setBookingOptionsLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [summaryFilter, setSummaryFilter] = useState("");
  const [flagModalLead, setFlagModalLead] = useState(null);
  const [flagSaving, setFlagSaving] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [currentBreak, setCurrentBreak] = useState(null);
  const [currentDay, setCurrentDay] = useState(null);
  const [workStatus, setWorkStatus] = useState(null);
  const [currentLeadWork, setCurrentLeadWork] = useState(null);
  const [newLeadStartedAt, setNewLeadStartedAt] = useState("");
  const [showMyInsights, setShowMyInsights] = useState(false);
  const [showAttendanceRequest, setShowAttendanceRequest] = useState(false);
  const [attendanceRequests, setAttendanceRequests] = useState([]);
  const [attendanceRange, setAttendanceRange] = useState(() => defaultAttendanceRange());
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [myInsightsLoading, setMyInsightsLoading] = useState(false);
  const [myLeadWorkToday, setMyLeadWorkToday] = useState([]);
  const [myBreaksToday, setMyBreaksToday] = useState([]);
  const [breakBusy, setBreakBusy] = useState(false);
  const [dayBusy, setDayBusy] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [customerMessage, setCustomerMessage] = useState(null);
  const [callNeededSavingId, setCallNeededSavingId] = useState(null);
  const [callNeededModalLead, setCallNeededModalLead] = useState(null);
  const [callingNumber, setCallingNumber] = useState("");
  const [unlinkedCalls, setUnlinkedCalls] = useState(null);
  const [unlinkedLoading, setUnlinkedLoading] = useState(false);
  const [newLeadDefaults, setNewLeadDefaults] = useState(null);
  const leadRowsRef = useRef([]);
  const allLeadsTabRef = useRef(false);
  const lastPageReloadRef = useRef(0);
  const token = () => localStorage.getItem(storageKey);
  // Calls only ever go out on the extension an administrator assigned; there is no picking one.
  const ownExtension = String(member?.cloudConnectExtension || "").trim();

  useEffect(() => { leadRowsRef.current = leadPage.content; }, [leadPage]);
  useEffect(() => { allLeadsTabRef.current = leadTab === "all"; }, [leadTab]);

  // Selected categories resolved to destination names: leads created before the category column existed carry only a
  // destination, and the backend needs both to reproduce what the old client-side category filter matched.
  const categoryDestinations = useMemo(() => {
    if (!categoryFilter.length) return [];
    const selected = new Set(categoryFilter);
    return categories
      .filter((category) => selected.has(String(category.id)))
      .flatMap((category) => category.trips || [])
      .map((trip) => String(trip.title || "").trim())
      .filter(Boolean);
  }, [categories, categoryFilter]);

  const leadQuery = useMemo(() => ({
    all: leadTab === "all",
    page: leadPageIndex,
    size: leadPageSize,
    sortBy: leadSort.key,
    sortDir: leadSort.dir,
    q: searchTerm || undefined,
    interestStatus: statusFilter.length ? statusFilter : undefined,
    categoryId: categoryFilter.length ? categoryFilter : undefined,
    categoryDestination: categoryDestinations.length ? categoryDestinations : undefined,
    destination: destinationFilter.length ? destinationFilter : undefined,
    assignment: team === "CALL" && assignmentFilter !== "ALL" ? assignmentFilter : undefined,
    summaryFilter: summaryFilter || undefined,
  }), [leadTab, leadPageIndex, leadPageSize, leadSort, searchTerm, statusFilter, categoryFilter,
    categoryDestinations, destinationFilter, assignmentFilter, summaryFilter, team]);
  const leadQueryRef = useRef(leadQuery);
  useEffect(() => { leadQueryRef.current = leadQuery; }, [leadQuery]);

  // Filter changes always restart at the first page, and they are applied together with the filter itself so the
  // query object only changes once per interaction.
  const withPageReset = (apply) => (value) => { apply(value); setLeadPageIndex(0); };
  const changeStatusFilter = withPageReset(setStatusFilter);
  const changeDestinationFilter = withPageReset(setDestinationFilter);
  const changeAssignmentFilter = withPageReset(setAssignmentFilter);
  const changeSort = withPageReset(setLeadSort);
  // Flag views open sorted by follow-up time; leaving one drops that sort.
  // Two views bring their own order: flags by reminder, and Not Yet Called longest-waiting first.
  // Leaving the view puts the usual order back.
  const changeSummaryFilter = (value) => {
    const enteringFlagView = isFlagFilter(value) && !isFlagFilter(summaryFilter);
    const enteringNotYetCalled = value === "notYetCalled" && summaryFilter !== "notYetCalled";
    setSummaryFilter(value);
    if (enteringFlagView) setLeadSort({ key: "flagReminder", dir: "asc" });
    else if (enteringNotYetCalled) setLeadSort({ key: "notYetCalledWaiting", dir: "asc" });
    else setLeadSort((current) => ((current.key === "flagReminder" && !isFlagFilter(value))
      || (current.key === "notYetCalledWaiting" && value !== "notYetCalled")
      ? { key: "lastUpdated", dir: "desc" }
      : current));
    setLeadPageIndex(0);
  };
  const changePageSize = withPageReset(setLeadPageSize);
  const changeCategoryFilter = (values) => {
    const selected = new Set(values);
    const allowed = (values.length ? categories.filter((category) => selected.has(String(category.id))) : categories)
      .flatMap((category) => category.trips || [])
      .map((trip) => String(trip.title || "").trim())
      .filter(Boolean);
    setCategoryFilter(values);
    setDestinationFilter((old) => old.filter((destination) => allowed.includes(destination)));
    setLeadPageIndex(0);
  };
  const clearLeadFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    searchTermRef.current = "";
    setStatusFilter([]);
    setCategoryFilter([]);
    setDestinationFilter([]);
    setAssignmentFilter("ALL");
    setSummaryFilter("");
    setLeadPageIndex(0);
  };

  // Typing no longer refetches on every keystroke; the committed search term and the page reset land in one update so
  // the query object changes exactly once.
  const searchTermRef = useRef("");
  useEffect(() => {
    const next = searchInput.trim();
    if (next === searchTermRef.current) return undefined;
    const timer = window.setTimeout(() => {
      searchTermRef.current = next;
      setSearchTerm(next);
      setLeadPageIndex(0);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadLookups = async () => {
    const [categoryRes, statusRows, dailyUpdateSuggestionRows, callMemberRows, conversionMemberRows] = await Promise.all([axios.get(`${API_BASE_URL}/categories`), listTeamInterestStatuses(team, token()), listTeamDailyUpdateSuggestions(team, token()), team === "CALL" ? listCallTeamMembers(token()) : Promise.resolve([]), listTeamConversionMembers(team, token())]);
    setCategories(categoryRes.data || []);
    setInterestStatusOptions(statusRows || []);
    setCallMembers(callMemberRows || []);
    setConversionMembers(conversionMemberRows || []);
    setDailyUpdateSuggestions(dailyUpdateSuggestionRows || []);
    const names = ensureCallFollowUpStatus((statusRows || []).map((row) => row.name).filter(Boolean), team);
    setInterestStatuses(names.length ? names : defaultInterest);
  };
  const ensureBookingOptions = async () => {
    if (bookingOptionsLoaded || bookingOptionsLoading) return;
    setBookingOptionsLoading(true);
    try {
      const rows = await listLeadBookingOptions(team, token());
      setBookingOptions(rows || []);
      setBookingOptionsLoaded(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load booking options");
    } finally {
      setBookingOptionsLoading(false);
    }
  };
  const loadLeads = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingLeads(true);
    try {
      const data = await listTeamLeadsPage(team, localStorage.getItem(storageKey), leadQueryRef.current);
      setLeadPage(normalizeLeadPage(data));
    } catch (error) {
      if (!silent) toast.error(error?.response?.data?.message || "Could not load leads");
    } finally {
      if (!silent) setLoadingLeads(false);
    }
  }, [team, storageKey]);
  const patchLeadRows = useCallback((patch) => {
    setLeadPage((old) => {
      const content = patch(old.content);
      return content === old.content ? old : { ...old, content };
    });
  }, []);
  const loadBreak = async () => {
    setCurrentBreak(await getCurrentTeamBreak(team, token()));
  };
  const loadDay = async () => {
    setCurrentDay(await getCurrentTeamDay(team, token()));
  };
  const loadWorkStatus = async () => {
    const data = await getTeamWorkStatus(team, token());
    setWorkStatus(data);
    setCurrentDay(data?.session?.active ? data.session : null);
    setCurrentBreak(data?.currentBreak?.active ? data.currentBreak : null);
    return data;
  };
  const loadLeadWork = async () => {
    setCurrentLeadWork(await getCurrentLeadWork(team, localStorage.getItem(storageKey)));
  };
  // Calls on the member's extension with numbers no lead has. They already count in call time.
  const loadUnlinkedCalls = async () => {
    if (team !== "CALL") return;
    setUnlinkedLoading(true);
    try {
      setUnlinkedCalls(await getMyUnlinkedCalls(token()));
    } catch {
      // The last list stays; the tab still works for whatever it shows.
    } finally {
      setUnlinkedLoading(false);
    }
  };
  const loadAttendanceRequests = async (range = attendanceRange) => {
    setAttendanceLoading(true);
    try {
      setAttendanceRequests(await listTeamAttendanceRequests(team, token(), {
        ...(range.fromDate ? { fromDate: range.fromDate } : {}),
        ...(range.toDate ? { toDate: range.toDate } : {}),
      }));
    } finally {
      setAttendanceLoading(false);
    }
  };
  const loadMyInsights = async ({ silent = false } = {}) => {
    if (!silent) setMyInsightsLoading(true);
    try {
      const [workRows, breakRows] = await Promise.all([
        getTeamLeadWorkSessionsToday(team, token()),
        getTeamBreaksToday(team, token()),
      ]);
      setMyLeadWorkToday(workRows || []);
      setMyBreaksToday(breakRows || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load today's work insights");
    } finally {
      if (!silent) setMyInsightsLoading(false);
    }
  };
  const validate = async () => {
    const savedToken = token();
    if (!savedToken) { setStatus("login"); return; }
    try {
      const data = await validateLeadTeam(team, savedToken);
      setMember(data);
      await Promise.all([loadLookups(), loadLeadWork(), loadWorkStatus()]);
      setStatus("ready");
      // Deliberately outside the block above: the token is already validated by this point, so a
      // failure loading the leave list is not a sign-in failure and must not clear the session.
      try {
        await loadAttendanceRequests();
      } catch {
        toast.error("Could not load your leave requests");
      }
      void loadUnlinkedCalls();
    } catch {
      localStorage.removeItem(storageKey);
      setStatus("login");
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { validate(); }, [team]);
  useEffect(() => {
    if (!currentLeadWork?.id) return undefined;
    const heartbeat = window.setInterval(async () => {
      try {
        setCurrentLeadWork(await getCurrentLeadWork(team, localStorage.getItem(storageKey)));
      } catch {
        // A transient heartbeat failure is handled by the lease timeout.
      }
    }, 60_000);
    return () => window.clearInterval(heartbeat);
  }, [team, storageKey, currentLeadWork?.id]);
  useEffect(() => {
    if (!showMyInsights) return undefined;
    void loadMyInsights();
    const poller = window.setInterval(() => { void loadMyInsights({ silent: true }); }, 45_000);
    return () => window.clearInterval(poller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMyInsights, team, storageKey]);
  // One batched sync per burst of live updates. Ids that are not on the current page mean a lead entered or left the
  // page, which only a refetch can resolve, so we reload the page instead of patching rows.
  const applyLeadUpdates = useCallback(async (leadIds) => {
    const ids = [...new Set((leadIds || []).filter((id) => id != null).map(Number))];
    if (!ids.length) return;
    const known = new Set(leadRowsRef.current.map((row) => String(row.id)));
    if (ids.some((id) => !known.has(String(id)))) {
      // On a busy day most changes are to leads on some other page, so the reload is throttled rather than run on
      // every poll. New leads still surface within a few seconds.
      const now = Date.now();
      if (now - lastPageReloadRef.current < 15_000) return;
      lastPageReloadRef.current = now;
      await loadLeads({ silent: true });
      return;
    }
    try {
      const items = await syncTeamLeads(team, localStorage.getItem(storageKey), ids);
      const allTab = allLeadsTabRef.current;
      const byId = new Map(items.map((item) => [String(item.leadId), item]));
      patchLeadRows((rows) => rows.reduce((next, row) => {
        const item = byId.get(String(row.id));
        if (!item) { next.push(row); return next; }
        const updated = allTab ? item.allLead : item.workableLead;
        if (!updated) return next;
        next.push({ ...row, ...updated });
        return next;
      }, []));
      // A patched row may no longer match the active filter or KPI, which only the server can judge. Same throttle
      // as above, so a busy filtered list refreshes at most every 15 seconds.
      const query = leadQueryRef.current || {};
      const filtered = Boolean(query.q || query.interestStatus || query.categoryId || query.categoryDestination
        || query.destination || query.assignment || query.summaryFilter);
      if (filtered && Date.now() - lastPageReloadRef.current >= 15_000) {
        lastPageReloadRef.current = Date.now();
        await loadLeads({ silent: true });
      }
    } catch {
      // The next event or poll retries synchronization.
    }
  }, [team, storageKey, loadLeads, patchLeadRows]);

  useEffect(() => {
    if (status !== "ready") return undefined;
    void loadLeads();
  }, [status, leadQuery, loadLeads]);

  useEffect(() => {
    if (status !== "ready") return undefined;
    const controller = new AbortController();
    let reconnectTimer;
    const applyUpdate = (leadId) => applyLeadUpdates([leadId]);
    const connect = async () => {
      try {
        await connectLeadEvents(team, localStorage.getItem(storageKey), controller.signal, applyUpdate);
      } catch {
        // Reconnect below unless the component is unmounting.
      }
      if (!controller.signal.aborted) reconnectTimer = window.setTimeout(connect, 3000);
    };
    connect();
    let changeCursor;
    const pollChanges = async () => {
      try {
        const changes = await getTeamLeadChanges(
          team,
          localStorage.getItem(storageKey),
          changeCursor,
        );
        changeCursor = changes.cursor;
        await applyLeadUpdates(changes.leadIds);
      } catch {
        // Keep the previous cursor so the next poll retries the same interval.
      }
    };
    pollChanges();
    const pollingTimer = window.setInterval(pollChanges, 10_000);
    return () => {
      controller.abort();
      window.clearTimeout(reconnectTimer);
      window.clearInterval(pollingTimer);
    };
  }, [status, team, storageKey, applyLeadUpdates]);

  const leadActionsBlocked = workStatus ? !workStatus.canModifyLeads : !currentDay || Boolean(currentBreak);
  const leadActionBlockedReason = workStatus?.blockedReason || (currentBreak ? "End break before creating or editing leads." : "Start day before creating or editing leads.");

  const saveLead = async (form) => {
    if (leadActionsBlocked) {
      toast.error(leadActionBlockedReason);
      await loadWorkStatus();
      return;
    }
    // An answered incoming call makes this a called lead the moment it is saved, so it cannot start in Details Sent.
    if (!editing?.id && form.incomingCallAnswered && String(form.interestStatus || "").trim().toLowerCase() === "details sent") {
      toast.error("This customer is on an answered call. Choose the Interest Status from this call instead of Details Sent.");
      return;
    }
    setSavingLead(true);
    try {
      const payload = normalizeLead(form, team);
      if (!editing?.id && newLeadStartedAt) payload.leadWorkStartedAt = newLeadStartedAt;
      let savedLead;
      if (form.interestStatus === "Confirmed" && !editing?.bookingId) {
        const conversionPayload = bookingConversionSaveRequest(payload, form);
        savedLead = editing?.id
          ? await convertLeadToBooking(team, token(), editing.id, conversionPayload)
          : await createLeadBooking(team, token(), conversionPayload);
      } else {
        savedLead = editing?.id ? await updateTeamLead(team, token(), editing.id, payload) : await createTeamLead(team, token(), payload);
      }
      const savedLeadId = savedLead.id;
      const myFlag = myLeadFlag(editing, member?.memberId);
      const flagChanged = Boolean(form.flagRequested) !== Boolean(myFlag)
        || (Boolean(form.flagRequested) && ((form.flagNote || "") !== (myFlag?.note || "")
          || (form.flagRemindAt || "") !== toReminderInput(myFlag?.remindAt)));
      if (savedLeadId && flagChanged) {
        try {
          const flagResult = form.flagRequested ? await flagLead(team, token(), savedLeadId, form.flagNote, form.flagRemindAt) : await unflagLead(team, token(), savedLeadId);
          savedLead = { ...savedLead, ...flagRowPatch(flagResult) };
          void refreshFlagReminders();
        } catch (error) {
          toast.error(error?.response?.data?.message || "Lead saved, but the flag could not be updated");
        }
      }
      if (savedLeadId && !editing?.id && form.incomingCallAnswered) {
        try {
          const withCall = await markManualCallOutcome(token(), savedLeadId, { outcome: "SUCCESSFUL", note: "Incoming call to company number" });
          savedLead = { ...savedLead, ...withCall };
        } catch (error) {
          toast.error(error?.response?.data?.message || "Lead saved, but the incoming call could not be recorded");
        }
      }
      // A booking conversion responds with ids only, so the submitted values fill in what it lacks.
      const messageLead = { ...payload, ...(savedLead?.customerNumber ? savedLead : {}) };
      const repName = member?.name || member?.username || "";
      // A picked daily update with its own message is offered for every status; otherwise the
      // trip-details message, for the statuses that are in conversation with the customer.
      const template = selectedDailyUpdateTemplate(form, dailyUpdateSuggestions);
      if (template) setCustomerMessage({
        text: fillCustomerMessageTemplate(template.customerMessage, {
          repName,
          // The employee's assigned extension is the one the customer reaches.
          extension: ownExtension,
          callbackTime: customerDateTime(messageLead.nextFollowUpDateTime),
        }),
        customerNumber: messageLead.customerNumber || "",
      });
      else if (leadStatusNeedsCustomerMessage(messageLead.interestStatus)) setCustomerMessage({
        text: buildLeadCustomerMessage(editing?.id ? editing : null, messageLead, categories, repName),
        customerNumber: messageLead.customerNumber || "",
      });
      const replaceSavedLead = (rows) => {
        const index = rows.findIndex((lead) => lead.id === savedLead.id);
        if (index < 0) return rows;
        const next = [...rows];
        next[index] = savedLead;
        return next;
      };
      // Patch first so the row updates instantly, then refetch: the save may have moved the lead out of the active
      // filter or KPI (e.g. out of "Not Yet Called"), and only the server re-applies filters and recounts the KPIs.
      if (leadRowsRef.current.some((row) => row.id === savedLead.id)) {
        patchLeadRows(replaceSavedLead);
      }
      void loadLeads({ silent: true });
      // Saving links any unlinked calls on the lead's numbers, so the list may have shrunk.
      if (team === "CALL") void loadUnlinkedCalls();
      setShowEditor(false); setEditing(null); setNewLeadStartedAt(""); setNewLeadDefaults(null); toast.success("Lead saved");
      void (async () => {
        try {
          if (savedLeadId) await finishLeadWork(savedLeadId);
        } catch {
          toast.error("Lead saved, but the work timer could not be stopped");
        }
      })();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.response?.data?.message || "Could not save lead");
    } finally {
      setSavingLead(false);
    }
  };
  const logout = () => { localStorage.removeItem(storageKey); setStatus("login"); setMember(null); setLeadPage(emptyLeadPage()); setInterestStatusOptions([]); setBookingOptions([]); setBookingOptionsLoaded(false); };
  const beginLeadWork = async (lead) => {
    if (!lead?.id) return false;
    if (leadActionsBlocked) {
      toast.error(leadActionBlockedReason);
      await loadWorkStatus();
      return false;
    }
    try {
      setCurrentLeadWork(await startLeadWork(team, token(), lead.id));
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not open lead");
      return false;
    }
  };
  const finishLeadWork = async (leadId) => {
    if (!leadId) return;
    try {
      const ended = await endLeadWork(team, token(), leadId);
      if (currentLeadWork?.id === ended?.id || currentLeadWork?.leadId === leadId) setCurrentLeadWork(null);
    } catch (error) {
      if (error?.response?.status !== 400) toast.error(error?.response?.data?.message || "Could not stop lead timer");
      await loadLeadWork();
    }
  };
  const flagRowPatch = (result) => ({ flaggedByMemberId: result.flaggedByMemberId, flaggedBy: result.flaggedBy, flaggedAt: result.flaggedAt, flagNote: result.flagNote, flags: result.flags || [] });
  // A flag change moves the My Flagged counts and can move the lead in or out of the open flag view,
  // which only the server works out - so patch the row now and refetch quietly.
  const applyFlagResult = (leadId, result) => {
    patchLeadRows((rows) => rows.map((row) => (row.id === leadId ? { ...row, ...flagRowPatch(result) } : row)));
    void loadLeads({ silent: true });
    void refreshFlagReminders();
  };
  const toggleLeadFlag = (lead) => { if (lead?.id) setFlagModalLead(lead); };
  const saveLeadFlag = async (note, remindAt) => {
    const lead = flagModalLead;
    if (!lead?.id) return;
    setFlagSaving(true);
    try {
      applyFlagResult(lead.id, await flagLead(team, token(), lead.id, note, remindAt));
      setFlagModalLead(null);
      toast.success(remindAt ? "Flag saved with reminder" : "Flag saved");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not update flag");
    } finally {
      setFlagSaving(false);
    }
  };
  const removeLeadFlag = async () => {
    const lead = flagModalLead;
    if (!lead?.id) return;
    setFlagSaving(true);
    try {
      applyFlagResult(lead.id, await unflagLead(team, token(), lead.id));
      setFlagModalLead(null);
      toast.success("Your flag was removed");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not update flag");
    } finally {
      setFlagSaving(false);
    }
  };
  const snoozeFlagReminder = async (reminder, minutes) => {
    try {
      const remindAt = toReminderInput(new Date(Date.now() + minutes * 60_000));
      applyFlagResult(reminder.leadId, await flagLead(team, token(), reminder.leadId, reminder.note, remindAt));
      toast.info(`Reminder moved to ${minutes === 60 ? "1 hour" : `${minutes} minutes`} from now`);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not snooze the reminder");
    }
  };
  const showDueFlags = () => {
    if (leadTab !== "workable") setLeadTab("workable");
    changeSummaryFilter("myFlagged:OVERDUE");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const showCallNeededLeads = () => {
    if (leadTab !== "workable") setLeadTab("workable");
    changeSummaryFilter("callNeeded");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const markManualCall = async (lead, outcome) => {
    if (!lead?.id) return;
    if (leadActionsBlocked) {
      toast.error(leadActionBlockedReason);
      await loadWorkStatus();
      return;
    }
    if (!member?.canManualCallUpdate) {
      toast.error("You do not have permission to manually update call counts");
      return;
    }
    const label = outcome === "SUCCESSFUL" ? "successful call" : "unanswered call";
    const note = window.prompt(`Optional note for this ${label}`, "Customer called company number") || "";
    try {
      const updated = await markManualCallOutcome(token(), lead.id, { outcome, note });
      const patch = (rows) => rows.map((row) => (row.id === updated.id ? updated : row));
      patchLeadRows(patch);
      setEditing((old) => (old?.id === updated.id ? { ...old, ...updated } : old));
      toast.success(`Marked ${label}`);
      return updated;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not update call count");
      return null;
    }
  };
  const openEditLead = async (lead) => {
    if (lead?.bookingId) {
      setNewLeadStartedAt("");
      setEditing(lead);
      setShowEditor(true);
      return;
    }
    if (!await beginLeadWork(lead)) return;
    setNewLeadStartedAt("");
    setEditing(lead);
    setShowEditor(true);
  };
  // From Unlinked Calls, with the call's number filled in; from the header button, empty.
  const openNewLead = (defaults) => {
    if (leadActionsBlocked) {
      toast.error(leadActionBlockedReason);
      void loadWorkStatus();
      return;
    }
    setNewLeadDefaults(defaults?.customerNumber ? defaults : null);
    setEditing(null);
    setNewLeadStartedAt(nowLocalDateTime());
    setShowEditor(true);
  };
  const closeEditor = async () => {
    if (savingLead) return;
    const leadId = editing?.id;
    setShowEditor(false);
    setEditing(null);
    setNewLeadStartedAt("");
    setNewLeadDefaults(null);
    if (leadId) await finishLeadWork(leadId);
  };
  const openViewLead = (lead) => {
    setViewing(lead);
  };
  const openLeadFromInsights = (workRow) => {
    if (!workRow?.leadId) return;
    const lead = leadPage.content.find((row) => String(row.id) === String(workRow.leadId));
    openViewLead(lead || {
      id: workRow.leadId,
      leadId: workRow.leadCode,
      customerNumber: workRow.customerNumber,
      destinationName: workRow.destinationName,
    });
  };
  const closeViewer = () => {
    setViewing(null);
  };
  const toggleBreak = async () => {
    setBreakBusy(true);
    try {
      const startingBreak = !currentBreak;
      currentBreak ? await endTeamBreak(team, token()) : await startTeamBreak(team, token());
      await loadWorkStatus();
      if (startingBreak) setCurrentLeadWork(null);
      toast.success(currentBreak ? "Break ended" : "Break started");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not update break status");
      await loadBreak();
    } finally {
      setBreakBusy(false);
    }
  };

  const callCustomer = async (leadOrPhone) => {
    const lead = typeof leadOrPhone === "object" && leadOrPhone !== null ? leadOrPhone : null;
    const phoneNumber = lead?.customerNumber || leadOrPhone;
    if (!ownExtension) {
      toast.error(NO_EXTENSION_MESSAGE);
      return;
    }
    setCallingNumber(phoneNumber);
    try {
      await clickToCall(token(), ownExtension, phoneNumber, lead?.id || null);
      toast.success(`Call started on extension ${ownExtension}`);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not start call");
    } finally {
      setCallingNumber("");
    }
  };

  const openQuotation = (lead) => {
    const selectedCategory = categories.find(
      (category) => String(category.id) === String(lead.categoryId)
    );
    const destinationName = String(lead.destinationName || "").trim().toLowerCase();
    const candidateTrips = selectedCategory
      ? selectedCategory.trips || []
      : categories.flatMap((category) => category.trips || []);
    const trip = candidateTrips.find(
      (item) => String(item.title || "").trim().toLowerCase() === destinationName
    );
    if (!trip?.path) {
      toast.error("No trip page found for this destination");
      return;
    }
    const prefillKey = `lead-quote-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(prefillKey, JSON.stringify({
      leadId: lead.id,
      // Which portal opened the quote, so the trip page uses this team's login for room photos
      // rather than another portal's older login left in the same browser.
      team,
      destinationName: lead.destinationName,
      adults: Number(lead.numberOfPersons || 0),
      kids: Number(lead.numberOfKids || 0),
      kidAges: parseKidsAgeValue(lead.kidsAge),
      travelDate: lead.plannedDates || "",
      pickupType: lead.pickupType || "",
      addons: String(lead.addons || "").split(",").map((item) => item.trim()).filter(Boolean),
    }));
    window.open(`/trips/${trip.path}?leadQuote=${encodeURIComponent(prefillKey)}#book-now`, "_blank", "noopener,noreferrer");
  };

  const openEditorQuotation = (form) => {
    openQuotation({ ...(editing || {}), ...form });
  };

  const toggleDay = async () => {
    setDayBusy(true);
    try {
      if (currentDay) {
        await endTeamDay(team, token());
        await loadWorkStatus();
        setCurrentLeadWork(null);
        toast.success("Day ended");
      } else {
        await startTeamDay(team, token(), getLeadDeviceToken());
        await loadWorkStatus();
        toast.success("Day started");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not update day status");
      await loadDay();
    } finally {
      setDayBusy(false);
    }
  };

  const assignLead = async (lead, assignedCallMemberId) => {
    if (team !== "CALL" || !lead?.id) return;
    try {
      const saved = await assignCallTeamLead(token(), lead.id, Number(assignedCallMemberId || 0));
      patchLeadRows((rows) => rows.map((row) => row.id === saved.id ? { ...row, ...saved } : row));
      toast.success(saved.assignedCallMemberName ? `Assigned to ${saved.assignedCallMemberName}` : "Lead assignment cleared");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not update lead assignment");
    }
  };
  const updateCallNeededFlag = async (lead, callNeeded, note = "", requestedAt = null) => {
    if (!lead?.id) return null;
    setCallNeededSavingId(lead.id);
    try {
      const saved = await updateLeadCallNeeded(team, token(), lead.id, {
        callNeeded,
        callNeededNote: note,
        callNeededRequestedAt: callNeeded && requestedAt ? requestedAt : null,
      });
      patchLeadRows((rows) => rows.map((row) => row.id === saved.id ? { ...row, ...saved } : row));
      setEditing((old) => old?.id === saved.id ? { ...old, ...saved } : old);
      setViewing((old) => old?.id === saved.id ? { ...old, ...saved } : old);
      toast.success(saved.callNeeded ? "Call Needed marked" : "Call Needed cleared");
      return saved;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not update Call Needed");
      return null;
    } finally {
      setCallNeededSavingId(null);
    }
  };
  // Marking asks for the reason and the customer's time in a dialog; clearing needs nothing more.
  const toggleCallNeededFromRow = async (lead) => {
    if (!lead?.id) return;
    if (lead.callNeeded) await updateCallNeededFlag(lead, false);
    else setCallNeededModalLead(lead);
  };
  const saveCallNeededFromModal = async (note, requestedAt) => {
    const saved = await updateCallNeededFlag(callNeededModalLead, true, note, requestedAt);
    if (saved) setCallNeededModalLead(null);
  };
  const saveAttendanceRequest = async (payload) => {
    try {
      await createTeamAttendanceRequest(team, token(), payload);
      toast.success(payload.requestType === "WEEK_OFF" ? "Week off request sent" : "Leave request sent");
      setShowAttendanceRequest(false);
      await loadAttendanceRequests();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save request");
    }
  };
  const cancelAttendanceRequest = async (row) => {
    try {
      await cancelTeamAttendanceRequest(team, token(), row.id);
      toast.success("Request cancelled");
      await loadAttendanceRequests();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not cancel request");
    }
  };
  const changeAttendanceRange = async (patch) => {
    const next = { ...attendanceRange, ...patch };
    if (next.fromDate && next.toDate && next.toDate < next.fromDate) next.toDate = next.fromDate;
    setAttendanceRange(next);
    try {
      await loadAttendanceRequests(next);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load requests");
    }
  };

  const { refresh: refreshFlagReminders } = useFlagReminderPopups({
    enabled: status === "ready", team, token, memberId: member?.memberId,
    onOpen: showDueFlags, onSnooze: snoozeFlagReminder,
  });
  // Customers who asked for a call at a set time: the whole Call Team is reminded once it passes.
  useCallNeededReminderPopups({
    enabled: status === "ready" && team === "CALL", token, memberId: member?.memberId,
    onOpen: showCallNeededLeads,
  });

  if (status === "checking") return <Container className="py-5 text-center"><Spinner /><div className="mt-2 text-muted">Checking session...</div></Container>;
  if (status === "login") return <LeadTeamLogin team={team} title={title} storageKey={storageKey} onSuccess={validate} />;

  return <Container fluid className="lead-team-modern py-3">
    <Card className="shadow-sm mb-3">
      <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <h4 className="mb-1">{title}</h4>
          <div className="text-muted small">Logged in as {member?.username || member?.name}</div>
          <div className={`small ${currentDay ? "text-success" : "text-muted"}`}>
            {currentDay ? `Day started at ${formatDateTime(currentDay.loggedInAt)}` : "Day not started"}
          </div>
          {workStatus && (
            <div className="small text-muted">
              <span className="work-status-metric" title="Total time from Day Started to now (or Day Ended) — everything, including breaks.">Total: {formatMinutes(workStatus.session?.durationMinutes)}</span> |{" "}
              <span className="work-status-metric" title="Total time minus breaks. This includes both actual lead work and Idle Time — a high number here doesn't mean you were actively working the whole time.">Effective work: {formatMinutes(workStatus.effectiveWorkingMinutes)}</span> |{" "}
              <span className="work-status-metric" title="Time spent on breaks, including lunch.">Breaks: {formatMinutes(workStatus.totalBreakMinutes)}</span> |{" "}
              <span className={`work-status-metric ${Number(workStatus.gapMinutes) > 0 ? "text-danger fw-semibold" : "text-success"}`} title="Logged in, not on a break, but no lead open. This should be 0 — if you're between leads, open one or take a proper break.">Idle Time: {formatMinutes(workStatus.gapMinutes)}</span>
            </div>
          )}
          {currentBreak && <div className="small text-warning">On break since {formatDateTime(currentBreak.startedAt)}</div>}
          {leadActionsBlocked && <div className="small text-danger">{leadActionBlockedReason}</div>}
          {currentLeadWork && <div className="small text-primary">Working on {currentLeadWork.leadCode} since {formatDateTime(currentLeadWork.startedAt)}</div>}
          {newLeadStartedAt && <div className="small text-primary">Creating new lead since {formatDateTime(newLeadStartedAt)}</div>}
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button variant={currentDay ? "outline-danger" : "success"} onClick={toggleDay} disabled={dayBusy || breakBusy || savingLead}>
            {dayBusy ? <><Spinner size="sm" className="me-2" />Updating</> : currentDay ? "End Day" : "Start Day"}
          </Button>
          <Button variant={currentBreak ? "warning" : "outline-warning"} onClick={toggleBreak} disabled={!currentDay || dayBusy || breakBusy || savingLead}>
            {breakBusy ? <><Spinner size="sm" className="me-2" />Updating</> : currentBreak ? "End Break" : "Start Break"}
          </Button>
          <Button variant="primary" className="fw-semibold" onClick={() => setShowAttendanceRequest(true)} disabled={savingLead || breakBusy || dayBusy}>
            Leave / Week Off
          </Button>
          <Button variant="outline-info" onClick={() => window.open(team === "CALL" ? "/call-team/hotel-images" : "/chat-team/hotel-images", "_blank", "noopener,noreferrer")} disabled={savingLead || breakBusy || dayBusy}>Hotel Images</Button>
          <Button variant="outline-info" onClick={() => window.open(team === "CALL" ? "/call-team/hotel-availability" : "/chat-team/hotel-availability", "_blank", "noopener,noreferrer")} disabled={savingLead || breakBusy || dayBusy}>Hotel Availability</Button>
          <Button variant="outline-secondary" onClick={() => setShowMyInsights(true)} disabled={savingLead || breakBusy || dayBusy}>My Insights</Button>
          <Button variant="outline-secondary" onClick={async () => { await Promise.all([loadLeads(), loadLeadWork(), loadWorkStatus(), loadAttendanceRequests(), loadUnlinkedCalls()]); }} disabled={loadingLeads || savingLead || breakBusy || dayBusy}>
            {loadingLeads ? <><Spinner size="sm" className="me-2" />Refreshing</> : "Refresh"}
          </Button>
          <Button onClick={() => openNewLead()} disabled={leadActionsBlocked || loadingLeads || savingLead} title={leadActionsBlocked ? leadActionBlockedReason : ""}>New Lead</Button>
          <Button variant="outline-danger" onClick={logout} disabled={savingLead || breakBusy || dayBusy}>Logout</Button>
          <StaffThemeSwitch theme={theme} onChange={setTheme} />
        </div>
      </Card.Body>
    </Card>
    {team === "CALL" && ownExtension && <div className="d-flex justify-content-end mb-2"><InputGroup size="sm" style={{ width: "auto" }} title="Assigned by an administrator"><InputGroup.Text>Extension</InputGroup.Text><InputGroup.Text className="fw-semibold">{ownExtension}</InputGroup.Text></InputGroup></div>}
    {loadingLeads && <div className="small text-muted mb-2"><Spinner size="sm" className="me-2" />Loading lead data...</div>}
    {team === "CALL" && <MyIncentiveCard team={team} token={token()} />}
    <LeadSummary summary={leadPage.summary} team={team} activeFilter={summaryFilter} onFilter={changeSummaryFilter} isManager={member?.memberRole === "MANAGER"} />
    <FlagViewBar filter={summaryFilter} summary={leadPage.summary} isManager={member?.memberRole === "MANAGER"} onFilter={changeSummaryFilter} />
    {flagModalLead && <LeadFlagModal lead={flagModalLead} memberId={member?.memberId} saving={flagSaving} onSave={saveLeadFlag} onRemove={removeLeadFlag} onHide={() => setFlagModalLead(null)} />}
    <Tabs activeKey={leadTab} onSelect={(key) => { const nextTab = key || "workable"; if (nextTab === leadTab) return; setLeadTab(nextTab); setSummaryFilter(""); setLeadPageIndex(0); if (nextTab === "unlinked") void loadUnlinkedCalls(); }} className="mb-3" mountOnEnter unmountOnExit>
      <Tab eventKey="workable" title={`Workable Leads${leadTab === "workable" && leadPage.summary ? ` (${leadPage.summary.total})` : ""}`}>
        {leadTab === "workable" && <LeadTable rows={leadPage.content} team={team} member={member} callMembers={callMembers} categories={categories} loading={loadingLeads} editable actionsBlocked={leadActionsBlocked} blockedReason={leadActionBlockedReason} interestStatusOptions={interestStatusOptions} summaryFilter={summaryFilter} onClearSummaryFilter={() => changeSummaryFilter("")} searchInput={searchInput} onSearchInput={setSearchInput} statusFilter={statusFilter} onStatusFilter={changeStatusFilter} categoryFilter={categoryFilter} onCategoryFilter={changeCategoryFilter} destinationFilter={destinationFilter} onDestinationFilter={changeDestinationFilter} assignmentFilter={assignmentFilter} onAssignmentFilter={changeAssignmentFilter} onClearFilters={clearLeadFilters} sort={leadSort} onSort={changeSort} page={leadPage.page} size={leadPage.size} totalElements={leadPage.totalElements} totalPages={leadPage.totalPages} onPageChange={setLeadPageIndex} onPageSizeChange={changePageSize} onEdit={openEditLead} onView={openViewLead} onQuotation={openQuotation} onToggleFlag={toggleLeadFlag} onAssignLead={assignLead} onToggleCallNeeded={toggleCallNeededFromRow} callNeededSavingId={callNeededSavingId} />}
      </Tab>
      {team === "CALL" && <Tab eventKey="unlinked" title={<span>Unlinked Calls{unlinkedCalls?.open?.length ? ` (${unlinkedCalls.open.length})` : ""}{unlinkedCalls?.callBackCount ? <Badge bg="danger" className="ms-1">{unlinkedCalls.callBackCount} call back</Badge> : null}</span>}>
        {leadTab === "unlinked" && <Card body><UnlinkedCallsPanel data={unlinkedCalls} loading={unlinkedLoading} token={token()} callingNumber={callingNumber} onCall={callCustomer} onCreateLead={(customerNumber) => openNewLead({ customerNumber })} onChanged={loadUnlinkedCalls} /></Card>}
      </Tab>}
      <Tab eventKey="all" title={`All Leads${leadTab === "all" && leadPage.summary ? ` (${leadPage.summary.total})` : ""}`}>
        {leadTab === "all" && <LeadTable rows={leadPage.content} team={team} member={member} callMembers={callMembers} categories={categories} loading={loadingLeads} interestStatusOptions={interestStatusOptions} summaryFilter={summaryFilter} onClearSummaryFilter={() => changeSummaryFilter("")} searchInput={searchInput} onSearchInput={setSearchInput} statusFilter={statusFilter} onStatusFilter={changeStatusFilter} categoryFilter={categoryFilter} onCategoryFilter={changeCategoryFilter} destinationFilter={destinationFilter} onDestinationFilter={changeDestinationFilter} assignmentFilter={assignmentFilter} onAssignmentFilter={changeAssignmentFilter} onClearFilters={clearLeadFilters} sort={leadSort} onSort={changeSort} page={leadPage.page} size={leadPage.size} totalElements={leadPage.totalElements} totalPages={leadPage.totalPages} onPageChange={setLeadPageIndex} onPageSizeChange={changePageSize} onView={openViewLead} onQuotation={openQuotation} onToggleFlag={toggleLeadFlag} onAssignLead={assignLead} onToggleCallNeeded={toggleCallNeededFromRow} callNeededSavingId={callNeededSavingId} />}
      </Tab>
    </Tabs>
    <LeadEditor show={showEditor} team={team} member={member} callMembers={callMembers} conversionMembers={conversionMembers} initial={editing} newLeadDefaults={newLeadDefaults} leadWork={currentLeadWork} newLeadStartedAt={newLeadStartedAt} categories={categories} interestStatuses={interestStatuses} interestStatusOptions={interestStatusOptions} dailyUpdateSuggestions={dailyUpdateSuggestions} bookingOptions={bookingOptions} bookingOptionsLoading={bookingOptionsLoading} onNeedBookingOptions={ensureBookingOptions} saving={savingLead} callingNumber={callingNumber} onCall={callCustomer} onManualCall={markManualCall} onToggleCallNeeded={updateCallNeededFlag} callNeededSaving={callNeededSavingId === editing?.id} onQuotation={openEditorQuotation} onLoadHistory={(leadId) => getTeamLeadHistory(team, token(), leadId)} onLoadCalls={(leadId) => getTeamLeadCalls(team, token(), leadId)} onHide={closeEditor} onSave={saveLead} /><LeadCustomerMessageModal message={customerMessage?.text} customerNumber={customerMessage?.customerNumber} onHide={() => setCustomerMessage(null)} /><CallNeededModal lead={callNeededModalLead} saving={Boolean(callNeededModalLead) && callNeededSavingId === callNeededModalLead.id} onSave={saveCallNeededFromModal} onHide={() => setCallNeededModalLead(null)} />
    <LeadViewModal show={!!viewing} lead={viewing} team={team} member={member} callingNumber={callingNumber} onCall={callCustomer} onHide={closeViewer} />
    <MyInsightsModal show={showMyInsights} loading={myInsightsLoading} rows={myLeadWorkToday} breaks={myBreaksToday} gaps={workStatus?.session?.gaps} onOpenLead={openLeadFromInsights} onHide={() => setShowMyInsights(false)} />
    <AttendanceRequestModal show={showAttendanceRequest} rows={attendanceRequests} range={attendanceRange} loading={attendanceLoading} onRangeChange={changeAttendanceRange} onCancel={cancelAttendanceRequest} onHide={() => setShowAttendanceRequest(false)} onSave={saveAttendanceRequest} />
  </Container>;
}

function LeadTeamLogin({ team, title, storageKey, onSuccess }) {
  const [username, setUsername] = useState(""); const [password, setPassword] = useState(""); const [saving, setSaving] = useState(false);
  const submit = async (event) => { event.preventDefault(); setSaving(true); try { const data = await loginLeadTeam(team, username, password, leadDevicePayload()); localStorage.setItem(storageKey, data.token); await onSuccess(); } catch (error) { toast.error(error?.response?.data?.message || "Invalid username or password"); } finally { setSaving(false); } };
  return <Container className="py-5" style={{ maxWidth: 460 }}><Card className="shadow-sm"><Card.Body><h3 className="mb-3">{title}</h3><Form onSubmit={submit}><Form.Label>Username</Form.Label><Form.Control value={username} onChange={(e) => setUsername(e.target.value)} required className="mb-2" /><Form.Label>Password</Form.Label><Form.Control type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mb-3" /><Button type="submit" className="w-100" disabled={saving}>{saving ? "Signing in..." : "Login"}</Button></Form></Card.Body></Card></Container>;
}

function AttendanceRequestModal({ show, rows = [], range, loading = false, onRangeChange, onCancel, onHide, onSave }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ requestType: "LEAVE", dayType: "FULL_DAY", startDate: today, endDate: today, reason: "" });
  useEffect(() => {
    if (show) setForm({ requestType: "LEAVE", dayType: "FULL_DAY", startDate: today, endDate: today, reason: "" });
  }, [show, today]);
  const update = (field, value) => setForm((old) => ({ ...old, [field]: value, ...(field === "startDate" && (!old.endDate || old.endDate < value) ? { endDate: value } : {}) }));
  const submit = (event) => {
    event.preventDefault();
    onSave({ ...form, endDate: form.endDate || form.startDate });
  };
  return <Modal show={show} onHide={onHide} centered size="lg"><Form onSubmit={submit}><Modal.Header closeButton><Modal.Title className="text-dark">Request Leave / Week Off</Modal.Title></Modal.Header><Modal.Body><Row className="g-3"><Col md={6}><Form.Label>Request Type</Form.Label><Form.Select value={form.requestType} onChange={(e) => update("requestType", e.target.value)}><option value="LEAVE">Leave</option><option value="WEEK_OFF">Week Off</option></Form.Select></Col><Col md={6}><Form.Label>Day Type</Form.Label><Form.Select value={form.dayType} onChange={(e) => update("dayType", e.target.value)}><option value="FULL_DAY">Full Day</option><option value="HALF_DAY">Half Day</option></Form.Select></Col><Col md={6}><Form.Label>From Date</Form.Label><Form.Control type="date" value={form.startDate} onChange={(e) => update("startDate", e.target.value)} required /></Col><Col md={6}><Form.Label>To Date</Form.Label><Form.Control type="date" min={form.startDate || ""} value={form.endDate} onChange={(e) => update("endDate", e.target.value)} required /></Col><Col xs={12}><Form.Label>Reason</Form.Label><Form.Control as="textarea" rows={3} value={form.reason} onChange={(e) => update("reason", e.target.value)} placeholder="Reason for leave or week off" /></Col></Row><div className="border-top mt-3 pt-3"><div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-2"><div className="fw-semibold text-dark">Recent Requests</div><div className="d-flex flex-wrap gap-2"><Form.Group><Form.Label className="small text-dark mb-1">From</Form.Label><Form.Control size="sm" type="date" value={range?.fromDate || ""} onChange={(e) => onRangeChange?.({ fromDate: e.target.value })} disabled={loading} /></Form.Group><Form.Group><Form.Label className="small text-dark mb-1">To</Form.Label><Form.Control size="sm" type="date" min={range?.fromDate || ""} value={range?.toDate || ""} onChange={(e) => onRangeChange?.({ toDate: e.target.value })} disabled={loading} /></Form.Group></div></div><EmployeeAttendanceRequests rows={rows} onCancel={onCancel} loading={loading} compact /></div></Modal.Body><Modal.Footer><Button type="button" variant="secondary" onClick={onHide}>Close</Button><Button type="submit" variant="primary" className="fw-semibold">Submit Request</Button></Modal.Footer></Form></Modal>;
}

function EmployeeAttendanceRequests({ rows = [], onCancel, loading = false, compact = false }) {
  const visible = [...(rows || [])].slice(0, 5);
  if (loading) return <div className="small text-dark"><Spinner size="sm" className="me-2" />Loading requests...</div>;
  if (!visible.length) return <div className="small text-dark">No requests found.</div>;
  const table = <div className="table-responsive"><Table size="sm" className="mb-0 align-middle"><thead><tr><th>Type</th><th>Dates</th><th>Day</th><th>Status</th><th>Reason</th><th className="text-end">Action</th></tr></thead><tbody>{visible.map((row) => <tr key={row.id}><td>{attendanceTypeLabel(row.requestType)}</td><td>{formatDateOnly(row.startDate)}{row.endDate && row.endDate !== row.startDate ? ` to ${formatDateOnly(row.endDate)}` : ""}</td><td>{attendanceDayLabel(row.dayType)}</td><td><Badge bg={attendanceStatusVariant(row.status)}>{attendanceStatusLabel(row.status)}</Badge></td><td className="small">{row.reason || "-"}</td><td className="text-end">{row.status === "PENDING" ? <Button type="button" size="sm" variant="outline-danger" onClick={() => onCancel?.(row)}>Cancel</Button> : "-"}</td></tr>)}</tbody></Table></div>;
  if (compact) return table;
  return <Card className="mb-3"><Card.Header className="fw-semibold text-dark bg-white">My Leave / Week Off Requests</Card.Header>{table}</Card>;
}

function getLeadDeviceToken() {
  let token = localStorage.getItem(leadDeviceStorageKey);
  if (!token) {
    token = window.crypto?.randomUUID?.() || `lead-device-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(leadDeviceStorageKey, token);
  }
  return token;
}

function leadDevicePayload() {
  return {
    deviceToken: getLeadDeviceToken(),
    deviceLabel: `${navigator.platform || "Desktop"} - ${navigator.userAgent?.includes("Chrome") ? "Chrome" : "Browser"}`,
    userAgent: navigator.userAgent || "",
    mobileBrowser: /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || ""),
  };
}

export function HotelImagesBrowser({ team, token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copyingImageId, setCopyingImageId] = useState(null);
  const [copyingAllCategoryId, setCopyingAllCategoryId] = useState(null);
  const [downloadingVideoId, setDownloadingVideoId] = useState(null);
  const [city, setCity] = useState("");
  const [hotelCode, setHotelCode] = useState("");
  const imageBlobCacheRef = useRef(new Map());
  const copySourceBlobCacheRef = useRef(new Map());
  const copyImageBlobCacheRef = useRef(new Map());
  const copySheetBlobCacheRef = useRef(new Map());

  useEffect(() => {
    if (!team || !token) return;
    setLoading(true);
    listTeamHotelImages(team, token)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((error) => toast.error(error?.response?.data?.message || "Could not load hotel images"))
      .finally(() => setLoading(false));
  }, [team, token]);

  useEffect(() => {
    imageBlobCacheRef.current.clear();
    copySourceBlobCacheRef.current.clear();
    copyImageBlobCacheRef.current.clear();
    copySheetBlobCacheRef.current.clear();
  }, [team, token]);

  const cities = useMemo(() => [...new Set(rows.map((row) => row.city).filter(Boolean))].sort(), [rows]);
  const hotels = useMemo(() => rows.filter((row) => !city || row.city === city), [rows, city]);
  const selectedHotel = hotels.find((row) => row.customerCode === hotelCode) || hotels[0] || null;

  useEffect(() => {
    if (!hotels.length) {
      setHotelCode("");
      return;
    }
    if (!hotels.some((hotel) => hotel.customerCode === hotelCode)) {
      setHotelCode(hotels[0].customerCode);
    }
  }, [hotels, hotelCode]);

  const hotelImageBlob = useCallback(async (imageId) => {
    if (!imageId) throw new Error("Image id is missing");
    const cacheKey = String(imageId);
    if (imageBlobCacheRef.current.has(cacheKey)) {
      return imageBlobCacheRef.current.get(cacheKey);
    }
    const blob = await getTeamHotelImageFile(team, token, imageId);
    imageBlobCacheRef.current.set(cacheKey, blob);
    return blob;
  }, [team, token]);

  const hotelImageCopySourceBlob = useCallback(async (imageId) => {
    if (!imageId) throw new Error("Image id is missing");
    const cacheKey = String(imageId);
    if (copySourceBlobCacheRef.current.has(cacheKey)) {
      return copySourceBlobCacheRef.current.get(cacheKey);
    }
    const blob = await getTeamHotelImageCopySourceFile(team, token, imageId);
    copySourceBlobCacheRef.current.set(cacheKey, blob);
    return blob;
  }, [team, token]);

  const hotelCopyImageBlob = useCallback(async (image, category) => {
    if (!image?.id) throw new Error("Image id is missing");
    const cacheKey = `${selectedHotel?.customerCode || ""}:${category?.categoryId || ""}:${image.id}`;
    if (copyImageBlobCacheRef.current.has(cacheKey)) {
      return copyImageBlobCacheRef.current.get(cacheKey);
    }
    try {
      const blob = await getTeamHotelImageCopyFile(team, token, image.id);
      copyImageBlobCacheRef.current.set(cacheKey, blob);
      return blob;
    } catch (error) {
      console.warn("Backend copy image failed, using browser fallback", error);
      const sourceBlob = await hotelImageBlob(image.id);
      const blob = await roomImageWithCompactHeader(sourceBlob, selectedHotel, category);
      copyImageBlobCacheRef.current.set(cacheKey, blob);
      return blob;
    }
  }, [team, token, hotelImageBlob, selectedHotel]);

  const copySheetBlob = useCallback(async (category, images) => {
    const cacheKey = `${selectedHotel?.customerCode || ""}:${category?.categoryId || ""}:${images.map((image) => image.id).join(",")}`;
    if (copySheetBlobCacheRef.current.has(cacheKey)) {
      return copySheetBlobCacheRef.current.get(cacheKey);
    }
    const sourceBlobs = await Promise.all(images.map(async (image) => {
      try {
        return await hotelImageCopySourceBlob(image.id);
      } catch (error) {
        console.warn("Backend resized copy source failed, using original image", error);
        return hotelImageBlob(image.id);
      }
    }));
    const blob = await roomImagesToSheet(sourceBlobs, selectedHotel, category);
    copySheetBlobCacheRef.current.set(cacheKey, blob);
    return blob;
  }, [hotelImageBlob, hotelImageCopySourceBlob, selectedHotel]);

  useEffect(() => {
    if (!selectedHotel) return undefined;
    let cancelled = false;
    const imageIds = (selectedHotel.roomCategories || [])
      .flatMap((category) => category.images || [])
      .filter((media) => media.mediaType !== "VIDEO" && media.id)
      .slice(0, 24)
      .map((media) => media.id);
    const prefetch = async () => {
      for (const imageId of imageIds) {
        if (cancelled) break;
        try {
          await hotelImageCopySourceBlob(imageId);
        } catch {
          // Copy actions fall back to the original image if the optimized source is unavailable.
        }
      }
    };
    const idleId = window.requestIdleCallback
      ? window.requestIdleCallback(() => { void prefetch(); }, { timeout: 1500 })
      : window.setTimeout(() => { void prefetch(); }, 400);
    return () => {
      cancelled = true;
      if (window.cancelIdleCallback && window.requestIdleCallback) {
        window.cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId);
      }
    };
  }, [selectedHotel, hotelImageCopySourceBlob]);

  const copyAllImages = async (category) => {
    const images = (category?.images || []).filter((media) => media.mediaType !== "VIDEO");
    if (!images.length) return;
    setCopyingAllCategoryId(category.categoryId);
    try {
      if (!window.ClipboardItem || !navigator.clipboard?.write) throw new Error("Image clipboard unsupported");
      const sheetBlob = await copySheetBlob(category, images);
      await navigator.clipboard.write([new ClipboardItem({ "image/png": sheetBlob })]);
      toast.success(images.length === 1 ? "Image copied. Paste it in WhatsApp." : `${images.length} images copied as one image sheet. Paste it in WhatsApp.`);
    } catch (error) {
      console.error("Could not copy hotel image sheet", error);
      toast.error("Could not copy all images. Try copying images one by one or use Open / Download.");
    } finally {
      setCopyingAllCategoryId(null);
    }
  };

  const copyImage = async (image, category) => {
    if (!image?.id) return;
    setCopyingImageId(image.id);
    try {
      if (!window.ClipboardItem || !navigator.clipboard?.write) throw new Error("Image clipboard unsupported");
      const imageBlob = await hotelCopyImageBlob(image, category);
      await navigator.clipboard.write([new ClipboardItem({ "image/png": imageBlob })]);
      toast.success("Image copied. Paste it in WhatsApp.");
    } catch (error) {
      console.error("Could not copy hotel image", error);
      toast.error("Could not copy this image. Try Open / Download.");
    } finally {
      setCopyingImageId(null);
    }
  };

  const downloadVideo = async (video) => {
    if (!video?.id) return;
    setDownloadingVideoId(video.id);
    try {
      const videoBlob = await getTeamHotelImageFile(team, token, video.id);
      const extension = videoFileExtension(videoBlob.type, video.imageUrl);
      const objectUrl = URL.createObjectURL(videoBlob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `hotel-video-${video.id}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      toast.error("Could not download this video.");
    } finally {
      setDownloadingVideoId(null);
    }
  };

  return loading ? <div className="text-center py-4"><Spinner /><div className="small text-muted mt-2">Loading hotel media...</div></div> : <><Row className="g-3 mb-3"><Col md={4}><Form.Label>City</Form.Label><Form.Select value={city} onChange={(e) => { setCity(e.target.value); setHotelCode(""); }}><option value="">All Cities</option>{cities.map((item) => <option key={item} value={item}>{item}</option>)}</Form.Select></Col><Col md={5}><Form.Label>Hotel</Form.Label><Form.Select value={hotelCode} onChange={(e) => setHotelCode(e.target.value)} disabled={!hotels.length}>{hotels.map((hotel) => <option key={hotel.customerCode} value={hotel.customerCode}>{hotel.customerCode}</option>)}</Form.Select></Col></Row>{!selectedHotel ? <div className="text-center text-muted py-4">No hotel room images or videos found.</div> : <div className="d-flex flex-column gap-3">{(selectedHotel.roomCategories || []).map((category) => <Card key={category.categoryId}><Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2"><div><div className="fw-semibold">{category.categoryName}</div><div className="small text-muted">Hotel: {selectedHotel.customerCode}</div></div>{(category.images || []).some((media) => media.mediaType !== "VIDEO") && <Button size="sm" variant="outline-primary" onClick={() => copyAllImages(category)} disabled={copyingAllCategoryId !== null || copyingImageId !== null}>{copyingAllCategoryId === category.categoryId ? <><Spinner size="sm" className="me-2" />Copying</> : "Copy All Images"}</Button>}</Card.Header><Card.Body><Row className="g-3">{(category.images || []).map((media, index) => <Col sm={6} lg={3} xl={2} key={media.id || media.imageUrl}>{media.mediaType === "VIDEO" ? <><video src={media.imageUrl} controls preload="metadata" className="w-100 rounded border" style={{ height: 130, objectFit: "cover" }} /><div className="d-flex gap-2 mt-2"><Button as="a" href={media.imageUrl} target="_blank" rel="noreferrer" size="sm" variant="outline-primary" className="flex-fill">Open</Button><Button size="sm" variant="outline-secondary" className="flex-fill" onClick={() => downloadVideo(media)} disabled={downloadingVideoId !== null}>{downloadingVideoId === media.id ? <><Spinner size="sm" className="me-2" />Downloading</> : "Download"}</Button></div></> : <><img src={media.imageUrl} alt={`${category.categoryName} ${index + 1}`} className="w-100 rounded border" style={{ height: 130, objectFit: "cover" }} /><Button size="sm" variant="outline-primary" className="w-100 mt-2" onClick={() => copyImage(media, category)} disabled={copyingImageId !== null || copyingAllCategoryId !== null}>{copyingImageId === media.id ? <><Spinner size="sm" className="me-2" />Copying</> : "Copy Image"}</Button></>}</Col>)}</Row></Card.Body></Card>)}</div>}</>;
}

function MyInsightsModal({ show, loading, rows, breaks, gaps, onOpenLead, onHide }) {
  return <Modal show={show} onHide={onHide} size="xl" centered scrollable><Modal.Header closeButton><Modal.Title>My Lead Work Today</Modal.Title></Modal.Header><Modal.Body>{loading ? <div className="text-center py-4"><Spinner /><div className="small text-muted mt-2">Loading today's work...</div></div> : <LeadWorkTimelineCard rows={rows} breaks={breaks} gaps={gaps} onOpenLead={onOpenLead} title="My Lead Work Today" emptyText="You are not working on any lead right now." />}</Modal.Body><Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button></Modal.Footer></Modal>;
}

function LeadSummary({ summary, team, activeFilter, onFilter, isManager = false }) {
  const stats = summary || { total: 0, callNeeded: 0, notYetCalled: 0, dueToday: 0, overdue: 0, closed: 0, myFlagged: 0 };
  const callTeam = team === "CALL";
  const dueToday = <Metric label="Due Today" number={callTeam ? 4 : null} value={stats.dueToday} active={activeFilter === "Due Today"} onClick={() => onFilter("Due Today")} />;
  const overdue = <Metric label="Overdue" number={callTeam ? 3 : null} value={stats.overdue} variant="danger" active={activeFilter === "Overdue"} onClick={() => onFilter("Overdue")} />;
  return <>{callTeam && summary && <CallPriorityBar stats={stats} activeFilter={activeFilter} onFilter={onFilter} />}<Row className="g-3 mb-3">
    <Metric label="Total Leads" value={stats.total} active={activeFilter === ""} onClick={() => onFilter("")} />
    {callTeam && <Metric label="Call Needed" number={1} value={stats.callNeeded} variant="danger" active={activeFilter === "callNeeded"} onClick={() => onFilter("callNeeded")} />}
    {callTeam && <Metric label="Not Yet Called" number={2} value={stats.notYetCalled} variant="warning" active={activeFilter === "notYetCalled"} onClick={() => onFilter("notYetCalled")} />}
    {callTeam ? <>{overdue}{dueToday}</> : <>{dueToday}{overdue}</>}
    {team === "CALL" && <Metric label="Closed" value={stats.closed} active={activeFilter === "Closed"} onClick={() => onFilter("Closed")} />}
    <NoDataReceivedMetric stats={stats} activeFilter={activeFilter} onFilter={onFilter} />
    <Metric label="My Flagged" value={stats.myFlagged} variant="info" active={isFlagFilter(activeFilter)}
      onClick={() => onFilter("myFlagged")}
      detail={<>
        <FlagCountsLine counts={stats.myFlags} />
        {isManager && stats.teamFlags?.total > 0 && <div className="small text-muted">Team: {stats.teamFlags.total}{stats.teamFlags.overdue ? <> · <span className="text-danger">{stats.teamFlags.overdue} overdue</span></> : ""}</div>}
      </>} />
  </Row></>;
}
// The call team works its queues in this order: the first with any leads is the one to work now,
// and with all four empty there is nothing left to call, which the manager needs to hear about.
const CALL_PRIORITY = [
  { filter: "callNeeded", label: "Call Needed", count: "callNeeded" },
  { filter: "notYetCalled", label: "Not Yet Called", count: "notYetCalled" },
  { filter: "Overdue", label: "Overdue", count: "overdue" },
  { filter: "Due Today", label: "Due Today", count: "dueToday" },
];
function CallPriorityBar({ stats, activeFilter, onFilter }) {
  const steps = CALL_PRIORITY.map((step, index) => ({ ...step, number: index + 1, value: Number(stats[step.count] || 0) }));
  const next = steps.find((step) => step.value > 0);
  if (!next) return <div className="call-priority call-priority-clear mb-3" role="status">
    <i className="bi bi-exclamation-circle-fill" aria-hidden="true" />
    <span>Call Needed, Not Yet Called, Overdue and Due Today are all 0. <strong>Please inform your manager.</strong></span>
  </div>;
  return <div className="call-priority mb-3">
    <span className="call-priority-title">Work in this order</span>
    {steps.map((step) => <button key={step.filter} type="button" onClick={() => onFilter(step.filter)}
      className={`call-priority-step ${step === next ? "is-next" : ""} ${step.value ? "" : "is-empty"} ${activeFilter === step.filter ? "active" : ""}`}>
      <span className="call-priority-number">{step.number}</span>{step.label}<span className="call-priority-count">{step.value}</span>
      {step === next && <span className="call-priority-now">Now</span>}
    </button>)}
  </div>;
}
// No Data Received leads wait on the customer, so they have this card to themselves and stay out
// of every other card but Total and Closed. Three ways in: all of them, the overdue ones, or today's.
function NoDataReceivedMetric({ stats, activeFilter, onFilter }) {
  const division = (filter, label, value, extraClass = "") => <button type="button" className={`no-data-metric-part ${extraClass} ${activeFilter === filter ? "active" : ""}`} onClick={() => onFilter(filter)}>
    <span className="no-data-metric-part-label">{label}</span><span className="no-data-metric-part-value">{value || 0}</span>
  </button>;
  const anyActive = String(activeFilter || "").startsWith("noDataReceived");
  return <Col sm={6} lg={3}><Card body className={`lead-metric lead-metric-secondary no-data-metric shadow-sm border-0 w-100 ${anyActive ? "active" : ""}`}>
    <button type="button" className={`no-data-metric-main ${activeFilter === "noDataReceived" ? "active" : ""}`} onClick={() => onFilter("noDataReceived")}>
      <div className="lead-metric-label small">No Data Received</div><div className="h3 mb-0">{stats.noDataReceived || 0}</div>
    </button>
    <div className="no-data-metric-parts">
      {division("noDataReceived:Overdue", "Overdue", stats.noDataReceivedOverdue, "is-overdue")}
      {division("noDataReceived:Due Today", "Due Today", stats.noDataReceivedDueToday)}
    </div>
  </Card></Col>;
}
function Metric({ label, value, variant = "primary", active = false, onClick, detail = null, number = null }) {
  return <Col sm={6} lg={3}><Card as="button" type="button" body onClick={onClick} className={`lead-metric lead-metric-${variant} shadow-sm text-start border-0 w-100 ${active ? "active" : ""}`}><div className="lead-metric-label small">{number && <span className="lead-metric-number">{number}</span>}{label}</div><div className="h3 mb-0">{value}</div>{detail}</Card></Col>;
}
function MultiSelectFilterDropdown({ label, title, options = [], groups = null, selected = [], disabled = false, onChange, emptyText = "No options available" }) {
  const values = options
    .map((option) => ({
      value: String(option?.value ?? option?.name ?? option ?? "").trim(),
      label: String(option?.label ?? option?.name ?? option?.title ?? option ?? "").trim(),
      // Optional second line, e.g. which category a destination belongs to.
      hint: String(option?.hint ?? "").trim(),
    }))
    .filter((option) => option.value && option.label);
  const toggle = (value) => {
    onChange?.(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
  };
  const idFor = (value, suffix) =>
    `${label.toLowerCase().replace(/[^a-z0-9_-]/g, "-")}-filter-${value.replace(/[^A-Za-z0-9_-]/g, "-")}${suffix}`;
  const renderOption = (option, keySuffix = "") => (
    <Form.Check
      key={`${option.value}${keySuffix}`}
      type="checkbox"
      id={idFor(option.value, keySuffix)}
      className="lead-filter-check"
      label={(
        <span className="lead-filter-option">
          <span className="lead-filter-option-name">{option.label}</span>
          {option.hint && <span className="lead-filter-option-hint">{option.hint}</span>}
        </span>
      )}
      checked={selected.includes(option.value)}
      onChange={() => toggle(option.value)}
    />
  );
  // Groups carry their own heading, e.g. one block per selected category. The same destination can
  // appear under two headings; it is one filter value, so ticking it in either block ticks both.
  const normalisedGroups = (groups || [])
    .map((group) => ({
      label: String(group?.label ?? "").trim(),
      options: (group?.options || [])
        .map((option) => ({
          value: String(option?.value ?? "").trim(),
          label: String(option?.label ?? "").trim(),
        }))
        .filter((option) => option.value && option.label),
    }))
    .filter((group) => group.options.length);
  return (
    <Dropdown autoClose="outside">
      <Dropdown.Toggle size="sm" variant={selected.length ? "primary" : "outline-secondary"} disabled={disabled} className="lead-filter-toggle">
        <span>{label}</span>
        {selected.length > 0 && <Badge bg="light" text="dark" className="ms-2">{selected.length}</Badge>}
      </Dropdown.Toggle>
      <Dropdown.Menu className="lead-filter-menu">
        <div className="lead-filter-menu-header">
          <span>{title || label}</span>
          {selected.length > 0 && <Button type="button" size="sm" variant="link" className="p-0" onClick={() => onChange?.([])}>Clear</Button>}
        </div>
        {normalisedGroups.length ? (
          <div className="lead-filter-groups">
            {normalisedGroups.map((group, groupIndex) => (
              <div className="lead-filter-group" key={group.label || groupIndex}>
                <div className="lead-filter-group-title">{group.label}</div>
                <div className="lead-filter-options">
                  {group.options.map((option) => renderOption(option, `-g${groupIndex}`))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`lead-filter-options${values.some((option) => option.hint) ? " lead-filter-options--stacked" : ""}`}>
            {values.map((option) => renderOption(option))}
            {!values.length && <div className="small text-muted">{emptyText}</div>}
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function InterestStatusFilterDropdown({ options = [], selected = [], disabled = false, onChange }) {
  return <MultiSelectFilterDropdown label="Interest" title="Interest status" options={options} selected={selected} disabled={disabled} onChange={onChange} emptyText="No statuses available" />;
}
function LeadTable({
  rows = [], team, member, callMembers = [], categories = [], loading = false, editable = false,
  actionsBlocked = false, blockedReason = "", interestStatusOptions = [],
  summaryFilter, onClearSummaryFilter, searchInput, onSearchInput,
  statusFilter = [], onStatusFilter, categoryFilter = [], onCategoryFilter,
  destinationFilter = [], onDestinationFilter, assignmentFilter = "ALL", onAssignmentFilter, onClearFilters,
  sort, onSort, page = 0, size = DEFAULT_LEAD_PAGE_SIZE, totalElements = 0, totalPages = 0,
  onPageChange, onPageSizeChange,
  onEdit, onView, onQuotation, onToggleFlag, onAssignLead,
  onToggleCallNeeded, callNeededSavingId = null,
}) {
  // Red after the Details Sent follow-up time: Not Yet Called leads stay off the Overdue card.
  const notYetCalledTargetMinutes = statusFollowUpMinutes(interestStatusOptions, "Details Sent");
  const selectedFilterCategories = useMemo(
    () => categories.filter((category) => categoryFilter.includes(String(category.id))),
    [categories, categoryFilter],
  );
  const destinationOptions = useMemo(() => {
    const source = categoryFilter.length ? selectedFilterCategories : categories;
    // Keyed on the trimmed title, which is also the option's value. Several trip titles are stored
    // with a stray leading or trailing space ("Wayanad " and "Wayanad"), so keying on the raw string
    // let them through as separate options that then rendered identically.
    const byTitle = new Map();
    source.forEach((category) => {
      const categoryName = category.title || category.name || `Category ${category.id}`;
      (category.trips || []).forEach((trip) => {
        const title = String(trip.title || "").trim();
        if (!title) return;
        if (!byTitle.has(title)) byTitle.set(title, { title, categories: [] });
        const entry = byTitle.get(title);
        // A destination can sit under more than one category, so collect them all rather than
        // letting the first one win - otherwise the hint would be wrong half the time.
        if (!entry.categories.includes(categoryName)) entry.categories.push(categoryName);
      });
    });
    return Array.from(byTitle.values());
  }, [categories, categoryFilter, selectedFilterCategories]);
  // With more than one category selected, the destinations are split under a heading per category -
  // otherwise one flat list gives no clue which package a name belongs to.
  const destinationGroups = useMemo(() => {
    if (categoryFilter.length < 2) return null;
    return selectedFilterCategories.map((category) => ({
      label: category.title || category.name || `Category ${category.id}`,
      options: (category.trips || [])
        .map((trip) => String(trip.title || "").trim())
        .filter(Boolean)
        .filter((title, index, all) => all.indexOf(title) === index)
        .sort((a, b) => a.localeCompare(b))
        .map((title) => ({ value: title, label: title })),
    }));
  }, [categoryFilter, selectedFilterCategories]);
  const requestSort = (key) => onSort(sort.key === key && sort.dir === "asc" ? { key, dir: "desc" } : { key, dir: "asc" });
  const hasActiveFilters = Boolean(String(searchInput || "").trim() || statusFilter.length || categoryFilter.length
    || destinationFilter.length || summaryFilter || (team === "CALL" && assignmentFilter !== "ALL"));
  const columnCount = team === "CALL" ? 16 : 13;
  const firstRow = totalElements === 0 ? 0 : page * size + 1;
  const lastRow = Math.min(totalElements, page * size + rows.length);
  return <Card className="shadow-sm">
    <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
      <div className="d-flex flex-wrap align-items-center gap-2">
        <span className="fw-semibold">Lead Tracker</span>
        {summaryFilter && <Badge bg="primary" role="button" onClick={onClearSummaryFilter}>{filterLabel(summaryFilter)} x</Badge>}
        {statusFilter.map((status) => <Badge bg="info" text="dark" key={status}>{status}</Badge>)}
        {selectedFilterCategories.map((category) => <Badge bg="info" text="dark" key={category.id}>{category.title || category.name || "Category"}</Badge>)}
        {destinationFilter.map((destination) => <Badge bg="info" text="dark" key={destination}>{destination}</Badge>)}
        {team === "CALL" && assignmentFilter !== "ALL" && <Badge bg="info" text="dark">{assignmentFilter === "ME" ? "Assigned to me" : assignmentFilter === "UNASSIGNED" ? "Unassigned" : callMembers.find((item) => String(item.id) === assignmentFilter)?.name || "Assigned"}</Badge>}
        {hasActiveFilters && <Badge bg="secondary">{totalElements} {totalElements === 1 ? "lead" : "leads"}</Badge>}
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2">
        <InterestStatusFilterDropdown options={interestStatusOptions} selected={statusFilter} disabled={loading} onChange={onStatusFilter} />
        <MultiSelectFilterDropdown label="Category" title="Categories" options={categories.map((category) => ({ value: String(category.id), label: category.title || category.name || `Category ${category.id}` }))} selected={categoryFilter} disabled={loading} onChange={onCategoryFilter} emptyText="No categories available" />
        <MultiSelectFilterDropdown
          label="Destination"
          title="Destinations"
          options={destinationOptions.map((trip) => ({ value: trip.title, label: trip.title || "Untitled trip" })).filter((trip) => trip.value)}
          groups={destinationGroups}
          selected={destinationFilter}
          disabled={loading || !destinationOptions.length}
          onChange={onDestinationFilter}
          emptyText="No destinations available"
        />
        {team === "CALL" && <Form.Select size="sm" className="lead-filter-inline-select" value={assignmentFilter} onChange={(e) => onAssignmentFilter(e.target.value)} disabled={loading} style={{ width: 190 }}>
          <option value="ALL">Assigned: All</option>
          <option value="ME">Assigned to me</option>
          <option value="UNASSIGNED">Unassigned</option>
          {callMembers.map((item) => <option key={item.id} value={String(item.id)}>{item.name || item.username}</option>)}
        </Form.Select>}
        <InputGroup size="sm" className="lead-filter-search" style={{ maxWidth: 360 }}>
          <Form.Control placeholder="Search lead ID, phone, destination, notes" value={searchInput} onChange={(e) => onSearchInput(e.target.value)} />
          {hasActiveFilters && <Button variant="outline-secondary" onClick={onClearFilters}>Clear all</Button>}
        </InputGroup>
      </div>
    </Card.Header>
    <LeadPagination loading={loading} page={page} size={size} totalElements={totalElements} totalPages={totalPages} firstRow={firstRow} lastRow={lastRow} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} className="border-bottom" />
    <div className="table-responsive sticky-table-scroll">
      <Table hover className="mb-0 align-middle">
        <thead className="table-light">
          <tr>
            <th style={{ width: 44 }}>#</th>
            <SortableTh label="Lead ID" sortKey="leadId" sort={sort} onSort={requestSort} />
            <SortableTh label="Customer" sortKey="customerNumber" sort={sort} onSort={requestSort} />
            <SortableTh label="Language" sortKey="customerLanguage" sort={sort} onSort={requestSort} />
            <SortableTh label="Destination" sortKey="destinationName" sort={sort} onSort={requestSort} />
            <SortableTh label="Planned Date" sortKey="plannedDates" sort={sort} onSort={requestSort} />
            <SortableTh label="Travellers" sortKey="travellers" sort={sort} onSort={requestSort} />
            {team === "CALL" && <SortableTh label="Call" sortKey="isCallDone" sort={sort} onSort={requestSort} />}
            {team === "CALL" && <SortableTh label="Assigned To" sortKey="assignedCallMemberName" sort={sort} onSort={requestSort} />}
            <SortableTh label="Interest" sortKey="interestStatus" sort={sort} onSort={requestSort} />
            <SortableTh label="Follow-up" sortKey="nextFollowUpDateTime" sort={sort} onSort={requestSort} />
            <SortableTh label="Created Time" sortKey="createdTimeStamp" sort={sort} onSort={requestSort} />
            <SortableTh label="Last Updated" sortKey="lastUpdated" sort={sort} onSort={requestSort} />
            <SortableTh label="Time Spent" sortKey="leadWorkTotalSeconds" sort={sort} onSort={requestSort} />
            {team === "CALL" && <SortableTh label="Actual Call Time" sortKey="actualCallSeconds" sort={sort} onSort={requestSort} />}
            <th className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((lead, index) => <LeadRow key={lead.id} rowNumber={page * size + index + 1} lead={lead} notYetCalledTargetMinutes={notYetCalledTargetMinutes} team={team} member={member} callMembers={callMembers} loading={loading} editable={editable} actionsBlocked={actionsBlocked} blockedReason={blockedReason} callNeededSavingId={callNeededSavingId} onEdit={onEdit} onView={onView} onQuotation={onQuotation} onToggleFlag={onToggleFlag} onAssignLead={onAssignLead} onToggleCallNeeded={onToggleCallNeeded} />)}
          {!rows.length && <tr><td colSpan={columnCount} className="text-center text-muted py-4">{loading ? <><Spinner size="sm" className="me-2" />Loading leads...</> : "No leads found."}</td></tr>}
        </tbody>
      </Table>
    </div>
    <LeadPagination loading={loading} page={page} size={size} totalElements={totalElements} totalPages={totalPages} firstRow={firstRow} lastRow={lastRow} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} as={Card.Footer} />
  </Card>;
}

// Rendered above and below the table so paging does not require scrolling to the bottom of a full page of leads.
function LeadPagination({ loading, page, size, totalElements, totalPages, firstRow, lastRow, onPageChange, onPageSizeChange, as: Wrapper = "div", className = "" }) {
  return <Wrapper className={`d-flex flex-wrap justify-content-between align-items-center gap-2 px-3 py-2 ${className}`}>
    <div className="small text-muted">{totalElements ? `Showing ${firstRow}-${lastRow} of ${totalElements}` : "No leads"}</div>
    <div className="d-flex align-items-center gap-2">
      <Form.Select size="sm" style={{ width: 120 }} value={size} onChange={(e) => onPageSizeChange(Number(e.target.value))} disabled={loading}>
        {LEAD_PAGE_SIZE_OPTIONS.map((option) => <option key={option} value={option}>{option} / page</option>)}
      </Form.Select>
      <Button size="sm" variant="outline-secondary" disabled={loading || page <= 0} onClick={() => onPageChange(page - 1)}>Previous</Button>
      <span className="small text-muted">Page {totalPages ? page + 1 : 0} of {totalPages}</span>
      <Button size="sm" variant="outline-secondary" disabled={loading || page + 1 >= totalPages} onClick={() => onPageChange(page + 1)}>Next</Button>
    </div>
  </Wrapper>;
}

const LeadRow = React.memo(function LeadRow({ rowNumber, lead, team, member, notYetCalledTargetMinutes = null, callMembers, loading, editable, actionsBlocked, blockedReason, callNeededSavingId, onEdit, onView, onQuotation, onToggleFlag, onAssignLead, onToggleCallNeeded }) {
  const myFlag = myLeadFlag(lead, member?.memberId);
  const flaggedByOthers = (lead.flags || []).some((flag) => flag.memberId !== member?.memberId);
  return <tr>
    <td className="small fw-semibold">{rowNumber}</td>
    <td className="fw-semibold"><Button size="sm" variant="link" className="p-0 me-2" title={lead.flags?.length ? `${flagSummaryText(lead.flags)}

Click to ${myFlag ? "edit or remove your flag" : "add your flag"}` : "Flag this lead to check later"} onClick={() => onToggleFlag?.(lead)}><i className={`bi ${myFlag ? "bi-flag-fill text-danger" : flaggedByOthers ? "bi-flag-fill text-warning" : "bi-flag text-muted"}`} /></Button>{lead.leadId}{myFlag?.remindAt && <FlagReminderBadge remindAt={myFlag.remindAt} className="ms-2" />}{lead.callNeeded && <Badge bg="danger" className="ms-2">Call Needed</Badge>}<CallNeededTiming lead={lead} /><NotYetCalledWaiting lead={lead} targetMinutes={notYetCalledTargetMinutes} /></td>
    <td>{lead.customerNumber}{lead.alternateNumber && <div className="small text-muted">Alt: {lead.alternateNumber}</div>}</td>
    <td>{lead.customerLanguage || "-"}</td>
    <td>{lead.destinationName || "-"}</td>
    <td>{formatDateOnly(lead.plannedDates)}</td>
    <td>{lead.numberOfPersons || 0} adults, {lead.numberOfKids || 0} kids{lead.kidsAge ? <div className="small text-muted">Kids age: {lead.kidsAge}</div> : null}</td>
    {team === "CALL" && <td><Badge bg={hasCallAttempt(lead) ? "success" : "warning"}>{hasCallAttempt(lead) ? "Called" : "Not Yet Called"}</Badge><div className="small text-muted mt-1">S {callCount(lead, "successful")} / U {callCount(lead, "unanswered")}</div></td>}
    {team === "CALL" && <td style={{ minWidth: 180 }}><AssignmentCell lead={lead} callMembers={callMembers} disabled={loading} onAssign={onAssignLead} /></td>}
    <td>{lead.interestStatus || "-"}</td>
    <td><div>{lead.followUpStatus || "-"}</div><div className="small text-muted">{formatDateTime(lead.nextFollowUpDateTime)}</div><Badge bg={badgeFor(lead.followUpCategory)}>{lead.followUpCategory || "Open"}</Badge></td>
    <td className="small">{formatDateTime(lead.createdTimeStamp)}</td>
    <td className="small">{formatDateTime(lead.lastUpdated)}</td>
    <td><LeadTimeCell lead={lead} /></td>
    {team === "CALL" && <td className="text-nowrap fw-semibold">{formatSeconds(lead.actualCallSeconds || 0)}</td>}
    <td className="text-end">
      {(team === "CALL" || team === "CHAT") && <Button size="sm" variant={lead.callNeeded ? "outline-danger" : "outline-warning"} className="me-2" onClick={() => onToggleCallNeeded?.(lead)} disabled={loading || callNeededSavingId === lead.id}>{callNeededSavingId === lead.id ? <Spinner size="sm" /> : lead.callNeeded ? "Clear Call" : "Call Needed"}</Button>}
      <Button size="sm" variant="outline-success" className="me-2" onClick={() => onQuotation?.(lead)} disabled={loading || !lead.destinationName}>Quotation</Button>{editable ? <Button size="sm" variant="outline-primary" onClick={() => onEdit(lead)} disabled={loading || actionsBlocked} title={actionsBlocked ? blockedReason : ""}>Edit</Button> : <Button size="sm" variant="outline-secondary" onClick={() => onView?.(lead)} disabled={loading}>View</Button>}
    </td>
  </tr>;
});

// The member dropdown is only built for the row being changed; rendering one per row put every member option in the
// DOM for every lead, which was a large part of what made this table slow to click.
function AssignmentCell({ lead, callMembers = [], disabled = false, onAssign }) {
  const [editing, setEditing] = useState(false);
  const legacyAssignedTo = String(lead.assignedTo || "").trim();
  if (!editing) {
    return <div>
      <div className="d-flex align-items-center gap-2">
        <span className="small">{lead.assignedCallMemberName || "Unassigned"}</span>
        <Button size="sm" variant="link" className="p-0 small" disabled={disabled} onClick={() => setEditing(true)}>Change</Button>
      </div>
      {!lead.assignedCallMemberName && legacyAssignedTo && <div className="small text-muted">{legacyAssignedTo}</div>}
    </div>;
  }
  return <Form.Select size="sm" autoFocus value={lead.assignedCallMemberId || 0} disabled={disabled} onBlur={() => setEditing(false)} onChange={(e) => { setEditing(false); onAssign?.(lead, e.target.value); }}>
    <option value={0}>Unassigned</option>
    {callMembers.map((item) => <option key={item.id} value={item.id}>{item.name || item.username}</option>)}
  </Form.Select>;
}
function LeadTimeCell({ lead }) {
  return <><div className="fw-semibold">{formatSeconds(lead.leadWorkTotalSeconds || 0)}</div><div className="small text-muted">{lead.leadWorkSessionCount || 0} sessions</div>{lead.leadWorkActive && <Badge bg="primary">Active</Badge>}</>;
}
function LeadViewModal({ show, lead, team, member, callingNumber, onCall, onHide }) {
  if (!lead) return null;
  const fields = [
    ["leadId", "Lead ID"], ["bookingId", "Booking ID"], ["staffName", "Staff Name"], ["customerNumber", "Customer Number"], ["alternateNumber", "Alternate Number"], ["destinationName", "Destination"],
    ["pickupType", "Pickup Type"], ["addons", "Add-ons"], ["plannedDates", "Planned Date"], ["numberOfPersons", "Adults"], ["numberOfKids", "Kids"], ["kidsAge", "Kids Age"], ["detailsSent", "Details Sent"],
    ["isCallDone", "Call Done"], ["frequency", "Successful Calls"], ["unresponsiveCallFrequency", "Unanswered Calls"], ["callNeeded", "Call Needed"], ["callNeededNote", "Call Needed Note"], ["callNeededMarkedBy", "Call Needed Marked By"], ["callNeededMarkedAt", "Call Needed Marked At"], ["callNeededRequestedAt", "Customer Asked for Call At"], ["interestStatus", "Interest Status"], ["finalStatus", "Final Status"], ["followUpStatus", "Follow-up"],
    ["followUpCategory", "Follow-up Category"], ["nextFollowUpDateTime", "Next Follow-up"], ["leadWorkTotalSeconds", "Time Spent"], ...(team === "CALL" ? [["actualCallSeconds", "Actual Call Time"]] : []), ["leadWorkSessionCount", "Work Sessions"], ["assignedCallMemberName", "Assigned To"], ["assignedTeam", "Assigned Team"], ["createdBy", "Created By"], ["createdTimeStamp", "Created Timestamp"], ["lastUpdated", "Last Updated"], ["flaggedBy", "Flagged By"], ["flagNote", "Flag Reason"]
  ];
  return <Modal show={show} onHide={onHide} size="lg" centered><Modal.Header closeButton><Modal.Title>View Lead {lead.leadId}</Modal.Title></Modal.Header><Modal.Body><Table size="sm" className="mb-0"><tbody>{fields.map(([key, label]) => <tr key={key}><th style={{ width: "34%" }}>{label}</th><td>{key === "customerNumber" && team === "CALL" ? <div className="d-flex align-items-center gap-2"><span>{formatLeadViewValue(lead, key)}</span><Button type="button" size="sm" variant="outline-success" title={canCallLead(member, lead) ? "Call customer" : "Customer preferred language is not assigned to you"} onClick={() => onCall?.(lead)} disabled={Boolean(callingNumber) || !canCallLead(member, lead)}>{callingNumber === lead.customerNumber ? <Spinner size="sm" /> : <i className="bi bi-telephone-fill" />}</Button></div> : key === "alternateNumber" && team === "CALL" && lead.alternateNumber ? <div className="d-flex align-items-center gap-2"><span>{lead.alternateNumber}</span><Button type="button" size="sm" variant="outline-success" title={canCallLead(member, lead) ? "Call the alternate number" : "Customer preferred language is not assigned to you"} onClick={() => onCall?.({ ...lead, customerNumber: lead.alternateNumber })} disabled={Boolean(callingNumber) || !canCallLead(member, lead)}>{callingNumber === lead.alternateNumber ? <Spinner size="sm" /> : <i className="bi bi-telephone-fill" />}</Button></div> : formatLeadViewValue(lead, key)}</td></tr>)}</tbody></Table></Modal.Body><Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button></Modal.Footer></Modal>;
}
export function LeadEditor({ show, team, member, callMembers = [], conversionMembers = [], initial, newLeadDefaults = null, leadWork = null, newLeadStartedAt = "", categories, interestStatuses, interestStatusOptions = [], dailyUpdateSuggestions = [], bookingOptions = [], bookingOptionsLoading = false, onNeedBookingOptions, saving = false, callingNumber = "", onCall, onManualCall, onToggleCallNeeded, callNeededSaving = false, onQuotation, onLoadHistory, onLoadCalls, onHide, onSave }) {
  const [form, setForm] = useState(blankLead);
  const formBaselineRef = useRef(null);
  const [leadPricingOptions, setLeadPricingOptions] = useState(null);
  const [leadPricingOptionsLoading, setLeadPricingOptionsLoading] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [callsExpanded, setCallsExpanded] = useState(false);
  const [callRows, setCallRows] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsError, setCallsError] = useState("");
  const fields = team === "CALL" || team === "ADMIN" ? callFields : chatFields;
  const visibleFields = fields.flatMap((field) => field === "numberOfKids" && Number(form.numberOfKids || 0) > 0 ? [field, "kidsAge"] : [field]);
  const selectedCategory = categories.find((category) => String(category.id) === String(form.categoryId));
  const trips = selectedCategory?.trips || [];
  const selectedTrip = trips.find((trip) => trip.title === form.destinationName);
  const sessionStartedAt = show
    ? initial?.id && String(leadWork?.leadId || "") === String(initial.id)
      ? leadWork.startedAt
      : !initial?.id
        ? newLeadStartedAt
        : ""
    : "";
  useEffect(() => {
    let cancelled = false;
    if (!selectedTrip?.id) {
      setLeadPricingOptions(null);
      setLeadPricingOptionsLoading(false);
      return undefined;
    }
    const tripId = selectedTrip.id;
    setLeadPricingOptions(null);
    setLeadPricingOptionsLoading(true);
    fetchTripQuoteOptions(tripId)
      .then((data) => { if (!cancelled) setLeadPricingOptions({ tripId: String(tripId), data }); })
      .catch(() => { if (!cancelled) setLeadPricingOptions(null); })
      .finally(() => { if (!cancelled) setLeadPricingOptionsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedTrip?.id]);
  useEffect(() => {
    setHistoryExpanded(false);
    setHistoryRows([]);
    setHistoryError("");
    setCallsExpanded(false);
    setCallRows([]);
    setCallsError("");
    const staffName = member?.username || member?.name || "";
    const defaultInterestStatus = interestStatuses[0] || blankLead.interestStatus;
    if (!initial) {
      setForm({ ...blankLead, staffName, interestStatus: defaultInterestStatus, nextFollowUpDateTime: suggestedFollowUpForStatus(defaultInterestStatus, interestStatusOptions), conversionCredits: defaultConversionCredits(member), ...(newLeadDefaults || {}) });
      return;
    }
    const inferredCategoryId = findCategoryIdForDestination(categories, initial.destinationName);
    const initialInterestStatus = interestStatuses.includes(initial.interestStatus) ? initial.interestStatus : defaultInterestStatus;
    const fresh = {
      ...blankLead,
      ...initial,
      categoryId: initial.categoryId || inferredCategoryId,
      staffName: initial.staffName || staffName,
      interestStatus: initialInterestStatus,
      dailyUpdates: "",
      dailyUpdateSuggestionId: "",
      chatTeamPersonNotes: team === "CHAT" || team === "ADMIN" ? "" : initial.chatTeamPersonNotes,
      plannedDates: toDateInput(initial.plannedDates),
      nextFollowUpDateTime: toInputDateTime(initial.nextFollowUpDateTime),
      flagRequested: Boolean(myLeadFlag(initial, member?.memberId)),
      flagNote: myLeadFlag(initial, member?.memberId)?.note || "",
      flagRemindAt: toReminderInput(myLeadFlag(initial, member?.memberId)?.remindAt),
      callNeededRequestedAt: toReminderInput(initial.callNeededRequestedAt),
      // The Converted By box shows this default when nothing is saved yet; it has to be in the
      // form too, or Save sees 0% until the percentage is retyped.
      conversionCredits: normalizeConversionCredits(initial.conversionCredits, member),
    };
    // The same open lead was refreshed from the server (manual call update, call-needed toggle):
    // take the new server values but keep whatever the user has changed and not saved yet.
    const baseline = formBaselineRef.current;
    const refreshingOpenLead = show && baseline && baseline.id === initial.id;
    formBaselineRef.current = fresh;
    if (!refreshingOpenLead) {
      setForm(fresh);
      return;
    }
    setForm((current) => {
      const edits = Object.fromEntries(Object.entries(current).filter(([key, value]) => value !== baseline[key]));
      return { ...fresh, ...edits };
    });
  }, [initial, newLeadDefaults, show, member, categories, interestStatuses, interestStatusOptions, team]);
  useEffect(() => {
    if (!show) formBaselineRef.current = null;
  }, [show]);
  const loadHistory = async () => {
    const nextExpanded = !historyExpanded;
    setHistoryExpanded(nextExpanded);
    if (!nextExpanded || !initial?.id || historyRows.length || !onLoadHistory) return;
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const rows = await onLoadHistory(initial.id);
      setHistoryRows(filterLeadTimeline(rows || []));
    } catch (error) {
      setHistoryError(error?.response?.data?.message || "Could not load lead history");
    } finally {
      setHistoryLoading(false);
    }
  };
  const loadCalls = async () => {
    const nextExpanded = !callsExpanded;
    setCallsExpanded(nextExpanded);
    if (!nextExpanded || !initial?.id || callRows.length || !onLoadCalls) return;
    setCallsLoading(true);
    setCallsError("");
    try {
      const rows = await onLoadCalls(initial.id);
      setCallRows(rows || []);
    } catch (error) {
      setCallsError(error?.response?.data?.message || "Could not load call recordings");
    } finally {
      setCallsLoading(false);
    }
  };
  const closeStatusSelected = isCloseStatus(form.interestStatus, interestStatusOptions);
  const bookingLocked = Boolean(initial?.bookingId);
  const bookingConversionRequired = form.interestStatus === "Confirmed" && !initial?.bookingId;
  const paymentReceiptReady = !bookingConversionRequired || (form.paymentReceiptShared && form.receiptDownloadedFingerprint === paymentReceiptFingerprint(form));
  const travelPolicyReady = !bookingConversionRequired || (form.travelPolicyShared && form.policyDownloadedFingerprint === paymentReceiptFingerprint(form));
  const extensionVehicleReady = !bookingConversionRequired
    || !form.extensionVehicleRequired
    || (extensionVehicleAmountsEntered(form.dynamicQuoteRequest) && Boolean(form.extensionVehicleAcknowledged));

  useEffect(() => {
    if (show && bookingConversionRequired) {
      onNeedBookingOptions?.();
    }
  }, [show, bookingConversionRequired, onNeedBookingOptions]);
  const update = (field, value) => setForm((old) => {
    const next = { ...old, [field]: value };
    if (field === "interestStatus") {
      next.nextFollowUpDateTime = isCloseStatus(value, interestStatusOptions) ? "" : suggestedFollowUpForStatus(value, interestStatusOptions);
    }
    return next;
  });
  const submit = async (event) => {
    event.preventDefault();
    if (leadEditorOnlyCallNeededChanged(initial, form, member, categories, interestStatuses, team)) {
      await handleCallNeededUpdate();
      return;
    }
    if (detailsSentAfterCall) {
      toast.error("This customer has already been called. Change the Interest Status from Details Sent before saving.");
      return;
    }
    if (bookingConversionRequired) {
      if (!form.bookingCustomerName?.trim()) return toast.error("Customer name is required");
      if (!form.bookingTripId) return toast.error("Trip is required");
      if (!form.dynamicQuoteRequest || Number(form.dynamicPackageTotal) <= 0) return toast.error("Complete the dynamic booking details");
      if (form.customizationRequired === "Yes" && !form.customizationDescription?.trim()) return toast.error("Customization details are required");
      if (form.customizationRequired === "Yes" && Number(form.customizationPrice) <= 0) return toast.error("Customization price must be greater than zero");
      if (form.customizationRequired === "Yes" && form.customizationPriceAdjustment === "REDUCE" && Number(form.customizationPrice) >= Number(form.dynamicPackageTotal)) return toast.error("Customization reduction must be less than the package total");
      if (Number(form.advancePayment) <= 0) return toast.error("Advance payment must be greater than zero");
      if (!form.plannedDates) return toast.error("Planned date is required");
      if (!form.pickupDateTime) return toast.error("Vehicle pickup date and time are required");
      if (!form.dropDateTime) return toast.error("Vehicle drop date and time are required");
      if (new Date(form.dropDateTime) <= new Date(form.pickupDateTime)) return toast.error("Vehicle drop date and time must be after vehicle pickup date and time");
      if (form.refreshmentNeeded && !form.refreshmentRoomId) return toast.error("Select a refreshment room");
      if (!(form.driverLanguages || []).length) return toast.error("Select at least one preferred driver language");
      if (conversionCreditTotal(form.conversionCredits) !== 100) return toast.error("Converted-by contribution total must be exactly 100%");
      if (!paymentReceiptReady) return toast.error("Download and share the current payment receipt before saving");
      if (!travelPolicyReady) return toast.error("Download and share the current travel policy before saving");
      if (!extensionVehicleReady) return toast.error("Enter the extension vehicle amount and confirm it before saving");
    }
    // The customer message for this update promises a call time, so it cannot go out blank.
    const pickedTemplate = team === "ADMIN" ? null : selectedDailyUpdateTemplate(form, dailyUpdateSuggestions);
    if (dailyUpdateTemplateNeedsCallbackTime(pickedTemplate) && (closeStatusSelected || !form.nextFollowUpDateTime)) {
      toast.error(`Set the Next Follow-up date and time. The "${pickedTemplate.text}" message tells the customer when we will call.`);
      return;
    }
    if (!closeStatusSelected && form.nextFollowUpDateTime && !isFutureDateTime(form.nextFollowUpDateTime)) {
      toast.error("Next follow-up date/time must be in the future");
      return;
    }
    if (form.plannedDates && !isTodayOrFutureDate(form.plannedDates)) {
      toast.error("Planned date must be today or a future date");
      return;
    }
    onSave(closeStatusSelected ? { ...form, nextFollowUpDateTime: "" } : form);
  };
  const handleManualCall = async (outcome) => {
    if (!initial?.id || !onManualCall) return;
    // The refreshed lead flows back in through `initial`, which keeps the user's unsaved edits.
    await onManualCall({ ...initial, ...form, id: initial.id }, outcome);
  };
  const handleCallNeededUpdate = async () => {
    if (!initial?.id || !onToggleCallNeeded) return;
    const updated = await onToggleCallNeeded({ ...initial, ...form, id: initial.id }, Boolean(form.callNeeded), form.callNeededNote || "", form.callNeededRequestedAt || null);
    if (updated) {
      setForm((old) => ({ ...old, ...updated }));
    }
  };
  const suggestedFollowUp = suggestedFollowUpForStatus(form.interestStatus, interestStatusOptions);
  const detailsSentAfterCall = calledLeadStillDetailsSent({ ...(initial || {}), ...form });
  const modalLoadingMessage = saving
    ? "Saving lead..."
    : bookingOptionsLoading
      ? "Loading booking details..."
      : leadPricingOptionsLoading
        ? "Loading destination options..."
        : historyLoading
          ? "Loading lead timeline..."
          : "";
return <Modal show={show} onHide={onHide} size="lg" centered className="lead-edit-modal"><Form onSubmit={submit}><Modal.Header closeButton={!saving}><Modal.Title>{initial ? "Edit Lead" : "New Lead"}</Modal.Title></Modal.Header><Modal.Body>{modalLoadingMessage && <div className="d-flex align-items-center gap-2 rounded border bg-light px-3 py-2 mb-3 small fw-semibold text-primary"><Spinner size="sm" />{modalLoadingMessage}</div>}{bookingLocked && <div className="alert alert-warning py-2">Booking #{initial.bookingId} has been created. This lead is read-only.</div>}{sessionStartedAt && <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 rounded border border-primary-subtle bg-primary-subtle px-3 py-2 mb-3 shadow-sm" style={{ position: "sticky", top: 0, zIndex: 5 }}><div><div className="small text-muted">Current session time</div><div className="fw-semibold text-primary"><SessionTimer startedAt={sessionStartedAt} /></div></div><div className="small text-muted">Started {formatDateTime(sessionStartedAt)}</div></div>}<Row className="g-3">{visibleFields.map((field) => <LeadField key={field} field={field} team={team} value={form[field]} initialValue={initial?.[field]} isCallDone={initial?.isCallDone} kidsCount={Number(form.numberOfKids || 0)} trips={trips} categories={categories} pricingOptions={leadPricingOptions?.tripId === String(selectedTrip?.id) ? leadPricingOptions.data : null} interestStatuses={interestStatuses} dailyUpdateSuggestions={dailyUpdateSuggestions} callMembers={callMembers} followUpDisabled={closeStatusSelected} suggestedFollowUp={suggestedFollowUp} callingNumber={callingNumber} member={member} lead={{ ...(initial || {}), ...form }} onCall={onCall} onChange={update} />)}{(team === "CALL" || team === "CHAT") && <Col xs={12}><div className="border rounded bg-white p-3"><Form.Check type="checkbox" id="lead-call-needed" label="Call Needed" checked={Boolean(form.callNeeded)} disabled={saving} onChange={(e) => update("callNeeded", e.target.checked)} />{Boolean(form.callNeeded) && <><Form.Label className="mt-2">Call Needed Note</Form.Label><Form.Control as="textarea" rows={2} value={form.callNeededNote || ""} disabled={saving} onChange={(e) => update("callNeededNote", e.target.value)} placeholder="Reason or preferred callback time" /><div className="mt-2"><CallRequestTimeField value={toReminderInput(form.callNeededRequestedAt)} disabled={saving} onChange={(value) => update("callNeededRequestedAt", value)} /></div>{form.callNeededMarkedBy && <div className="small text-muted mt-2">Marked by {form.callNeededMarkedBy}{form.callNeededMarkedAt ? ` at ${formatDateTime(form.callNeededMarkedAt)}` : ""}</div>}</>}</div></Col>}{team !== "ADMIN" && <Col xs={12}><Form.Check type="checkbox" id="lead-flag-requested" label={<span><i className="bi bi-flag-fill text-danger me-1" /> Flag this lead so I can check on it later{!initial && " (applied after saving)"}</span>} checked={Boolean(form.flagRequested)} disabled={saving} onChange={(e) => update("flagRequested", e.target.checked)} />{Boolean(form.flagRequested) && <><Form.Control className="mt-2" placeholder="Why are you flagging this lead? (optional)" value={form.flagNote || ""} disabled={saving} onChange={(e) => update("flagNote", e.target.value)} /><InputGroup className="mt-2" size="sm"><InputGroup.Text><i className="bi bi-alarm me-1" />Remind me</InputGroup.Text><Form.Control type="datetime-local" value={form.flagRemindAt || ""} disabled={saving} onChange={(e) => update("flagRemindAt", e.target.value)} />{form.flagRemindAt && <Button variant="outline-secondary" disabled={saving} onClick={() => update("flagRemindAt", "")}>Clear</Button>}</InputGroup><Form.Text muted>Only you see this reminder; it does not change the lead's follow-up date.</Form.Text></>}</Col>}{bookingConversionRequired && <ConfirmedBookingFields form={form} team={team} member={member} conversionMembers={conversionMembers} options={bookingOptions} categories={categories} bookingOptionsLoading={bookingOptionsLoading} onChange={update} />}{initial?.bookingId && form.interestStatus === "Confirmed" && <Col xs={12}><div className="alert alert-success mb-0">Converted to Booking #{initial.bookingId}{conversionCreditsText(initial.conversionCredits) && <div className="small mt-1">Converted by: <span className="fw-semibold">{conversionCreditsText(initial.conversionCredits)}</span></div>}</div></Col>}{team === "CALL" && <><Col md={4}><Form.Label>Successful Calls</Form.Label><Form.Control value={form.frequency || 0} readOnly disabled /></Col><Col md={4}><Form.Label>Unanswered Calls</Form.Label><Form.Control value={form.unresponsiveCallFrequency || 0} readOnly disabled /></Col>{initial?.id && member?.canManualCallUpdate && <Col md={4}><Form.Label>Manual Call Update</Form.Label><div className="d-flex gap-2"><Button type="button" variant="outline-success" onClick={() => handleManualCall("SUCCESSFUL")} disabled={saving}>Successful</Button><Button type="button" variant="outline-secondary" onClick={() => handleManualCall("UNANSWERED")} disabled={saving}>Unanswered</Button></div></Col>}{!initial?.id && member?.canManualCallUpdate && <Col md={4}><Form.Label>Manual Call Update</Form.Label><Form.Check type="checkbox" id="lead-incoming-call" label="Customer called us - mark as answered" checked={Boolean(form.incomingCallAnswered)} disabled={saving} onChange={(e) => update("incomingCallAnswered", e.target.checked)} /><div className="small text-muted">Recorded as a successful call when you save.</div></Col>}</>} {initial?.id && onLoadHistory && <Col xs={12}><Card className="lead-history-timeline-card"><Card.Header className="d-flex justify-content-between align-items-center"><span className="fw-semibold">Lead Timeline</span><Button type="button" size="sm" variant="outline-secondary" onClick={loadHistory}>{historyExpanded ? "Hide" : "Show"}</Button></Card.Header>{historyExpanded && <Card.Body>{historyLoading && <div className="text-center py-3"><Spinner size="sm" /></div>}{historyError && <div className="text-danger small">{historyError}</div>}{!historyLoading && !historyError && <LeadTimeline rows={historyRows} />}</Card.Body>}</Card></Col>}{initial?.id && onLoadCalls && <Col xs={12}><Card><Card.Header className="d-flex justify-content-between align-items-center"><span className="fw-semibold">Call Recordings</span><Button type="button" size="sm" variant="outline-secondary" onClick={loadCalls}>{callsExpanded ? "Hide" : "Show"}</Button></Card.Header>{callsExpanded && <Card.Body>{callsLoading && <div className="text-center py-3"><Spinner size="sm" /></div>}{callsError && <div className="text-danger small">{callsError}</div>}{!callsLoading && !callsError && <LeadCallRecordings rows={callRows} />}</Card.Body>}</Card></Col>}</Row></Modal.Body><Modal.Footer><Button variant="outline-success" onClick={() => onQuotation?.(form)} disabled={saving || !selectedTrip?.path}>Quotation</Button><Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button><Button type="submit" disabled={bookingLocked || saving || bookingOptionsLoading || !paymentReceiptReady || !travelPolicyReady || !extensionVehicleReady || detailsSentAfterCall}>{saving ? <><Spinner size="sm" className="me-2" />Saving Lead</> : "Save Lead"}</Button></Modal.Footer></Form></Modal>;
}

// Kept out of LeadEditor on purpose: this ticks once a second, and inside the editor it re-rendered the whole form
// (including ConfirmedBookingFields) every second.
function SessionTimer({ startedAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!startedAt) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [startedAt]);
  if (!startedAt) return null;
  return <>{formatSeconds(elapsedSecondsSince(startedAt, now))}</>;
}
function LeadCallRecordings({ rows = [] }) {
  if (!rows.length) return <div className="text-center text-muted py-3">No calls logged for this lead.</div>;
  return <div className="d-flex flex-column gap-2">{rows.map((call) => {
    const incoming = call.callDirection === "INBOUND";
    return <div className="border rounded p-2" key={call.id}>
      <div className="d-flex flex-wrap justify-content-between gap-2 mb-1">
        <div className="small">
          <Badge bg={incoming ? "info" : "primary"} text={incoming ? "dark" : undefined} className="me-2">{incoming ? "Incoming" : "Outgoing"}</Badge>
          {incoming ? <>{call.phoneNumber} to <CallerName call={call} /></> : <><CallerName call={call} /> to {call.phoneNumber}</>}
          {call.callStatus ? <Badge bg={call.callStatus === "Answered" ? "success" : "secondary"} className="ms-2">{call.callStatus}</Badge> : null}
        </div>
        <div className="text-muted small">{formatTimelineDateTime(call.calledAt)}</div>
      </div>
      {!call.resolved && <div className="small text-muted">Recording pending...</div>}
      {call.resolved && call.callRecPath && <audio controls src={call.callRecPath} style={{ width: "100%" }} />}
      {call.resolved && !call.callRecPath && <div className="small text-muted">No recording available{call.callSeconds != null ? ` (${call.callSeconds}s call)` : ""}.</div>}
    </div>;
  })}</div>;
}

function LeadTimeline({ rows = [] }) {
  const groups = groupLeadTimeline(rows);
  if (!groups.length) return <div className="text-center text-muted py-3">No timeline entries found.</div>;
  return <div className="lead-history-timeline">{groups.map((group) => <div className="lead-history-timeline-item" key={group.key}><div className="lead-history-dot" /><div className="lead-history-content"><div className="d-flex flex-wrap justify-content-between gap-2 mb-1"><div className="fw-semibold">{group.updatedBy || "-"}{group.updatedByTeam ? ` (${group.updatedByTeam})` : ""}</div><div className="small text-muted">{formatTimelineDateTime(group.updatedAt)}</div></div><div className="lead-history-change-list">{group.items.map((row) => <div className="lead-history-change" key={row.id || `${row.fieldName}-${row.oldValue}-${row.newValue}`}><div className="fw-semibold small">{row.fieldName}</div><div className="small">{timelineChangedValue(row)}</div></div>)}</div></div></div>)}</div>;
}

function groupLeadTimeline(rows = []) {
  const groups = new Map();
  rows.forEach((row) => {
    const key = [row.updatedAt || "", row.updatedBy || "", row.updatedByTeam || ""].join("|");
    const group = groups.get(key) || { key, updatedAt: row.updatedAt, updatedBy: row.updatedBy, updatedByTeam: row.updatedByTeam, items: [] };
    group.items.push(row);
    groups.set(key, group);
  });
  return Array.from(groups.values());
}

function filterLeadTimeline(rows = []) {
  const allowed = new Set([
    "daily updates",
    "chat team person notes",
    "interest status",
    "successful calls",
    "unanswered calls",
    "next follow-up date/time",
    "follow up time",
  ]);
  return rows.filter((row) => allowed.has(String(row?.fieldName || "").trim().toLowerCase()));
}

function timelineChangedValue(row) {
  const field = String(row?.fieldName || "").trim().toLowerCase();
  if (field === "daily updates") {
    return appendedTimelineValue(row);
  }
  if (field === "next follow-up date/time" || field === "follow up time") {
    return formatTimelineDateTime(row?.newValue);
  }
  const text = String(row?.newValue ?? "").trim();
  return text || "-";
}

function appendedTimelineValue(row) {
  const oldText = String(row?.oldValue ?? "").trim();
  const newText = String(row?.newValue ?? "").trim();
  if (!newText) return "-";
  const addedText = oldText && newText.startsWith(oldText)
    ? newText.slice(oldText.length).trim()
    : sameDayTimelineEntries(newText, row?.updatedAt).join("\n").trim();
  const lines = (addedText || newText).split(/\r?\n/).map(cleanTimestampedUpdateLine).filter(Boolean);
  return lines.join("; ") || "-";
}

function sameDayTimelineEntries(value, updatedAt) {
  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return [];
  const dayPrefix = `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${date.getFullYear()}`;
  return String(value || "").split(/\r?\n/).filter((line) => line.trim().startsWith(dayPrefix));
}

function cleanTimestampedUpdateLine(value) {
  const text = String(value || "").trim();
  const match = text.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}\s+[AP]M\s+-\s+.*?\s+-\s+(.*)$/);
  return (match?.[1] || text).trim();
}

function formatTimelineDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const period = date.getHours() >= 12 ? "PM" : "AM";
  const hour12 = date.getHours() % 12 || 12;
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()} ${pad2(hour12)}:${pad2(date.getMinutes())} ${period}`;
}

function DriverLanguagePriorityField({ team, selected, onChange }) {
  const [available, setAvailable] = useState([]);
  useEffect(() => {
    let active = true;
    loadEditorLanguages(team).then((rows) => { if (active) setAvailable(rows); });
    return () => { active = false; };
  }, [team]);
  const unselected = available.filter((language) => !selected.includes(language.name));
  const move = (index, offset) => {
    const target = index + offset;
    if (target < 0 || target >= selected.length) return;
    const next = [...selected];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  return <Col md={4}>
    <Form.Label>Preferred Driver Language *</Form.Label>
    <div className="border rounded px-3 py-2 h-100">
      <Form.Select size="sm" value="" onChange={(e) => { if (e.target.value) onChange([...selected, e.target.value]); }} disabled={!unselected.length}>
        <option value="">{available.length ? (unselected.length ? "Add language" : "All languages added") : "Loading languages..."}</option>
        {unselected.map((language) => <option key={language.id || language.name} value={language.name}>{language.name}</option>)}
      </Form.Select>
      {selected.length ? <ol className="mb-0 mt-2 ps-3 small">
        {selected.map((name, index) => <li key={name} className="d-flex align-items-center justify-content-between gap-2 py-1">
          <span>{name}</span>
          <span className="d-flex gap-1">
            <Button type="button" size="sm" variant="outline-secondary" className="py-0 px-1" title="Higher priority" disabled={index === 0} onClick={() => move(index, -1)}><i className="bi bi-arrow-up" /></Button>
            <Button type="button" size="sm" variant="outline-secondary" className="py-0 px-1" title="Lower priority" disabled={index === selected.length - 1} onClick={() => move(index, 1)}><i className="bi bi-arrow-down" /></Button>
            <Button type="button" size="sm" variant="outline-danger" className="py-0 px-1" title="Remove" onClick={() => onChange(selected.filter((item) => item !== name))}><i className="bi bi-x" /></Button>
          </span>
        </li>)}
      </ol> : null}
      <div className={`small mt-1 ${selected.length ? "text-muted" : "text-danger"}`}>
        {selected.length ? "Listed in priority order - the first language matters most." : "Select at least one language, most preferred first."}
      </div>
    </div>
  </Col>;
}

function ConversionCreditEditor({ value = [], members = [], currentMember = null, onChange, disabled = false }) {
  const rows = Array.isArray(value) && value.length ? value : defaultConversionCredits(currentMember);
  const activeMembers = (members || []).filter((member) => member?.active !== false);
  const total = conversionCreditTotal(rows);
  const updateRow = (index, changes) => onChange(rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...changes } : row));
  const addRow = () => onChange([...rows, { memberId: "", contributionPercent: "" }]);
  const removeRow = (index) => {
    const next = rows.filter((_, rowIndex) => rowIndex !== index);
    onChange(next.length ? next : defaultConversionCredits(currentMember));
  };
  return (
    <Col xs={12}>
      <div className="border rounded bg-white p-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <Form.Label className="mb-0 fw-semibold">Converted By *</Form.Label>
          <Badge bg={total === 100 ? "success" : "danger"}>{total}% / 100%</Badge>
        </div>
        <div className="d-flex flex-column gap-2">
          {rows.map((row, index) => (
            <Row className="g-2 align-items-end" key={`${row.memberId || "new"}-${index}`}>
              <Col md={7}>
                <Form.Label className="small mb-1">Team member</Form.Label>
                <Form.Select
                  value={row.memberId || ""}
                  disabled={disabled}
                  onChange={(event) => updateRow(index, { memberId: event.target.value })}
                >
                  <option value="">Select team member</option>
                  {activeMembers.map((member) => (
                    <option key={member.id} value={member.id}>{conversionMemberLabel(member)}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small mb-1">Contribution %</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  value={row.contributionPercent ?? ""}
                  disabled={disabled}
                  onChange={(event) => updateRow(index, { contributionPercent: event.target.value })}
                />
              </Col>
              <Col md={2} className="d-grid">
                <Button type="button" variant="outline-danger" disabled={disabled || rows.length === 1} onClick={() => removeRow(index)}>Remove</Button>
              </Col>
            </Row>
          ))}
        </div>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
          <Button type="button" size="sm" variant="outline-primary" disabled={disabled} onClick={addRow}>Add Member</Button>
          {total !== 100 && <span className="small text-danger">Contribution total must be exactly 100%.</span>}
        </div>
      </div>
    </Col>
  );
}

function ConfirmedBookingFields({ form, team, member, conversionMembers = [], options, categories, bookingOptionsLoading = false, onChange }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [calculatedQuote, setCalculatedQuote] = useState(null);
  const [roomAvailabilityByKey, setRoomAvailabilityByKey] = useState({});
  const [extensionAvailabilityByKey, setExtensionAvailabilityByKey] = useState({});
  const quote = form.dynamicQuoteRequest || {};
  const tripId = form.bookingTripId;
  const categoryId = form.bookingCategoryId || form.categoryId || "";
  const selectedCategory = (categories || []).find((category) => String(category.id) === String(categoryId));
  const categoryTripIds = new Set((selectedCategory?.trips || []).map((trip) => String(trip.id)));
  const filteredTrips = categoryId ? options.filter((option) => categoryTripIds.has(String(option.tripId))) : [];
  useEffect(() => {
    let cancelled = false;
    if (!tripId) { setConfig(null); return undefined; }
    setLoading(true); setQuoteError("");
    fetchTripQuoteOptions(tripId).then((data) => {
      if (cancelled) return;
      setConfig(data);
      const pickup = (data.pickups || []).find((item) => item.active !== false);
      setCalculatedQuote(null);
      onChange("dynamicQuoteRequest", { adults: Number(form.numberOfPersons || 0), kids: Number(form.numberOfKids || 0), kidAges: parseKidsAgeValue(form.kidsAge).map(Number), kidVehicleSeatNeeded: [], pickupId: pickup?.id || null, vehicleId: null, acType: "AC", selectedAddonIds: [], selectedRoomRateIds: [], selectedRoomRateNightSelections: [], selectedRoomCityByNight: {}, selectedRoomCategoryIds: [], selectedRoomUnits: [], selectedRoomCategorySelections: [], selectedHotelFacilities: [], travelDate: form.plannedDates || null, tripExtensionRequired: false, extensionScope: "BOTH", extraPreNights: 0, extraPostNights: 0, extensionStays: [], tripExtensionNote: "" });
      onChange("dynamicPackageTotal", "");
    }).catch(() => { if (!cancelled) setQuoteError("Could not load dynamic booking details for this trip"); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);
  useEffect(() => {
    if (!tripId || !form.dynamicQuoteRequest) return;
    const adults = Number(form.numberOfPersons || 0);
    const kids = Number(form.numberOfKids || 0);
    const kidAges = parseKidsAgeValue(form.kidsAge).map(Number);
    const travelDate = form.plannedDates || null;
    if (Number(quote.adults || 0) === adults
        && Number(quote.kids || 0) === kids
        && JSON.stringify(quote.kidAges || []) === JSON.stringify(kidAges)
        && (quote.travelDate || null) === travelDate) return;
    onChange("dynamicQuoteRequest", {
      ...quote,
      adults,
      kids,
      kidAges,
      kidVehicleSeatNeeded: Array.from(
        { length: kids },
        (_, index) => Boolean(quote.kidVehicleSeatNeeded?.[index]),
      ),
      travelDate,
    });
    onChange("dynamicPackageTotal", "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, form.numberOfPersons, form.numberOfKids, form.kidsAge, form.plannedDates]);
  // What is actually free on these dates, so a room that is already sold cannot be picked here and
  // only fail on save - by which point the advance is collected and the receipt is with the
  // customer. Same source the customer trip page and the admin booking modal read.
  useEffect(() => {
    if (!tripId || !form.plannedDates) {
      setRoomAvailabilityByKey({});
      return undefined;
    }
    let cancelled = false;
    fetchTripRoomAvailability(tripId, form.plannedDates)
      .then((items) => {
        if (cancelled) return;
        setRoomAvailabilityByKey(
          Object.fromEntries(items.map((item) => [`${item.roomRateId}:${item.roomCategoryId}`, item]))
        );
      })
      .catch(() => {
        if (!cancelled) setRoomAvailabilityByKey({});
      });
    return () => { cancelled = true; };
  }, [tripId, form.plannedDates]);
  // Any quote edit invalidates the acknowledgement too - otherwise it could be ticked early and
  // the pickup, nights or amount changed underneath it.
  const updateQuote = (changes) => { setCalculatedQuote(null); onChange("dynamicQuoteRequest", { ...quote, ...changes }); onChange("dynamicPackageTotal", ""); onChange("extensionVehicleAcknowledged", false); };
  // The planned date is the package's Day 1. The vehicle can run longer: an extra pre-night that
  // includes the vehicle starts it earlier, a post-night that includes the vehicle ends it later.
  const vehiclePreDays = vehicleExtensionDays(quote, "PRE");
  const vehiclePostDays = vehicleExtensionDays(quote, "POST");
  const updateBookingPlannedDate = (nextDate, { resetPickup = true } = {}) => {
    onChange("plannedDates", nextDate);
    if (quote) {
      updateQuote({ travelDate: nextDate || null });
    }
    // Default pickup/drop times follow the vehicle's first and last day; the employee can still
    // edit either field afterwards if the default doesn't fit. When this runs because the
    // employee just typed a specific pickup time (see updatePickupDateTime), resetPickup is false
    // so we don't stomp on the time they just chose.
    const vehicle = vehicleTripDates(nextDate, quote, config?.durationNights);
    if (resetPickup) {
      onChange("pickupDateTime", defaultPickupDateTime(vehicle.start));
    }
    onChange("dropDateTime", defaultDropDateTime(vehicle.end));
  };
  const updatePickupDateTime = (nextDateTime) => {
    onChange("pickupDateTime", nextDateTime);
    // A pickup on the vehicle pre-night is still the same package: Day 1 stays that many days later.
    const pickupDate = String(nextDateTime || "").slice(0, 10);
    const plannedDate = pickupDate ? addDaysToDateInput(pickupDate, vehiclePreDays) : "";
    if (plannedDate && plannedDate !== form.plannedDates) {
      updateBookingPlannedDate(plannedDate, { resetPickup: false });
    }
  };
  useEffect(() => {
    // Drop time depends on the trip's duration, which only becomes known once a trip is
    // selected and its config finishes loading - recompute it whenever that duration changes
    // (including switching to a different trip). Pickup time doesn't depend on duration, so it's
    // only filled in here if it isn't set yet (e.g. first time a trip is picked for this lead).
    if (!form.plannedDates || config?.durationNights == null) return;
    const vehicle = vehicleTripDates(form.plannedDates, quote, config.durationNights);
    if (!form.pickupDateTime) {
      onChange("pickupDateTime", defaultPickupDateTime(vehicle.start));
    }
    onChange("dropDateTime", defaultDropDateTime(vehicle.end));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.durationNights]);
  // Adding, removing or re-scoping an extra night moves the vehicle's first/last day. Move the
  // pickup/drop dates with it and keep whatever times the employee already chose.
  const vehicleExtensionKey = `${vehiclePreDays}:${vehiclePostDays}`;
  const previousVehicleExtensionKey = useRef(vehicleExtensionKey);
  useEffect(() => {
    if (previousVehicleExtensionKey.current === vehicleExtensionKey) return;
    previousVehicleExtensionKey.current = vehicleExtensionKey;
    if (!form.plannedDates) return;
    const vehicle = vehicleTripDates(form.plannedDates, quote, config?.durationNights);
    if (vehicle.start) onChange("pickupDateTime", `${vehicle.start}T${timeOfDateTime(form.pickupDateTime, "06:00")}`);
    if (vehicle.end) onChange("dropDateTime", `${vehicle.end}T${timeOfDateTime(form.dropDateTime, "22:00")}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleExtensionKey]);
  const pickups = (config?.pickups || []).filter((item) => item.active !== false);
  const vehicles = (config?.vehicles || []).filter((item) => item.active !== false && (!quote.pickupId || !item.pickupId || String(item.pickupId) === String(quote.pickupId)));
  const nights = Array.from({ length: Number(config?.durationNights || 0) }, (_, index) => index + 1);
  // Refreshment rooms are configured per pickup: offer only the ones ticked for the chosen pickup.
  // Options from a server without per-pickup rooms fall back to the trip's list.
  const refreshmentPickup = pickups.find((item) => String(item.id) === String(quote.pickupId));
  const configuredRefreshmentRooms = (config?.refreshmentRooms?.length ? config.refreshmentRooms : (config?.refreshmentRoom ? [config.refreshmentRoom] : []))
    .filter((room) => room && room.active !== false)
    .filter((room) => !Array.isArray(refreshmentPickup?.refreshmentRoomIds)
      || refreshmentPickup.refreshmentRoomIds.map(String).includes(String(room.id)));
  const selectedRefreshmentRoom = configuredRefreshmentRooms.find((room) => String(room.id) === String(form.refreshmentRoomId));
  useEffect(() => {
    if (!config || !form.refreshmentNeeded) return;
    if (selectedRefreshmentRoom) return;
    onChange("refreshmentRoomId", configuredRefreshmentRooms.length === 1 ? String(configuredRefreshmentRooms[0].id) : "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, form.refreshmentNeeded, form.refreshmentRoomId, quote.pickupId]);
  const extensionStayDateLabel = (stay) => {
    if (!form.plannedDates) return "";
    const offset = stay.position === "POST"
      ? Number(config?.durationNights || 0) + Number(stay.nightIndex || 1) - 1
      : -Number(stay.nightIndex || 1);
    return formatDateOnly(addDaysToDateInput(form.plannedDates, offset));
  };
  // Receipt and policy drop the city: it repeats on every line and costs horizontal space that
  // the hotel and room name need. The on-screen pickers below still show it. Customer documents
  // only ever carry the hotel's display name (code), never its real name.
  const roomRateDocumentLabel = (rate) => {
    if (!rate) return "Room details unavailable";
    const hotel = (config?.availableHotels || []).find((item) => String(item.id) === String(rate.hotelId));
    const hotelName = rate.hotelCode || hotel?.hotelCode || "Hotel";
    const roomLabel = rate.roomCategoryName || rate.roomLabel || "Room";
    return hotelName === roomLabel ? hotelName : `${hotelName} - ${roomLabel}`;
  };
  const roomRateLabel = (rate) => {
    if (!rate) return "Room details unavailable";
    const hotel = (config?.availableHotels || []).find((item) => String(item.id) === String(rate.hotelId));
    const hotelName = rate.hotelName || rate.hotelCode || hotel?.hotelName || hotel?.hotelCode || "Hotel";
    const city = rate.hotelCity || hotel?.city || "";
    const hotelLabel = city ? `${hotelName} (${city})` : hotelName;
    const roomLabel = rate.roomCategoryName || rate.roomLabel || "Room";
    return hotelName === roomLabel ? hotelLabel : `${hotelLabel} - ${roomLabel}`;
  };
  const selectedRates = quote.selectedRoomRateIds || [];
  const selectedCategorySelections = quote.selectedRoomCategorySelections || [];
  const roomCategoriesForRate = (rate) => {
    const hotel = (config?.availableHotels || []).find((item) => String(item.id) === String(rate?.hotelId));
    return (hotel?.roomCategories || []).filter((category) => category.active !== false);
  };
  const hotelForRate = (rate) => (config?.availableHotels || []).find((item) => String(item.id) === String(rate?.hotelId));
  const hotelFacilityItems = (hotel, rate = null, stay = null) => {
    const items = [];
    const upsert = (facility) => {
      const name = facility?.name;
      if (!name) return;
      const existing = items.find((item) => item.name === name);
      if (existing) {
        existing.available = existing.available || facility.available === true;
        existing.costBehavior = mergeHotelFacilityCostBehavior(existing, facility);
        existing.optionalWithCost = existing.optionalWithCost || hotelFacilityCostBehavior(facility) === "OPTIONAL_PAID";
        existing.extraCost = Number(existing.extraCost || 0) || Number(facility.extraCost || 0);
        existing.pricingType = existing.pricingType || facility.pricingType || "PER_ACTIVITY";
        existing.id = existing.id || facility.id;
        return;
      }
      items.push({ id: facility.id, name, available: facility.available === true, optionalWithCost: hotelFacilityCostBehavior(facility) === "OPTIONAL_PAID", costBehavior: hotelFacilityCostBehavior(facility), pricingType: facility.pricingType || "PER_ACTIVITY", extraCost: Number(facility.extraCost || 0) });
    };
    [hotel?.facilities, rate?.facilities, stay?.facilities].forEach((facilities) => {
      (facilities || []).filter((facility) => facility?.name && facility?.active !== false).forEach(upsert);
    });
    const addFlag = (label, value) => upsert({ name: label, available: value === true });
    addFlag("Breakfast", stay?.breakfast ?? rate?.breakfast ?? hotel?.breakfast);
    addFlag("Dinner", stay?.dinner ?? rate?.dinner ?? hotel?.dinner);
    addFlag("Campfire", stay?.campfire ?? rate?.campfire ?? hotel?.campfire);
    addFlag("Swimming Pool", stay?.swimmingPool ?? rate?.swimmingPool ?? hotel?.swimmingPool);
    return items;
  };
  const maxPeoplePerRoomForCategory = (category) => {
    const configuredMax = Number(category?.maxMemberCount || 0);
    if (configuredMax > 0) return configuredMax;
    const maxMemberCount = (category?.priceOptions || [])
      .filter((option) => Number(option?.memberCount || 0) > 0)
      .reduce((max, option) => Math.max(max, Number(option.memberCount || 0)), 0);
    return Math.max(1, maxMemberCount);
  };
  const extensionHotels = (config?.availableHotels || []).filter((hotel) => hotel?.active !== false && (hotel.roomCategories || []).some((category) => category?.active !== false));
  const extensionCities = Array.from(new Set(extensionHotels.map((hotel) => hotel.city).filter(Boolean))).sort();
  const extensionHotelsForCity = (city) => extensionHotels.filter((hotel) => !city || String(hotel.city || "") === String(city));
  const extensionCategoriesForHotel = (hotelId) => (extensionHotels.find((hotel) => String(hotel.id) === String(hotelId))?.roomCategories || []).filter((category) => category?.active !== false);
  const extensionStayDateValue = (stay) => {
    if (!form.plannedDates) return "";
    const offset = stay?.position === "POST"
      ? Number(config?.durationNights || 0) + Number(stay?.nightIndex || 1) - 1
      : -Number(stay?.nightIndex || 1);
    return addDaysToDateInput(form.plannedDates, offset);
  };
  // Extension nights sit outside the trip's own nights, so they need their own lookup, keyed by
  // room category and stay date rather than by room rate.
  useEffect(() => {
    const preNights = Number(quote.extraPreNights || 0);
    const postNights = Number(quote.extraPostNights || 0);
    const categoryIds = extensionHotels.flatMap((hotel) =>
      (hotel.roomCategories || []).filter((category) => category?.active !== false).map((category) => category.id));
    if (!form.plannedDates || !categoryIds.length || (preNights <= 0 && postNights <= 0)) {
      setExtensionAvailabilityByKey({});
      return undefined;
    }
    const startDate = addDaysToDateInput(form.plannedDates, -preNights);
    const endDate = addDaysToDateInput(form.plannedDates, Number(config?.durationNights || 0) + postNights - 1);
    let cancelled = false;
    fetchExtensionRoomAvailability(categoryIds, startDate, endDate)
      .then((items) => {
        if (cancelled) return;
        setExtensionAvailabilityByKey(
          Object.fromEntries(items.map((item) => [`${item.roomCategoryId}:${item.stayDate}`, item]))
        );
      })
      .catch(() => {
        if (!cancelled) setExtensionAvailabilityByKey({});
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.plannedDates, quote.extraPreNights, quote.extraPostNights, config?.durationNights, config?.availableHotels]);
  const extensionMaxUnits = (category, stay) => {
    const configuredMaximum = Math.max(1, Number(category?.maximumUnits || 1));
    const availability = extensionAvailabilityByKey[`${category?.id}:${extensionStayDateValue(stay)}`];
    if (!availability) return configuredMaximum;
    return Math.max(0, Math.min(configuredMaximum, Number(availability.availableUnits ?? configuredMaximum)));
  };
  const suggestedExtensionUnits = (category) => {
    if (!category) return 0;
    const people = Math.max(1, Number(form.numberOfPersons || 0) + Number(form.numberOfKids || 0));
    const maxPeople = maxPeoplePerRoomForCategory(category);
    const maxUnits = Math.max(1, Number(category.maximumUnits || 1));
    return Math.min(maxUnits, Math.max(1, Math.ceil(people / maxPeople)));
  };
  const defaultExtensionStay = (position, previous = {}, nightIndex = 1) => {
    const city = previous.city || extensionCities[0] || extensionHotels[0]?.city || "";
    const hotel = extensionHotelsForCity(city).find((item) => String(item.id) === String(previous.hotelId)) || extensionHotelsForCity(city)[0] || extensionHotels[0] || null;
    const categories = extensionCategoriesForHotel(hotel?.id);
    const category = categories.find((item) => String(item.id) === String(previous.roomCategoryId)) || categories[0] || null;
    const previousSelections = Array.isArray(previous.roomCategorySelections) ? previous.roomCategorySelections : [];
    const roomCategorySelections = previousSelections.length
      ? previousSelections
      : category ? [{ roomCategoryId: category.id, units: previous.units || suggestedExtensionUnits(category) }] : [];
    const primarySelection = roomCategorySelections.find((selection) => Number(selection.units || 0) > 0) || roomCategorySelections[0] || {};
    return {
      position,
      nightIndex: Number(previous.nightIndex || nightIndex || 1),
      stayKey: previous.stayKey || `ext-${position}-${nightIndex}-${Math.random().toString(36).slice(2, 9)}`,
      scope: previous.scope || "BOTH",
      city: hotel?.city || city,
      hotelId: hotel?.id || "",
      roomCategoryId: primarySelection.roomCategoryId || category?.id || "",
      units: primarySelection.units || previous.units || suggestedExtensionUnits(category),
      roomCategorySelections,
      note: previous.note || "",
    };
  };
  const setTripExtensionRequired = (required) => {
    if (!required) {
      updateQuote({ tripExtensionRequired: false, extraPreNights: 0, extraPostNights: 0, extensionStays: [], tripExtensionNote: "" });
      return;
    }
    updateQuote({ tripExtensionRequired: true, extensionScope: "BOTH", extraPreNights: quote.extraPreNights || 1, extraPostNights: quote.extraPostNights || 0, extensionStays: [defaultExtensionStay("PRE", quote.extensionStays?.[0], 1)], tripExtensionNote: quote.tripExtensionNote || "" });
  };
  const setExtensionNightCount = (field, value) => {
    const nextQuote = { ...quote, [field]: Math.max(0, Number(value || 0)), tripExtensionRequired: true };
    const nights = [
      ...Array.from({ length: Number(nextQuote.extraPreNights || 0) }, (_, index) => ({ position: "PRE", nightIndex: index + 1 })),
      ...Array.from({ length: Number(nextQuote.extraPostNights || 0) }, (_, index) => ({ position: "POST", nightIndex: index + 1 })),
    ];
    const grouped = (quote.extensionStays || []).reduce((acc, stay) => {
      const key = `${stay.position || "PRE"}:${Number(stay.nightIndex || 1)}`;
      acc[key] = [...(acc[key] || []), stay];
      return acc;
    }, {});
    updateQuote({
      [field]: nextQuote[field],
      extensionStays: nights.flatMap(({ position, nightIndex }) => {
        const existing = grouped[`${position}:${nightIndex}`] || [];
        return existing.length
          ? existing.map((stay) => defaultExtensionStay(position, stay, nightIndex))
          : [defaultExtensionStay(position, {}, nightIndex)];
      }),
    });
  };
  const updateExtensionStay = (index, changes) => updateQuote({ extensionStays: (quote.extensionStays || []).map((stay, stayIndex) => stayIndex === index ? { ...stay, ...changes } : stay) });
  const addExtensionHotel = (stay, index) => {
    const stays = quote.extensionStays || [];
    const lastIndex = stays.reduce((found, item, itemIndex) => String(item.position || "PRE") === String(stay.position || "PRE") && Number(item.nightIndex || 1) === Number(stay.nightIndex || 1) ? itemIndex : found, index);
    const next = [...stays];
    next.splice(lastIndex + 1, 0, defaultExtensionStay(stay.position || "PRE", {}, Number(stay.nightIndex || 1)));
    updateQuote({ extensionStays: next });
  };
  const removeExtensionHotel = (index) => updateQuote({ extensionStays: (quote.extensionStays || []).filter((_, stayIndex) => stayIndex !== index) });
  const selectionsForRate = (rateId) => selectedCategorySelections.filter((item) => String(item.roomRateId) === String(rateId) && Number(item.units) > 0);
  const rateById = (rateId) => (config?.roomRates || []).find((item) => String(item.id) === String(rateId));
  const activeRoomRates = (config?.roomRates || []).filter((item) => item.active !== false);
  const roomRateCity = (rate) => rate?.hotelCity || hotelForRate(rate)?.city || "Location not set";
  const roomCityOptions = Array.from(new Set(activeRoomRates.map(roomRateCity).filter(Boolean))).sort();
  const selectedRoomCityByNight = quote.selectedRoomCityByNight || {};
  const selectedRoomRateNightSelections = quote.selectedRoomRateNightSelections || [];
  const defaultRoomCityForNight = (night) =>
    selectedRoomCityByNight[String(night)] ||
    roomRateCity(activeRoomRates.find((item) => Number(item.nightNumber || 1) === Number(night))) ||
    roomCityOptions[0] ||
    "";
  const effectiveNightForRateId = (rateId) => {
    const mapped = selectedRoomRateNightSelections.find((item) => String(item.roomRateId) === String(rateId));
    return Number(mapped?.nightNumber || rateById(rateId)?.nightNumber || 1);
  };
  const roomRatesForNight = (night) => {
    const city = defaultRoomCityForNight(night);
    const cityRates = activeRoomRates.filter((item) => String(roomRateCity(item)) === String(city));
    return cityRates.length ? cityRates : activeRoomRates.filter((item) => Number(item.nightNumber || 1) === Number(night));
  };
  const hotelOptionsForNight = (night) => {
    const preferredNight = Number(night || 1);
    const optionsByLabel = new Map();
    roomRatesForNight(night).forEach((item) => {
      if (!item?.id) return;
      const label = roomRateLabel(item);
      const option = {
        value: String(item.id),
        label,
        nightNumber: Number(item.nightNumber || 1),
      };
      const existing = optionsByLabel.get(label);
      if (
        !existing ||
        (Number(existing.nightNumber || 1) !== preferredNight &&
          option.nightNumber === preferredNight)
      ) {
        optionsByLabel.set(label, option);
      }
    });
    return Array.from(optionsByLabel.values());
  };
  const preferredRoomRateIdForNight = (roomRateId, night) => {
    const rate = rateById(roomRateId);
    if (!rate?.id) return String(roomRateId || "");
    const label = roomRateLabel(rate);
    const preferred = hotelOptionsForNight(night).find((item) => item.label === label);
    return preferred?.value || String(roomRateId);
  };
  const uniqueRoomRateIdsByLabel = (rateIds) => {
    const seenLabels = new Set();
    return (rateIds || [])
      .filter(Boolean)
      .map(String)
      .filter((rateId) => {
        const rate = rateById(rateId);
        if (!rate?.id) return false;
        const label = roomRateLabel(rate);
        if (seenLabels.has(label)) return false;
        seenLabels.add(label);
        return true;
      });
  };
  const selectedRateIdsForNight = (night) => {
    const mappedIds = selectedRoomRateNightSelections
      .filter((selection) => Number(selection?.nightNumber || 0) === Number(night))
      .map((selection) => preferredRoomRateIdForNight(selection.roomRateId, night));
    if (mappedIds.length) return uniqueRoomRateIdsByLabel(mappedIds);
    return uniqueRoomRateIdsByLabel(
      selectedRates.filter((rateId) => effectiveNightForRateId(rateId) === Number(night))
        .map((rateId) => preferredRoomRateIdForNight(rateId, night))
    );
  };
  const setSelectedRateIdsForNight = (night, rateIds) => {
    const cleanedIds = uniqueRoomRateIdsByLabel(rateIds);
    const previousIds = selectedRateIdsForNight(night);
    const removedIds = previousIds.filter((rateId) => !cleanedIds.includes(String(rateId)));
    const nextNightSelections = [
      ...selectedRoomRateNightSelections.filter((item) =>
        Number(item.nightNumber || 1) !== Number(night)
      ),
      ...cleanedIds.map((rateId) => ({ roomRateId: Number(rateId), nightNumber: Number(night) })),
    ];
    const nextRateIds = nextNightSelections.map((selection) => Number(selection.roomRateId));
    const remainingRateIds = new Set(nextRateIds.map((rateId) => String(rateId)));
    const nextSelections = selectedCategorySelections.filter((item) =>
      !removedIds.includes(String(item.roomRateId)) || remainingRateIds.has(String(item.roomRateId))
    );
    updateQuote({
      selectedRoomRateIds: nextRateIds,
      selectedRoomRateNightSelections: nextNightSelections,
      selectedRoomCategoryIds: nextSelections.map((item) => item.roomCategoryId),
      selectedRoomUnits: nextSelections.map((item) => item.units),
      selectedRoomCategorySelections: nextSelections,
      selectedHotelFacilities: (quote.selectedHotelFacilities || []).filter((item) => item.extensionStayKey || nextRateIds.some((rateId) => String(rateId) === String(item.roomRateId))),
    });
  };
  const updateRoomCityForNight = (night, city) => {
    const removedIds = selectedRateIdsForNight(night);
    const nextNightSelections = selectedRoomRateNightSelections.filter((item) =>
      Number(item.nightNumber || 1) !== Number(night)
    );
    const nextRateIds = nextNightSelections.map((selection) => Number(selection.roomRateId));
    const remainingRateIds = new Set(nextRateIds.map((rateId) => String(rateId)));
    const nextSelections = selectedCategorySelections.filter((item) =>
      !removedIds.includes(String(item.roomRateId)) || remainingRateIds.has(String(item.roomRateId))
    );
    updateQuote({
      selectedRoomCityByNight: { ...selectedRoomCityByNight, [String(night)]: city },
      selectedRoomRateIds: nextRateIds,
      selectedRoomRateNightSelections: nextNightSelections,
      selectedRoomCategoryIds: nextSelections.map((item) => item.roomCategoryId),
      selectedRoomUnits: nextSelections.map((item) => item.units),
      selectedRoomCategorySelections: nextSelections,
      selectedHotelFacilities: (quote.selectedHotelFacilities || []).filter((item) =>
        item.extensionStayKey || nextRateIds.some((rateId) => String(rateId) === String(item.roomRateId))
      ),
    });
  };
  // Package nights the customer arranges their own stay for - no hotel is picked, priced or held.
  const noStayNights = (quote.noStayNights || []).map(Number);
  const isNoStayNight = (night) => noStayNights.includes(Number(night));
  const stayNights = nights.filter((night) => !isNoStayNight(night));
  const setNightNoStay = (night, skip) => {
    if (!skip) {
      updateQuote({ noStayNights: noStayNights.filter((item) => item !== Number(night)) });
      return;
    }
    // Drop the night's hotel too, so the saved quote and the booking made from it carry no rooms
    // for a night nobody is staying in.
    const removedIds = selectedRateIdsForNight(night);
    const nextNightSelections = selectedRoomRateNightSelections.filter((item) =>
      Number(item.nightNumber || 1) !== Number(night)
    );
    const nextRateIds = nextNightSelections.map((selection) => Number(selection.roomRateId));
    const remainingRateIds = new Set(nextRateIds.map((rateId) => String(rateId)));
    const nextSelections = selectedCategorySelections.filter((item) =>
      !removedIds.includes(String(item.roomRateId)) || remainingRateIds.has(String(item.roomRateId))
    );
    updateQuote({
      noStayNights: Array.from(new Set([...noStayNights, Number(night)])).sort((a, b) => a - b),
      selectedRoomRateIds: nextRateIds,
      selectedRoomRateNightSelections: nextNightSelections,
      selectedRoomCategoryIds: nextSelections.map((item) => item.roomCategoryId),
      selectedRoomUnits: nextSelections.map((item) => item.units),
      selectedRoomCategorySelections: nextSelections,
      selectedHotelFacilities: (quote.selectedHotelFacilities || []).filter((item) =>
        item.extensionStayKey || nextRateIds.some((rateId) => String(rateId) === String(item.roomRateId))
      ),
    });
  };
  const canQuote = tripId && quote.pickupId && quote.vehicleId && stayNights.length > 0 && stayNights.every((night) => {
    const rateIds = selectedRateIdsForNight(night);
    return rateIds.length > 0 && rateIds.every((rateId) => selectionsForRate(rateId).length > 0);
  });
  const capacityIssue = () => {
    const personCount = Math.max(1, Number(form.numberOfPersons || 0) + Number(form.numberOfKids || 0));
    const night = stayNights.find((nightNumber) => {
      const selectedCapacity = selectedRateIdsForNight(nightNumber)
        .map((rateId) => rateById(rateId))
        .filter(Boolean)
        .reduce((total, rate) => {
          const rateCapacity = selectionsForRate(rate.id).reduce((rateTotal, selection) => {
            const category = roomCategoriesForRate(rate).find((item) => String(item.id) === String(selection.roomCategoryId));
            return rateTotal + Number(selection.units || 0) * maxPeoplePerRoomForCategory(category);
          }, 0);
          return total + rateCapacity;
        }, 0);
      return selectedCapacity > 0 && selectedCapacity < personCount;
    });
    if (!night) return "";
    const firstRate = selectedRateIdsForNight(night).map((rateId) => rateById(rateId)).find(Boolean);
    const hotel = config?.availableHotels?.find((item) => String(item.id) === String(firstRate?.hotelId));
    const location = firstRate?.hotelCity || hotel?.city || firstRate?.hotelName || "the selected location";
    return `Please add more rooms or choose a larger room in ${location} for ${personCount} person${personCount === 1 ? "" : "s"}.`;
  };
  // Vehicles added on request (Add Vehicle), each starting as a copy of the first one.
  const additionalVehicles = quote.additionalVehicles || [];
  const severalVehicles = additionalVehicles.length > 0;
  const quoteVehicles = [
    vehicles.find((item) => String(item.id) === String(quote.vehicleId)),
    ...vehiclesForIds(additionalVehicles.map((item) => item.vehicleId), vehicles),
  ].filter(Boolean);
  const addVehicle = () => updateQuote({ additionalVehicles: [...additionalVehicles, { vehicleId: Number(quote.vehicleId || vehicles[0]?.id) || null, extensionVehicleAmount: 0 }] });
  const updateAdditionalVehicle = (index, changes) => updateQuote({ additionalVehicles: additionalVehicles.map((item, i) => (i === index ? { ...item, ...changes } : item)) });
  const removeAdditionalVehicle = (index) => updateQuote({ additionalVehicles: additionalVehicles.filter((_, i) => i !== index) });
  const vehicleRowLabel = (vehicleId, number) => {
    const vehicle = vehicles.find((item) => String(item.id) === String(vehicleId));
    return `Vehicle ${number}${vehicle ? ` - ${vehicle.vehicleName || vehicle.vehicleType}` : ""}`;
  };
  // The quote only warns about an undersized vehicle, so without this a group of 8 could be quoted
  // and receipted in a 7-seat Innova, and the price then jumped when the booking was edited. With
  // vehicles added the seats of all of them count together.
  const vehicleSeatIssue = () => {
    const vehicle = combinedVehicle(quoteVehicles);
    if (!vehicle || vehicleFitsGroup(vehicle, quote)) return "";
    const name = severalVehicles ? "The selected vehicles" : (vehicle.vehicleName || vehicle.vehicleType || "This vehicle");
    const kidsNote = (quote.kidAges || []).length ? ` (kids over ${Number(vehicle.childConcessionAgeLimit ?? 0)} need their own seat)` : "";
    return `${name} seat${severalVehicles ? "" : "s"} ${Number(vehicle.capacityMax || 0)}, but this group needs ${vehicleSeatsNeeded(vehicle, quote)} seats${kidsNote}. Please choose a bigger vehicle${severalVehicles ? " or add one" : ""}.`;
  };
  const capacityError = canQuote ? (capacityIssue() || vehicleSeatIssue()) : "";
  useEffect(() => {
    let cancelled = false;
    if (!canQuote) return undefined;
    if (capacityError) {
      onChange("dynamicPackageTotal", "");
      setQuoteError(capacityError);
      return undefined;
    }
    setLoading(true); setQuoteError("");
    calculateTeamTripQuote(team, tripId, quote).then((result) => {
      if (!cancelled) {
        setCalculatedQuote(result);
        onChange("dynamicPackageTotal", String(result.roundedTotalPrice || result.finalTripPrice || ""));
      }
    }).catch((error) => { if (!cancelled) { setCalculatedQuote(null); onChange("dynamicPackageTotal", ""); setQuoteError(error?.response?.data?.message || "Could not calculate dynamic price"); } }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, tripId, JSON.stringify(quote), canQuote, capacityError]);
  const addRoom = (night) => {
    const selectedIds = selectedRateIdsForNight(night);
    const nextOption = hotelOptionsForNight(night).find((item) => !selectedIds.includes(String(item.value)));
    if (nextOption?.value) setSelectedRateIdsForNight(night, [...selectedIds, String(nextOption.value)]);
  };
  const updateRoom = (night, previousRateId, nextRateId) => {
    const selectedIds = selectedRateIdsForNight(night);
    setSelectedRateIdsForNight(night, selectedIds.map((rateId) => String(rateId) === String(previousRateId) ? String(nextRateId) : rateId));
  };
  const removeRoom = (night, rateId) => {
    setSelectedRateIdsForNight(night, selectedRateIdsForNight(night).filter((id) => String(id) !== String(rateId)));
  };
  // The configured capacity, reduced to what is still free on these dates once availability has
  // loaded. Before it loads, or if the lookup failed, the configured capacity stands - the server
  // still refuses an overbooking on save.
  const maxUnitsForCategory = (rateId, category) => {
    const configuredMaximum = Math.max(1, Number(category?.maximumUnits || 1));
    const availability = roomAvailabilityByKey[`${rateId}:${category?.id}`];
    if (!availability) return configuredMaximum;
    return Math.max(0, Math.min(configuredMaximum, Number(availability.availableUnits ?? configuredMaximum)));
  };
  const selectRoomCategory = (rateId, categoryId, unitValue) => {
    if (!rateId || !categoryId) return;
    const category = roomCategoriesForRate(rateById(rateId)).find((item) => String(item.id) === String(categoryId));
    const nextUnits = Math.max(0, Math.min(maxUnitsForCategory(rateId, category), Number(unitValue || 0)));
    const selections = selectedCategorySelections.filter((item) => !(String(item.roomRateId) === String(rateId) && String(item.roomCategoryId) === String(categoryId)));
    if (nextUnits > 0) selections.push({ roomRateId: Number(rateId), roomCategoryId: Number(categoryId), units: nextUnits });
    updateQuote({
      selectedRoomCategoryIds: selections.map((item) => item.roomCategoryId),
      selectedRoomUnits: selections.map((item) => item.units),
      selectedRoomCategorySelections: selections,
    });
  };
  const hotelFacilitySelected = (rateId, facilityId) => (quote.selectedHotelFacilities || []).some((item) => String(item.roomRateId) === String(rateId) && String(item.facilityId) === String(facilityId) && item.selected !== false);
  const toggleHotelFacility = (rateId, facilityId, selected) => {
    const current = (quote.selectedHotelFacilities || []).filter((item) => !(String(item.roomRateId) === String(rateId) && String(item.facilityId) === String(facilityId)));
    updateQuote({ selectedHotelFacilities: selected ? [...current, { roomRateId: Number(rateId), facilityId: Number(facilityId), selected: true }] : current });
  };
  const extensionFacilitySelected = (stayKey, facilityId) => (quote.selectedHotelFacilities || []).some((item) => String(item.extensionStayKey) === String(stayKey) && String(item.facilityId) === String(facilityId) && item.selected !== false);
  const toggleExtensionFacility = (stayKey, facilityId, selected) => {
    const current = (quote.selectedHotelFacilities || []).filter((item) => !(String(item.extensionStayKey) === String(stayKey) && String(item.facilityId) === String(facilityId)));
    updateQuote({ selectedHotelFacilities: selected ? [...current, { extensionStayKey: stayKey, facilityId: Number(facilityId), selected: true }] : current });
  };
  const toggleAddon = (id) => {
    const selected = quote.selectedAddonIds || [];
    const removing = selected.includes(id);
    updateQuote({
      selectedAddonIds: removing ? selected.filter((item) => item !== id) : [...selected, id],
      ...(removing ? { selectedAddonOptions: (quote.selectedAddonOptions || []).filter((item) => item.addonId !== id) } : {}),
    });
  };
  // The option of an add-on, and how many take it. Nothing is kept for the first option and everyone,
  // the default, so such a quote reads like one made before add-ons had options.
  const chooseAddonOption = (id, change) => {
    const rest = (quote.selectedAddonOptions || []).filter((item) => item.addonId !== id);
    const next = { ...((quote.selectedAddonOptions || []).find((item) => item.addonId === id) || { addonId: id }), ...change };
    updateQuote({ selectedAddonOptions: next.optionId || next.people ? [...rest, next] : rest });
  };
  const quoteTravellers = Number(quote.adults || 0) + Number(quote.kids || 0);
  const customizationPrice = form.customizationRequired === "Yes" ? Number(form.customizationPrice || 0) : 0;
  const customizationAdjustment = form.customizationPriceAdjustment === "REDUCE" ? -customizationPrice : customizationPrice;
  const finalTotal = Number(form.dynamicPackageTotal || 0) + customizationAdjustment;
  const cashbackAmountPerPerson = Math.max(0, Number(form.cashbackAmountPerPerson || 0));
  const totalCashbackAmount = cashbackAmountPerPerson * Math.max(0, Number(form.numberOfPersons || 0));
  const selectedTrip = options.find((option) => String(option.tripId) === String(tripId));
  const selectedPickup = pickups.find((item) => String(item.id) === String(quote.pickupId));
  const selectedVehicle = vehicles.find((item) => String(item.id) === String(quote.vehicleId));
  const fixedPriceExtensionVehicleWarning = Boolean(
    quote.tripExtensionRequired &&
    (quote.extensionStays || []).some((stay) => (stay.scope || "BOTH") !== "STAY") &&
    (selectedPickup?.fixedPrice === true ||
      String(selectedPickup?.pricingType || "").toUpperCase() === "FIXED_PRICE"),
  );
  // The pickup config lives in this component's own state, so the modal cannot work this out for
  // itself. Mirror it onto the form so the Save Lead gate can see it.
  useEffect(() => {
    if (Boolean(form.extensionVehicleRequired) !== fixedPriceExtensionVehicleWarning) {
      onChange("extensionVehicleRequired", fixedPriceExtensionVehicleWarning);
    }
  }, [fixedPriceExtensionVehicleWarning, form.extensionVehicleRequired, onChange]);
  // One room rate row can serve several nights, so selectedRoomRateIds carries the same id once
  // per night. Pair each id with the night it was picked for - reading nightNumber off the rate
  // row instead prints the rate's own configured night on every line, which is why a two night
  // stay came out as "Night 1" four times on the receipt.
  const receiptRateNightPairs = [
    ...(selectedRoomRateNightSelections.length
      ? selectedRoomRateNightSelections
          .map((selection) => ({ rateId: selection.roomRateId, night: Number(selection.nightNumber || 0) }))
      : selectedRates.map((id) => ({ rateId: id, night: effectiveNightForRateId(id) })))
      // A hotel left picked on a night later switched to "No stay" is not part of the booking.
      .filter(({ night }) => !isNoStayNight(night)),
    ...noStayNights.filter((night) => nights.includes(night)).map((night) => ({ noStay: true, night })),
  ].sort((left, right) => left.night - right.night);
  // Customer documents count days from the first night away, so an extra pre-night is Day 1 and
  // the package's own Day 1 moves down. Each line carries its check-in date rather than internal
  // wording like "Extra pre-night 1". Pre-nights are night -1, -2 ...; post-nights follow the package.
  const receiptPreNights = quote.tripExtensionRequired ? Math.max(0, Number(quote.extraPreNights || 0)) : 0;
  const receiptPostNights = quote.tripExtensionRequired ? Math.max(0, Number(quote.extraPostNights || 0)) : 0;
  const receiptBaseNights = Number(config?.durationNights || 0);
  const receiptDayLabel = (night, noStay = false) => {
    const offset = night < 0 ? night : night - 1;
    const day = `Day ${offset + receiptPreNights + 1}`;
    if (!form.plannedDates) return day;
    return `${day} (${noStay ? "" : "Check-in "}${formatDateOnly(addDaysToDateInput(form.plannedDates, offset))})`;
  };
  const noStayRoomLine = (night) => ({ night, text: `${receiptDayLabel(night, true)}: No stay (own arrangement)` });
  const selectedRoomLines = receiptRateNightPairs.flatMap(({ rateId, night, noStay }) => {
    if (noStay) return [noStayRoomLine(night)];
    const rate = (config?.roomRates || []).find((item) => String(item.id) === String(rateId));
    return selectionsForRate(rateId).map((selection) => {
      const category = roomCategoriesForRate(rate).find((item) => String(item.id) === String(selection.roomCategoryId));
      const selectedFacilities = hotelFacilityItems(hotelForRate(rate), rate).filter((facility) => facility.id != null && hotelFacilitySelected(rate?.id, facility.id)).map((facility) => `${facility.name} (+INR ${Number(facility.extraCost || 0).toLocaleString("en-IN")}${facility.pricingType === "PER_PERSON" ? " / person" : ""})`);
      return rate && category ? { night, text: `${night > 0 ? `${receiptDayLabel(night)}: ` : ""}${roomRateDocumentLabel(rate)} - ${category.categoryName} (${selection.units} unit${Number(selection.units) === 1 ? "" : "s"})${selectedFacilities.length ? ` | Optional: ${selectedFacilities.join(", ")}` : ""}` } : null;
    });
  }).filter(Boolean);
  const extensionRoomLines = quote.tripExtensionRequired ? (quote.extensionStays || []).flatMap((stay) => {
    // A vehicle-only extra night has no hotel in the price, even if one was picked earlier.
    if ((stay.scope || quote.extensionScope || "BOTH") === "VEHICLE") return [];
    const hotel = extensionHotels.find((item) => String(item.id) === String(stay.hotelId));
    const nightIndex = Number(stay.nightIndex || 1);
    const night = stay.position === "POST" ? receiptBaseNights + nightIndex : -nightIndex;
    const hotelName = hotel?.hotelCode || "Hotel";
    const city = stay.city || hotel?.city || "";
    return (stay.roomCategorySelections || [{ roomCategoryId: stay.roomCategoryId, units: stay.units }]).map((selection) => {
      const category = extensionCategoriesForHotel(stay.hotelId).find((item) => String(item.id) === String(selection.roomCategoryId));
      const room = category?.categoryName || `Room ${selection.roomCategoryId}`;
      return hotel && category && Number(selection.units || 0) > 0 ? { night, text: `${receiptDayLabel(night)}: ${city ? `${city} - ` : ""}${hotelName} - ${room} (${Number(selection.units || 0)} unit${Number(selection.units || 0) === 1 ? "" : "s"})` } : null;
    });
  }).filter(Boolean) : [];
  // An extra night with no hotel on it is vehicle only: the customer stays on their own.
  const extraNightsWithoutStay = [
    ...Array.from({ length: receiptPreNights }, (_, index) => -(index + 1)),
    ...Array.from({ length: receiptPostNights }, (_, index) => receiptBaseNights + index + 1),
  ].filter((night) => !extensionRoomLines.some((line) => line.night === night));
  const receiptRoomLabels = [...selectedRoomLines, ...extensionRoomLines, ...extraNightsWithoutStay.map(noStayRoomLine)]
    .sort((left, right) => left.night - right.night)
    .map((line) => line.text);
  // With the quote in, its names carry the option chosen - "Bus (AC)".
  const selectedAddonLabels = calculatedQuote?.selectedAddons?.length
    ? calculatedQuote.selectedAddons.map((item) => item.displayName || item.name).filter(Boolean)
    : (config?.addons || []).filter((item) => item.active !== false && (quote.selectedAddonIds || []).includes(item.id)).map((item) => item.name).filter(Boolean);
  // A booking confirmed here is new, so its receipt carries the two-stage payment schedule.
  const receiptPaymentSchedule = paymentSchedule(finalTotal, form.advancePayment, form.advancePayment);
  // Customer documents cover the whole trip, stay-only extra nights included.
  const tripDates = tripStayDates(form.plannedDates, quote, config?.durationNights);
  // The policy lists each night's meals and inclusions the way the Trip Details page will.
  const policyStayInclusions = stayInclusionLines(
    [...(calculatedQuote?.roomStayCosts || []), ...(calculatedQuote?.extensionStayCosts || [])],
    (hotelId) => (config?.availableHotels || []).find((hotel) => String(hotel.id) === String(hotelId)),
    form.plannedDates,
    tripDates.start
  );
  const bookingDurationLabel = `${calculatedQuote?.finalDurationNights ?? config?.durationNights ?? 0} Nights ${calculatedQuote?.finalDurationDays ?? config?.durationDays ?? 0} Days`;
  // The receipt only says the trip is extended: its duration row has the final length and the
  // extra nights show under Hotel / Room Details with their dates.
  const extensionDescription = quote.tripExtensionRequired ? "Trip Extension: Yes" : "";
  const currentFingerprint = paymentReceiptFingerprint(form);
  const receiptDownloaded = form.receiptDownloadedFingerprint === currentFingerprint;
  const policyDownloaded = form.policyDownloadedFingerprint === currentFingerprint;
  const canDownloadReceipt = Boolean(finalTotal > 0 && Number(form.advancePayment) > 0 && form.bookingCustomerName?.trim() && form.pickupDateTime && form.dropDateTime && new Date(form.dropDateTime) > new Date(form.pickupDateTime) && selectedTrip && selectedPickup && selectedVehicle && canQuote && !capacityError);
  const receiptBlockedReason = () => {
    if (!selectedTrip) return "Select a destination and trip.";
    if (!selectedPickup) return "Select a pickup option.";
    if (!selectedVehicle) return "Select a vehicle.";
    if (!canQuote) return "Pricing is still being calculated for this trip.";
    if (capacityError) return capacityError;
    if (!(finalTotal > 0)) return "Final total must be greater than zero.";
    if (!(Number(form.advancePayment) > 0)) return "Enter an advance payment amount.";
    if (!form.bookingCustomerName?.trim()) return "Enter the customer's name.";
    if (!form.pickupDateTime) return "Enter Vehicle Pickup Date & Time.";
    if (!form.dropDateTime) return "Enter Vehicle Drop Date & Time.";
    if (!(new Date(form.dropDateTime) > new Date(form.pickupDateTime))) return "Vehicle Drop Date & Time must be after Vehicle Pickup Date & Time.";
    return "";
  };
  const downloadReceipt = async () => {
    const receiptNumber = form.receiptNumber || createPaymentReceiptNumber(form);
    onChange("receiptNumber", receiptNumber);
    await generatePaymentReceipt({ receiptNumber, customerName: form.bookingCustomerName, customerNumber: form.customerNumber, pickup: selectedPickup?.pickupName || selectedPickup?.pickupValue, trip: selectedTrip?.tripTitle, travelDate: form.plannedDates, pickupDateTime: form.pickupDateTime, dropDateTime: form.dropDateTime, tripStartDate: tripDates.start, tripEndDate: tripDates.end, adults: Number(form.numberOfPersons || 0), kids: Number(form.numberOfKids || 0), kidsAge: form.kidsAge, duration: bookingDurationLabel, vehicle: vehicleNamesLabel(quoteVehicles), rooms: receiptRoomLabels, addons: selectedAddonLabels, packageTotal: finalTotal, advancePaid: Number(form.advancePayment), balance: finalTotal - Number(form.advancePayment), paymentSchedule: receiptPaymentSchedule, customization: form.customizationRequired === "Yes" ? form.customizationDescription : (extensionDescription || "No"), bookingNotes: form.bookingNotes, cashbackAmountPerPerson, totalCashbackAmount });
    onChange("receiptDownloadedFingerprint", currentFingerprint); onChange("paymentReceiptShared", false);
  };
  const downloadPolicy = () => {
    const policyNumber = form.receiptNumber || createPaymentReceiptNumber(form);
    onChange("receiptNumber", policyNumber);
    generateTravelPolicy({ policyNumber, customerName: form.bookingCustomerName, customerNumber: form.customerNumber, trip: selectedTrip?.tripTitle, pickupDateTime: form.pickupDateTime, dropDateTime: form.dropDateTime, tripStartDate: tripDates.start, tripEndDate: tripDates.end, adults: Number(form.numberOfPersons || 0), kids: Number(form.numberOfKids || 0), kidsAge: form.kidsAge, duration: bookingDurationLabel, rooms: receiptRoomLabels, stayInclusions: policyStayInclusions, bookingNotes: form.bookingNotes, cashbackAmountPerPerson, totalCashbackAmount });
    onChange("policyDownloadedFingerprint", currentFingerprint); onChange("travelPolicyShared", false);
  };
  return <>
    <Col xs={12}><hr className="my-1" /><h6 className="mb-0">Booking Details</h6></Col>
    {(bookingOptionsLoading || loading) && (
      <Col xs={12}>
        <div className="d-flex align-items-center gap-2 rounded border bg-light px-3 py-2 small text-primary">
          <Spinner size="sm" />
          {bookingOptionsLoading ? "Loading trip options..." : config ? "Calculating dynamic price..." : "Loading dynamic booking details..."}
        </div>
      </Col>
    )}
    <Col md={4}><Form.Label>Customer Name *</Form.Label><Form.Control value={form.bookingCustomerName || ""} onChange={(e) => onChange("bookingCustomerName", e.target.value)} required /></Col>
    <Col md={4}><Form.Label>Booking Category *</Form.Label><Form.Select value={categoryId} onChange={(e) => { onChange("bookingCategoryId", e.target.value); onChange("bookingTripId", ""); onChange("dynamicQuoteRequest", null); onChange("dynamicPackageTotal", ""); onChange("refreshmentNeeded", false); onChange("refreshmentRoomId", ""); }} required><option value="">Select category</option>{(categories || []).map((category) => <option key={category.id} value={category.id}>{category.title || category.name}</option>)}</Form.Select></Col>
    <Col md={4}><Form.Label>Trip *</Form.Label><Form.Select value={tripId || ""} onChange={(e) => { onChange("bookingTripId", e.target.value); onChange("refreshmentNeeded", false); onChange("refreshmentRoomId", ""); }} disabled={!categoryId || bookingOptionsLoading} required><option value="">{bookingOptionsLoading ? "Loading trips..." : "Select trip"}</option>{filteredTrips.map((option) => <option key={option.tripId} value={option.tripId}>{option.tripTitle}</option>)}</Form.Select>{categoryId && !bookingOptionsLoading && !filteredTrips.length && <div className="small text-danger mt-1">No trips available in this category.</div>}</Col>
    <Col md={4}><Form.Label>Planned Date *</Form.Label><Form.Control type="date" min={todayInputDate()} value={form.plannedDates || ""} onChange={(e) => updateBookingPlannedDate(e.target.value)} required /></Col>
    <BookingDateTimeField label="Vehicle Pickup Date & Time *" value={form.pickupDateTime} minDate={todayInputDate()} onChange={updatePickupDateTime} />
    <BookingDateTimeField label="Vehicle Drop Date & Time *" value={form.dropDateTime} minDate={form.pickupDateTime ? String(form.pickupDateTime).slice(0, 10) : todayInputDate()} minDateTime={form.pickupDateTime} onChange={(next) => onChange("dropDateTime", next)} />
    <DriverLanguagePriorityField team={team} selected={form.driverLanguages || []} onChange={(next) => onChange("driverLanguages", next)} />
    {config && (
      <Col md={4}>
        <Form.Label>Refreshment</Form.Label>
        <div className="border rounded px-3 py-2 h-100">
          <Form.Check
            type="checkbox"
            id="lead-refreshment-needed"
            label="Refreshment needed"
            checked={Boolean(form.refreshmentNeeded)}
            onChange={(e) => {
              onChange("refreshmentNeeded", e.target.checked);
              if (!e.target.checked) onChange("refreshmentRoomId", "");
              else if (configuredRefreshmentRooms.length === 1) onChange("refreshmentRoomId", String(configuredRefreshmentRooms[0].id));
            }}
          />
          {form.refreshmentNeeded && configuredRefreshmentRooms.length > 0 && (
            <Form.Select
              className="mt-2"
              size="sm"
              value={form.refreshmentRoomId || ""}
              onChange={(e) => onChange("refreshmentRoomId", e.target.value)}
              required
            >
              <option value="">Select refreshment room</option>
              {configuredRefreshmentRooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.name}{room.city ? ` - ${room.city}` : ""}
                </option>
              ))}
            </Form.Select>
          )}
          <div className={`small mt-1 ${form.refreshmentNeeded && !selectedRefreshmentRoom ? "text-danger" : "text-muted"}`}>
            {!configuredRefreshmentRooms.length
              ? `No refreshment room configured for ${refreshmentPickup?.pickupName || "this pickup"}.`
              : selectedRefreshmentRoom
                ? `${selectedRefreshmentRoom.name}${selectedRefreshmentRoom.city ? ` - ${selectedRefreshmentRoom.city}` : ""}`
                : "Select one configured refreshment room."}
          </div>
        </div>
      </Col>
    )}
    {config && <><Col md={4}><Form.Label>Pickup *</Form.Label><Form.Select value={quote.pickupId || ""} onChange={(e) => updateQuote({ pickupId: Number(e.target.value), vehicleId: null, additionalVehicles: [] })}><option value="">Select pickup</option>{pickups.map((item) => <option key={item.id} value={item.id}>{item.pickupName || item.pickupValue}</option>)}</Form.Select></Col><Col md={4}><div className="d-flex justify-content-between align-items-center gap-2"><Form.Label>Vehicle *</Form.Label><Button type="button" size="sm" variant="outline-primary" className="mb-2" disabled={!vehicles.length} onClick={addVehicle}>Add Vehicle</Button></div><Form.Select aria-label="Vehicle 1" value={quote.vehicleId || ""} onChange={(e) => { const vehicle=vehicles.find((item)=>String(item.id)===e.target.value); updateQuote({ vehicleId: Number(e.target.value), acType: vehicle?.acType || "AC" }); }}><option value="">Select vehicle</option>{vehicles.map((item) => { const fits = severalVehicles || vehicleFitsGroup(item, quote); return <option key={item.id} value={item.id} disabled={!fits && String(item.id) !== String(quote.vehicleId || "")}>{item.vehicleName || item.vehicleType}{fits ? "" : ` - too small (seats ${Number(item.capacityMax || 0)}, needs ${vehicleSeatsNeeded(item, quote)})`}</option>; })}</Form.Select>{additionalVehicles.map((item, index) => <div key={index} className="d-flex align-items-center gap-2 mt-2"><Form.Select aria-label={`Vehicle ${index + 2}`} value={item.vehicleId || ""} onChange={(e) => updateAdditionalVehicle(index, { vehicleId: Number(e.target.value) })}>{vehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.vehicleName || vehicle.vehicleType} (seats {Number(vehicle.capacityMax || 0)})</option>)}</Form.Select><Button type="button" size="sm" variant="outline-danger" onClick={() => removeAdditionalVehicle(index)}>Remove</Button></div>)}</Col><Col md={4}><Form.Label>AC Type</Form.Label><Form.Select value={quote.acType || "AC"} onChange={(e) => updateQuote({ acType: e.target.value })}><option value="NON_AC">Non-AC</option><option value="AC">AC</option></Form.Select></Col>
    <Col xs={12}>
      <div className="border rounded p-2 bg-light">
        <div className="fw-semibold small mb-2">Trip Extension</div>
        <Row className="g-2">
          <Col md={4}>
            <Form.Label>Trip Extension Required</Form.Label>
            <Form.Select value={quote.tripExtensionRequired ? "YES" : "NO"} onChange={(e) => setTripExtensionRequired(e.target.value === "YES")}>
              <option value="NO">No</option>
              <option value="YES">Yes</option>
            </Form.Select>
          </Col>
          {quote.tripExtensionRequired && <>
            {fixedPriceExtensionVehicleWarning && (
              <Col xs={12}>
                <Alert variant="warning" className="py-2 mb-0">
                  <div className="small fw-semibold mb-1">
                    For this Pickup Location vehicles are not available for Trip extension.
                  </div>
                  <div className="small mb-2">
                    This pickup charges one flat vehicle price that does not increase for the extra days,
                    so the quote adds no vehicle cost for the extension. Enter what the transport vendor
                    charges for the extra days. The customer is charged exactly this amount, and it shows
                    against the transport vendor on the booking.
                  </div>
                  <Row className="g-2 align-items-end">
                    <Col md={4}>
                      <Form.Label className="small mb-1">
                        Extension vehicle amount{severalVehicles ? ` - ${vehicleRowLabel(quote.vehicleId, 1)}` : ""}
                      </Form.Label>
                      <InputGroup size="sm">
                        <InputGroup.Text>Rs.</InputGroup.Text>
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          value={quote.extensionVehicleAmount ?? ""}
                          onChange={(e) => updateQuote({ extensionVehicleAmount: e.target.value === "" ? "" : Math.max(0, Number(e.target.value)) })}
                        />
                      </InputGroup>
                    </Col>
                    {additionalVehicles.map((item, index) => (
                      <Col md={4} key={index}>
                        <Form.Label className="small mb-1">Extension vehicle amount - {vehicleRowLabel(item.vehicleId, index + 2)}</Form.Label>
                        <InputGroup size="sm">
                          <InputGroup.Text>Rs.</InputGroup.Text>
                          <Form.Control
                            type="number"
                            min="0"
                            step="1"
                            value={item.extensionVehicleAmount ?? ""}
                            onChange={(e) => updateAdditionalVehicle(index, { extensionVehicleAmount: e.target.value === "" ? "" : Math.max(0, Number(e.target.value)) })}
                          />
                        </InputGroup>
                      </Col>
                    ))}
                    <Col md={8}>
                      <Form.Check
                        type="checkbox"
                        id="lead-extension-vehicle-ack"
                        className="small"
                        label={`I have confirmed ${severalVehicles ? "these extension vehicle amounts" : "this extension vehicle amount"} with the transport vendor`}
                        checked={Boolean(form.extensionVehicleAcknowledged)}
                        disabled={!extensionVehicleAmountsEntered(quote)}
                        onChange={(e) => onChange("extensionVehicleAcknowledged", e.target.checked)}
                      />
                      {!extensionVehicleAmountsEntered(quote) && (
                        <div className="small text-muted">Enter {severalVehicles ? "every amount" : "the amount"} to enable this.</div>
                      )}
                    </Col>
                  </Row>
                </Alert>
              </Col>
            )}
            <Col md={4}><Form.Label>Extra pre-nights</Form.Label><Form.Control type="number" min="0" step="1" value={quote.extraPreNights ?? 0} onChange={(e) => setExtensionNightCount("extraPreNights", e.target.value)} /></Col>
            <Col md={4}><Form.Label>Extra post-nights</Form.Label><Form.Control type="number" min="0" step="1" value={quote.extraPostNights ?? 0} onChange={(e) => setExtensionNightCount("extraPostNights", e.target.value)} /></Col>
            {(quote.extensionStays || []).map((stay, index) => {
              const hotels = extensionHotelsForCity(stay.city);
              const categories = extensionCategoriesForHotel(stay.hotelId);
              const extensionSelections = stay.roomCategorySelections || [];
              const stayIncludesStay = (stay.scope || "BOTH") !== "VEHICLE";
              const selectedExtensionHotel = extensionHotels.find((item) => String(item.id) === String(stay.hotelId));
              const facilityItems = stayIncludesStay && selectedExtensionHotel ? hotelFacilityItems(selectedExtensionHotel) : [];
              const includedFacilityItems = facilityItems.filter((facility) => hotelFacilityCostBehavior(facility) !== "OPTIONAL_PAID");
              const optionalFacilityItems = facilityItems.filter((facility) => facility.id != null && facility.available && hotelFacilityCostBehavior(facility) === "OPTIONAL_PAID");
              const categoryUnits = (categoryId) => Number(extensionSelections.find((selection) => String(selection.roomCategoryId) === String(categoryId))?.units || 0);
              const updateExtensionCategoryUnits = (category, units) => {
                const nextUnits = Math.max(0, Math.min(extensionMaxUnits(category, stay), Number(units || 0)));
                const nextSelections = extensionSelections.filter((selection) => String(selection.roomCategoryId) !== String(category.id));
                if (nextUnits > 0) nextSelections.push({ roomCategoryId: category.id, units: nextUnits });
                const primary = nextSelections.find((selection) => Number(selection.units || 0) > 0) || {};
                updateExtensionStay(index, { roomCategorySelections: nextSelections, roomCategoryId: primary.roomCategoryId || "", units: primary.units || 0 });
              };
              const sameNightCount = (quote.extensionStays || []).filter((item) => String(item.position || "PRE") === String(stay.position || "PRE") && Number(item.nightIndex || 1) === Number(stay.nightIndex || 1)).length;
              const stayDateLabel = extensionStayDateLabel(stay);
              return (
                <Col xs={12} key={stay.stayKey || `${stay.position}-${index}`}>
                  <div className="border rounded p-2 bg-white">
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                      <div className="small fw-semibold">
                        {stay.position === "POST" ? "Extra post-night" : "Extra pre-night"} {Number(stay.nightIndex || 1)}
                        {stayDateLabel ? ` - Stay Date: ${stayDateLabel}` : ""}
                      </div>
                      <div className="d-flex gap-2">
                        {sameNightCount > 1 && <Button type="button" size="sm" variant="outline-danger" onClick={() => removeExtensionHotel(index)}>Remove</Button>}
                        {stayIncludesStay && <Button type="button" size="sm" variant="outline-primary" onClick={() => addExtensionHotel(stay, index)}>Add Hotel</Button>}
                      </div>
                    </div>
                    <Row className="g-2">
                      <Col md={4}>
                        <Form.Label>This night needs</Form.Label>
                        <Form.Select value={stay.scope || "BOTH"} onChange={(e) => updateExtensionStay(index, { scope: e.target.value })}>
                          <option value="BOTH">Stay and Vehicle</option>
                          <option value="STAY">Stay only</option>
                          <option value="VEHICLE">Vehicle only</option>
                        </Form.Select>
                      </Col>
                      {stayIncludesStay && <>
                      <Col md={4}><Form.Label>City</Form.Label><Form.Select value={stay.city || ""} onChange={(e) => { const city = e.target.value; const hotel = extensionHotelsForCity(city)[0] || null; const category = extensionCategoriesForHotel(hotel?.id)[0] || null; updateQuote({ extensionStays: (quote.extensionStays || []).map((item, itemIndex) => itemIndex === index ? { ...item, city, hotelId: hotel?.id || "", roomCategoryId: category?.id || "", units: suggestedExtensionUnits(category), roomCategorySelections: category ? [{ roomCategoryId: category.id, units: suggestedExtensionUnits(category) }] : [] } : item), selectedHotelFacilities: (quote.selectedHotelFacilities || []).filter((item) => String(item.extensionStayKey) !== String(stay.stayKey)) }); }}><option value="">Select city</option>{extensionCities.map((city) => <option key={city} value={city}>{city}</option>)}</Form.Select></Col>
                      <Col md={4}><Form.Label>Hotel</Form.Label><Form.Select value={stay.hotelId || ""} onChange={(e) => { const hotelId = e.target.value; const category = extensionCategoriesForHotel(hotelId)[0] || null; updateQuote({ extensionStays: (quote.extensionStays || []).map((item, itemIndex) => itemIndex === index ? { ...item, hotelId, roomCategoryId: category?.id || "", units: suggestedExtensionUnits(category), roomCategorySelections: category ? [{ roomCategoryId: category.id, units: suggestedExtensionUnits(category) }] : [] } : item), selectedHotelFacilities: (quote.selectedHotelFacilities || []).filter((item) => String(item.extensionStayKey) !== String(stay.stayKey)) }); }}><option value="">Select hotel</option>{hotels.map((hotel) => <option key={hotel.id} value={hotel.id}>{hotel.hotelCode || hotel.hotelName || `Hotel ${hotel.id}`}</option>)}</Form.Select></Col>
                      <Col xs={12}>
                        <div className="small mb-2">
                          <span className="text-muted">Facilities:</span>{" "}
                          {includedFacilityItems.length ? includedFacilityItems.map((facility) => <Badge bg={facility.available ? "success" : "danger"} className="me-1" key={`${stay.stayKey}-facility-${facility.name}`}>{facility.name}: {facility.available ? "Yes" : "No"}</Badge>) : <span className="text-muted">Not listed</span>}
                        </div>
                        {optionalFacilityItems.length > 0 && (
                          <div className="border-top pt-2 mb-2">
                            <div className="small text-muted mb-1">Optional facilities</div>
                            <div className="d-flex flex-column gap-1">
                              {optionalFacilityItems.map((facility) => (
                                <Form.Check
                                  key={`${stay.stayKey}-${facility.id}`}
                                  type="switch"
                                  className="small"
                                  id={`extension-facility-${stay.stayKey}-${facility.id}`}
                                  label={`${facility.name} (+INR ${Number(facility.extraCost || 0).toLocaleString("en-IN")}${facility.pricingType === "PER_PERSON" ? " / person / night" : " / night"})`}
                                  checked={extensionFacilitySelected(stay.stayKey, facility.id)}
                                  onChange={(e) => toggleExtensionFacility(stay.stayKey, facility.id, e.target.checked)}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </Col>
                      <Col xs={12}>
                        <Form.Label>Room Categories</Form.Label>
                        <div className="d-flex flex-column gap-1">
                          {categories.map((category) => {
                            const units = categoryUnits(category.id);
                            const maxUnits = extensionMaxUnits(category, stay);
                            const availabilityKnown = Boolean(extensionAvailabilityByKey[`${category.id}:${extensionStayDateValue(stay)}`]);
                            const soldOut = availabilityKnown && maxUnits === 0;
                            return (
                              <div key={category.id} className="d-flex align-items-center justify-content-between gap-3 py-1">
                                <div>
                                  <div className={soldOut ? "text-muted" : undefined}>{category.categoryName}</div>
                                  {availabilityKnown && <div className="small text-muted">{soldOut ? "Sold out for this night" : `${maxUnits} left for this night`}</div>}
                                  {units > maxUnits && <div className="small text-danger">Only {maxUnits} left - reduce this before saving.</div>}
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                  <Button type="button" size="sm" variant="outline-secondary" disabled={units === 0} onClick={() => updateExtensionCategoryUnits(category, units - 1)}>-</Button>
                                  <span className="text-center" style={{ minWidth: "2rem" }}>{units}</span>
                                  <Button type="button" size="sm" variant="outline-secondary" disabled={units >= maxUnits} onClick={() => updateExtensionCategoryUnits(category, units + 1)}>+</Button>
                                </div>
                              </div>
                            );
                          })}
                          {!categories.length && <div className="small text-danger">No room categories available for this hotel.</div>}
                        </div>
                      </Col>
                      </>}
                    </Row>
                  </div>
                </Col>
              );
            })}
            <Col xs={12}><Form.Label>Extension note</Form.Label><Form.Control value={quote.tripExtensionNote || ""} onChange={(e) => updateQuote({ tripExtensionNote: e.target.value })} placeholder="Late pickup / direct transfer" /></Col>
            {calculatedQuote?.tripExtensionCost > 0 && <Col xs={12}><div className="small text-muted">Extension cost: INR {Number(calculatedQuote.tripExtensionCost || 0).toLocaleString("en-IN")} | Final duration: {calculatedQuote.finalDurationNights || 0}N/{calculatedQuote.finalDurationDays || 0}D</div></Col>}
          </>}
        </Row>
      </div>
    </Col>
    {nights.map((night) => {
      const nightNoStay = isNoStayNight(night);
      // At least one night keeps a stay, so the last one can't be switched off.
      const canSkipStay = nightNoStay || stayNights.length > 1;
      const selectedIds = nightNoStay ? [] : selectedRateIdsForNight(night);
      const selectedNightRates = selectedIds.map((id) => rateById(id)).filter(Boolean);
      const hotelOptions = hotelOptionsForNight(night);
      return (
        <Col xs={12} key={night}>
          <div className="border rounded p-2">
            <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
              <Form.Label className="mb-0">Night {night} Hotel{nightNoStay ? "" : " *"}</Form.Label>
              <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                {nights.length > 1 && (
                  <Form.Select
                    size="sm"
                    value={nightNoStay ? "NO_STAY" : "STAY"}
                    onChange={(e) => setNightNoStay(night, e.target.value === "NO_STAY")}
                    aria-label={`Stay needed for night ${night}`}
                    style={{ width: 150 }}
                  >
                    <option value="STAY">Stay needed</option>
                    <option value="NO_STAY" disabled={!canSkipStay}>No stay</option>
                  </Form.Select>
                )}
                {!nightNoStay && roomCityOptions.length > 1 && (
                  <Form.Select
                    size="sm"
                    value={defaultRoomCityForNight(night)}
                    onChange={(e) => updateRoomCityForNight(night, e.target.value)}
                    aria-label={`Select city for night ${night}`}
                    style={{ width: 160 }}
                  >
                    {roomCityOptions.map((city) => <option key={city} value={city}>{city}</option>)}
                  </Form.Select>
                )}
                {!nightNoStay && (
                  <Button type="button" size="sm" variant="outline-primary" disabled={selectedIds.length >= hotelOptions.length} onClick={() => addRoom(night)}>
                    Add Hotel
                  </Button>
                )}
              </div>
            </div>
            {nightNoStay && (
              <div className="small text-muted">
                No stay for this night - the customer arranges their own accommodation. The vehicle and
                the rest of the trip are still included.
              </div>
            )}
            {!nightNoStay && !selectedNightRates.length && (
              <Form.Select value="" onChange={(e) => setSelectedRateIdsForNight(night, e.target.value ? [e.target.value] : [])}>
                <option value="">Select hotel</option>
                {hotelOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </Form.Select>
            )}
            <div className="d-flex flex-column gap-2">
              {selectedNightRates.map((rate, hotelIndex) => {
                const roomCategories = roomCategoriesForRate(rate);
                const rateSelections = selectionsForRate(rate.id);
                const facilityItems = hotelFacilityItems(hotelForRate(rate), rate);
                const includedFacilityItems = facilityItems.filter((facility) => hotelFacilityCostBehavior(facility) !== "OPTIONAL_PAID");
                const optionalFacilityItems = facilityItems.filter((facility) => facility.id != null && facility.available && hotelFacilityCostBehavior(facility) === "OPTIONAL_PAID");
                return (
                  <div className="border rounded px-3 py-2" key={rate.id}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Form.Select value={rate.id || ""} onChange={(e) => updateRoom(night, rate.id, e.target.value)}>
                        {hotelOptions.map((item) => (
                          <option key={item.value} value={item.value} disabled={selectedIds.includes(String(item.value)) && String(item.value) !== String(rate.id)}>
                            Hotel {hotelIndex + 1}: {item.label}
                          </option>
                        ))}
                      </Form.Select>
                      {selectedNightRates.length > 1 && (
                        <Button type="button" size="sm" variant="outline-danger" onClick={() => removeRoom(night, rate.id)}>
                          Remove
                        </Button>
                      )}
                    </div>
                    <div className="small mb-2">
                      <span className="text-muted">Facilities:</span>{" "}
                      {includedFacilityItems.length ? includedFacilityItems.map((facility) => <Badge bg={facility.available ? "success" : "danger"} className="me-1" key={`${rate.id}-facility-${facility.name}`}>{facility.name}: {facility.available ? "Yes" : "No"}</Badge>) : <span className="text-muted">Not listed</span>}
                    </div>
                    {optionalFacilityItems.length > 0 && (
                      <div className="border-top pt-2 mb-2">
                        <div className="small text-muted mb-1">Optional facilities</div>
                        <div className="d-flex flex-column gap-1">
                          {optionalFacilityItems.map((facility) => (
                            <Form.Check
                              key={`${rate.id}-${facility.id}`}
                              type="switch"
                              className="small"
                              id={`lead-confirmed-hotel-paid-facility-${rate.id}-${facility.id}`}
                              label={`${facility.name} (+INR ${Number(facility.extraCost || 0).toLocaleString("en-IN")}${facility.pricingType === "PER_PERSON" ? " / person" : ""})`}
                              checked={hotelFacilitySelected(rate.id, facility.id)}
                              onChange={(e) => toggleHotelFacility(rate.id, facility.id, e.target.checked)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {roomCategories.map((category) => {
                      const selection = rateSelections.find((item) => String(item.roomCategoryId) === String(category.id));
                      const units = Number(selection?.units || 0);
                      const maxUnits = maxUnitsForCategory(rate.id, category);
                      const availabilityKnown = Boolean(roomAvailabilityByKey[`${rate.id}:${category.id}`]);
                      const soldOut = availabilityKnown && maxUnits === 0;
                      return (
                        <div key={category.id} className="d-flex align-items-center justify-content-between gap-3 py-1">
                          <div>
                            <div className={soldOut ? "text-muted" : undefined}>{category.categoryName}</div>
                            {availabilityKnown && <div className="small text-muted">{soldOut ? "Sold out for these dates" : `${maxUnits} left for these dates`}</div>}
                            {units > maxUnits && <div className="small text-danger">Only {maxUnits} left - reduce this before saving.</div>}
                          </div>
                          <div className="d-flex align-items-center gap-1">
                            <Button type="button" size="sm" variant="outline-secondary" disabled={units === 0} onClick={() => selectRoomCategory(rate.id, category.id, units - 1)}>-</Button>
                            <span className="text-center" style={{ minWidth: "2rem" }}>{units}</span>
                            <Button type="button" size="sm" variant="outline-secondary" disabled={units >= maxUnits} onClick={() => selectRoomCategory(rate.id, category.id, units + 1)}>+</Button>
                          </div>
                        </div>
                      );
                    })}
                    {!roomCategories.length && <div className="small text-danger">No room categories available for this hotel.</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </Col>
      );
    })}
    {(config.addons || []).filter((item) => item.active !== false).length > 0 && <Col xs={12}><Form.Label>Add-ons</Form.Label><div className="d-flex flex-wrap gap-3">{config.addons.filter((item) => item.active !== false).map((item) => {
      const selected = (quote.selectedAddonIds || []).includes(item.id);
      const options = (item.options || []).filter((option) => option.active !== false);
      const choice = (quote.selectedAddonOptions || []).find((entry) => entry.addonId === item.id) || {};
      const option = options.find((entry) => String(entry.id) === String(choice.optionId)) || options[0] || null;
      const countable = ["PER_PERSON", "PER_VEHICLE"].includes((option || item).pricingType);
      return (
        <div key={item.id} className="d-flex flex-wrap align-items-center gap-2">
          <Form.Check type="checkbox" label={item.name} checked={selected} onChange={() => toggleAddon(item.id)} />
          {selected && options.length > 0 && (
            <Form.Select size="sm" style={{ width: "auto" }} aria-label={`${item.name} option`} value={option?.id ?? ""} onChange={(e) => chooseAddonOption(item.id, { optionId: Number(e.target.value) })}>
              {options.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
            </Form.Select>
          )}
          {selected && countable && (
            <InputGroup size="sm" style={{ width: 150 }}>
              <InputGroup.Text>People</InputGroup.Text>
              <Form.Control type="number" min="1" max={quoteTravellers || undefined} placeholder={quoteTravellers ? `All ${quoteTravellers}` : "All"} aria-label={`How many take ${item.name}`} value={choice.people ?? ""}
                onChange={(e) => {
                  const typed = e.target.value === "" ? null : Math.max(1, Number(e.target.value));
                  chooseAddonOption(item.id, { people: typed && quoteTravellers && typed >= quoteTravellers ? null : typed });
                }} />
            </InputGroup>
          )}
        </div>
      );
    })}</div></Col>}</>}
    {loading && <Col xs={12}><Spinner size="sm" className="me-2" />Calculating dynamic price...</Col>}{quoteError && <Col xs={12}><div className="text-danger small">{quoteError}</div></Col>}
    {/* Priced for the team, refused on the customer's page - confirm the exception with the hotel first. */}
    {!quoteError && (calculatedQuote?.minGuestsWarnings || []).map((warning) => <Col xs={12} key={warning}><div className="text-danger small"><i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />Below the hotel's minimum guests - confirm with the hotel first: {warning}</div></Col>)}
    <Col md={4}><Form.Label>Customization Required *</Form.Label><Form.Select value={form.customizationRequired || "No"} onChange={(e) => onChange("customizationRequired", e.target.value)}><option value="No">No</option><option value="Yes">Yes</option></Form.Select></Col>
    {form.customizationRequired === "Yes" && <><Col md={4}><Form.Label>Customization Details *</Form.Label><Form.Control value={form.customizationDescription || ""} onChange={(e) => onChange("customizationDescription", e.target.value)} required /></Col><Col md={4}><Form.Label>Price Adjustment *</Form.Label><Form.Select value={form.customizationPriceAdjustment || "ADD"} onChange={(e) => onChange("customizationPriceAdjustment", e.target.value)}><option value="ADD">Add to final amount</option><option value="REDUCE">Reduce from final amount</option></Form.Select></Col><Col md={4}><Form.Label>Customization Price *</Form.Label><Form.Control type="number" min="0.01" step="0.01" value={form.customizationPrice || ""} onChange={(e) => onChange("customizationPrice", e.target.value)} required /></Col></>}
    <Col md={4}><Form.Label>Dynamic Package Total</Form.Label><Form.Control value={form.dynamicPackageTotal ? `INR ${Number(form.dynamicPackageTotal).toLocaleString()}` : ""} readOnly /></Col>
    <Col md={4}><Form.Label>Final Total</Form.Label><Form.Control value={finalTotal ? `INR ${finalTotal.toLocaleString()}` : ""} readOnly /></Col>
    <Col md={4}><Form.Label>Advance Payment *</Form.Label><Form.Control type="number" min="0.01" max={finalTotal || undefined} step="0.01" value={form.advancePayment || ""} onChange={(e) => onChange("advancePayment", e.target.value)} required /></Col>
    <Col md={4}><Form.Label>Cashback Amount Per Adult</Form.Label><Form.Control type="number" min="0" step="0.01" value={form.cashbackAmountPerPerson || ""} onChange={(e) => { onChange("cashbackAmountPerPerson", e.target.value); onChange("totalCashbackAmount", String(Math.max(0, Number(e.target.value || 0)) * Math.max(0, Number(form.numberOfPersons || 0)))); }} placeholder="Optional" /></Col>
    <Col md={4}><Form.Label>Total Cashback</Form.Label><Form.Control value={totalCashbackAmount ? `INR ${totalCashbackAmount.toLocaleString()}` : ""} readOnly /></Col>
    <ConversionCreditEditor value={form.conversionCredits} members={conversionMembers} currentMember={member} onChange={(rows) => onChange("conversionCredits", rows)} />
    <Col xs={12}><Form.Label>Notes <Badge bg="warning" text="dark" className="ms-1">Shown to customer on receipt & policy</Badge></Form.Label><Form.Control as="textarea" rows={2} value={form.bookingNotes || ""} onChange={(e) => onChange("bookingNotes", e.target.value)} placeholder="Notes shown in payment receipt, policy, and booking table" /></Col>
    <Col xs={12}><ItineraryDocumentsField team={team} value={form.itineraryDocuments || []} onChange={(documents) => onChange("itineraryDocuments", documents)} /></Col>
    <Col xs={12}><div className="small text-muted">Cashback is independent from trip amount and payable only after review video, travel photos, and Google review are shared.</div></Col>
    <Col xs={12}><div className="d-flex flex-wrap align-items-center gap-3 border-top pt-3"><Button type="button" variant="outline-primary" onClick={downloadReceipt} disabled={!canDownloadReceipt} title={canDownloadReceipt ? undefined : receiptBlockedReason()}><i className="bi bi-receipt me-2" aria-hidden="true" />{receiptDownloaded ? "Download Receipt Again" : "Generate & Download Payment Receipt"}</Button><Button type="button" variant="outline-secondary" onClick={downloadPolicy} disabled={!canDownloadReceipt} title={canDownloadReceipt ? undefined : receiptBlockedReason()}><i className="bi bi-file-earmark-text me-2" aria-hidden="true" />{policyDownloaded ? "Download Policy Again" : "Generate & Download Policy"}</Button><Form.Check type="checkbox" id="payment-receipt-shared" label="Payment Receipt Shared with Customer" checked={Boolean(form.paymentReceiptShared) && receiptDownloaded} disabled={!receiptDownloaded} onChange={(e) => onChange("paymentReceiptShared", e.target.checked)} /><Form.Check type="checkbox" id="travel-policy-shared" label="Policy Shared with Customer" checked={Boolean(form.travelPolicyShared) && policyDownloaded} disabled={!policyDownloaded} onChange={(e) => onChange("travelPolicyShared", e.target.checked)} />{receiptDownloaded && <span className="small text-muted">Receipt {form.receiptNumber}</span>}{!canDownloadReceipt && <span className="small text-danger w-100">{receiptBlockedReason()}</span>}</div></Col>
  </>;
}
function LeadField({ field, team, value, initialValue, isCallDone, kidsCount, trips, categories, pricingOptions, interestStatuses, dailyUpdateSuggestions = [], callMembers = [], followUpDisabled = false, suggestedFollowUp = "", callingNumber = "", member, lead, onCall, onChange }) {
  const [availableLanguages, setAvailableLanguages] = useState([]);
  useEffect(() => {
    let active = true;
    loadEditorLanguages(team).then((rows) => { if (active) setAvailableLanguages(rows); });
    return () => { active = false; };
  }, [team]);
  if (field === "staffName") return <Col md={4}><Form.Label>{labels[field]}</Form.Label><Form.Control value={value || ""} readOnly /></Col>;
  if (field === "assignedCallMemberId" && team === "CALL") return <Col md={4}><Form.Label>{labels[field]}</Form.Label><Form.Select value={value || 0} onChange={(e) => onChange(field, Number(e.target.value || 0))}><option value={0}>Unassigned</option>{callMembers.map((item) => <option key={item.id} value={item.id}>{item.name || item.username}</option>)}</Form.Select></Col>;
  if (field === "customerLanguage") return <Col md={4}><Form.Label>{labels[field]}</Form.Label><Form.Select value={value || ""} onChange={(e) => onChange(field, e.target.value)}><option value="">Unassigned Language</option>{availableLanguages.map((language) => <option key={language.id || language.name} value={language.name}>{language.name}</option>)}</Form.Select></Col>;
  if (field === "categoryId") return <Col md={4}><Form.Label>{labels[field]}</Form.Label><Form.Select value={value || ""} onChange={(e) => { onChange("categoryId", e.target.value); onChange("destinationName", ""); onChange("pickupType", ""); onChange("addons", ""); }}><option value="">Select category</option>{categories.map((category) => <option key={category.id} value={category.id}>{category.title || category.name}</option>)}</Form.Select></Col>;
  if (field === "destinationName") return <Col md={4}><Form.Label>{labels[field]}</Form.Label><Form.Select value={value || ""} onChange={(e) => { onChange("destinationName", e.target.value); onChange("pickupType", ""); onChange("addons", ""); }}><option value="">Select trip</option>{trips.map((trip) => <option key={trip.id || trip.path} value={trip.title}>{trip.title}</option>)}</Form.Select></Col>;
  if (field === "pickupType") {
    const pickups = (pricingOptions?.pickups || []).filter((item) => item.active !== false);
    return <Col md={4}><Form.Label>{labels[field]}</Form.Label><Form.Select value={value || ""} onChange={(e) => onChange(field, e.target.value)} disabled={!pickups.length}><option value="">{pickups.length ? "Select pickup type" : "Select destination first"}</option>{pickups.map((item) => { const label = item.pickupName || item.pickupValue; return <option key={item.id} value={label}>{label}</option>; })}</Form.Select></Col>;
  }
  if (field === "addons") {
    const options = (pricingOptions?.addons || []).filter((item) => item.active !== false);
    const selected = String(value || "").split(",").map((item) => item.trim()).filter(Boolean);
    const toggle = (name) => onChange(field, selected.includes(name) ? selected.filter((item) => item !== name).join(", ") : [...selected, name].join(", "));
    return <Col xs={12}><Form.Label>{labels[field]}</Form.Label>{options.length ? <div className="d-flex flex-wrap gap-3">{options.map((item) => <Form.Check key={item.id} type="checkbox" label={item.name} checked={selected.includes(item.name)} onChange={() => toggle(item.name)} />)}</div> : <div className="small text-muted">No add-ons available for the selected destination.</div>}</Col>;
  }
  if (field === "detailsSent") return <Col md={4}><Form.Label>{labels[field]}</Form.Label><Form.Select value={value || "No"} onChange={(e) => onChange(field, e.target.value)}><option>No</option><option>Yes</option></Form.Select></Col>;
  if (field === "interestStatus") { const mustLeaveDetailsSent = calledLeadStillDetailsSent(lead); return <Col md={4} lg={6}><Form.Label>{labels[field]}</Form.Label><Form.Select value={value || ""} isInvalid={mustLeaveDetailsSent} onChange={(e) => onChange(field, e.target.value)}>{interestStatuses.map((option) => <option key={option}>{option}</option>)}</Form.Select>{mustLeaveDetailsSent && <Form.Control.Feedback type="invalid">A call has already been made to this customer - choose a new Interest Status to save.</Form.Control.Feedback>}</Col>; }
  if (field === "frequency") return null;
  if (field === "kidsAge") return <KidsAgeField value={value} kidsCount={kidsCount} onChange={(next) => onChange(field, next)} />;
  if (field === "plannedDates") return <Col md={4}><Form.Label>{labels[field]}</Form.Label><Form.Control type="date" min={todayInputDate()} value={value ?? ""} onChange={(e) => onChange(field, e.target.value)} /></Col>;
  if (field === "nextFollowUpDateTime") return <FollowUpDateTimeField value={value} disabled={followUpDisabled} suggestedValue={suggestedFollowUp} onSuggest={() => onChange(field, suggestedFollowUp)} onChange={(next) => onChange(field, next)} />;
  if (field === "dailyUpdates") return <DailyUpdateField value={value} initialValue={initialValue} suggestions={dailyUpdateSuggestions} onChange={(next) => onChange(field, next)} onPick={(id) => onChange("dailyUpdateSuggestionId", id)} />;
  // Calls on it link to this lead like calls on the main number.
  if (field === "alternateNumber" && team === "CALL") return <Col md={4}><Form.Label>{labels[field]}</Form.Label><InputGroup><Form.Control type="tel" value={value ?? ""} placeholder="Optional" onChange={(e) => onChange(field, e.target.value)} /><Button type="button" variant="outline-success" title={canCallLead(member, lead) ? "Call the alternate number" : "Customer preferred language is not assigned to you"} onClick={() => onCall?.({ ...(lead || {}), customerNumber: value })} disabled={Boolean(callingNumber) || searchablePhoneDigits(value).length < 10 || !canCallLead(member, lead)}>{callingNumber === value ? <Spinner size="sm" /> : <i className="bi bi-telephone-fill" />}</Button></InputGroup></Col>;
  if (field === "customerNumber" && team === "CALL") return <Col md={4}><Form.Label>{labels[field]}</Form.Label><InputGroup><Form.Control type="tel" value={value ?? ""} onChange={(e) => onChange(field, e.target.value)} required /><Button type="button" variant="outline-success" title={canCallLead(member, lead) ? "Call customer" : "Customer preferred language is not assigned to you"} onClick={() => onCall?.({ ...(lead || {}), customerNumber: value })} disabled={Boolean(callingNumber) || searchablePhoneDigits(value).length < 10 || !canCallLead(member, lead)}>{callingNumber === value ? <Spinner size="sm" /> : <i className="bi bi-telephone-fill" />}</Button></InputGroup></Col>;
  return <Col md={4}><Form.Label>{labels[field]}</Form.Label><Form.Control type={inputType(field)} value={value ?? ""} onChange={(e) => onChange(field, e.target.value)} required={field === "customerNumber"} /></Col>;
}


function DailyUpdateField({ value, initialValue, suggestions = [], onChange, onPick }) {
  const activeSuggestions = suggestions.filter((item) => item?.active !== false && String(item?.text || "").trim());
  const applySuggestion = (id) => {
    const suggestion = activeSuggestions.find((item) => String(item.id) === String(id));
    if (!suggestion) return;
    onChange(suggestion.text);
    // Remembered so the matching customer message is offered after saving.
    onPick?.(suggestion.id);
  };
  return <Col xs={12}>{initialValue ? <><Form.Label>Saved Daily Updates</Form.Label><Form.Control className="mb-2" as="textarea" rows={3} value={initialValue || ""} readOnly /></> : null}<Form.Label>{initialValue ? "New Daily Update" : labels.dailyUpdates}</Form.Label>{activeSuggestions.length ? <Form.Select className="mb-2" value="" onChange={(e) => applySuggestion(e.target.value)}><option value="">Select common update</option>{activeSuggestions.map((item) => <option key={item.id || item.text} value={item.id}>{item.text}</option>)}</Form.Select> : null}<Form.Control as="textarea" rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Type the update only; employee and time are added on save" /></Col>;
}
function KidsAgeField({ value, kidsCount, onChange }) {
  const count = Math.max(0, Number(kidsCount || 0));
  const ages = parseKidsAgeValue(value);
  const update = (index, nextValue) => {
    const next = Array.from({ length: count }, (_, itemIndex) => ages[itemIndex] ?? "");
    next[index] = nextValue;
    onChange(next.join(", "));
  };
  return <Col xs={12}><Form.Label>{labels.kidsAge}</Form.Label><Row className="g-2">{Array.from({ length: count }, (_, index) => <Col md={4} key={index}><InputGroup><InputGroup.Text>Kid {index + 1}</InputGroup.Text><Form.Control type="number" min="0" step="1" value={ages[index] ?? ""} onChange={(e) => update(index, e.target.value)} /><InputGroup.Text>yrs</InputGroup.Text></InputGroup></Col>)}</Row></Col>;
}

function FollowUpDateTimeField({ value, disabled = false, suggestedValue = "", onSuggest, onChange }) {
  const parsed = parseInputDateTime(value);
  const date = parsed.date || todayInputDate();
  const current = inputPartsToDateTime(date, parsed.hour12, parsed.minute, parsed.period);
  const quickDays = [2, 3, 5, 7];
  const update = (part, nextValue) => {
    const next = { date, hour12: parsed.hour12 || "12", minute: parsed.minute || "00", period: parsed.period || "PM", [part]: nextValue };
    if (!disabled) onChange(inputPartsToDateTime(next.date, next.hour12, next.minute, next.period));
  };
  return <Col md={8} lg={6}><div className="d-flex align-items-center gap-2 mb-1 flex-wrap"><Form.Label className="mb-0">{labels.nextFollowUpDateTime}</Form.Label>{suggestedValue && !disabled && <Button type="button" size="sm" variant="outline-secondary" onClick={onSuggest}>Auto-suggest</Button>}{!disabled && quickDays.map((days) => <Button key={days} type="button" size="sm" variant="outline-secondary" onClick={() => onChange(followUpAfterDays(days))}>{days}D</Button>)}</div><div className="d-flex gap-2 flex-wrap"><Form.Control type="date" min={todayInputDate()} value={disabled ? "" : date} onChange={(e) => update("date", e.target.value)} disabled={disabled} style={{ flex: "1 1 150px", minWidth: 150 }} /><Form.Select value={parsed.hour12 || "12"} onChange={(e) => update("hour12", e.target.value)} disabled={disabled} style={{ flex: "0 0 76px", minWidth: 76 }}>{Array.from({ length: 12 }, (_, i) => String(i + 1)).map((hour) => <option key={hour} disabled={!isFuturePart(date, hour, parsed.minute || "00", parsed.period || "PM")}>{hour}</option>)}</Form.Select><Form.Select value={parsed.minute || "00"} onChange={(e) => update("minute", e.target.value)} disabled={disabled} style={{ flex: "0 0 76px", minWidth: 76 }}>{Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map((minute) => <option key={minute} disabled={!isFuturePart(date, parsed.hour12 || "12", minute, parsed.period || "PM")}>{minute}</option>)}</Form.Select><Form.Select value={parsed.period || "PM"} onChange={(e) => update("period", e.target.value)} disabled={disabled} style={{ flex: "0 0 92px", minWidth: 92 }}>{["AM", "PM"].map((period) => <option key={period} disabled={!isFuturePart(date, parsed.hour12 || "12", parsed.minute || "00", period)}>{period}</option>)}</Form.Select></div>{current && !disabled && !isFutureDateTime(current) && <div className="small text-danger mt-1">Select a future time.</div>}</Col>;
}


// The itinerary plan agreed with the customer. Uploaded as soon as it is picked, it joins the booking
// when this confirmation is saved and shows in Manage Bookings' Notes column. Taking one off here only
// leaves it out of the booking.
function ItineraryDocumentsField({ team, value = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const pick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("The itinerary file must be under 10 MB");
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadLeadItinerary(team, file);
      onChange([...value, uploaded]);
      toast.success("Itinerary uploaded");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not upload the itinerary");
    } finally {
      setUploading(false);
    }
  };
  return <>
    <Form.Label>Itinerary Plan <span className="small text-muted">(optional, shown in the Booking table Notes)</span></Form.Label>
    <div className="d-flex flex-wrap align-items-center gap-2">
      {value.map((uploaded) => <span key={uploaded.id} className="badge bg-light text-dark border d-inline-flex align-items-center gap-1"><i className="bi bi-file-earmark-text" aria-hidden="true" />{uploaded.fileName}<Button size="sm" variant="link" className="p-0 ms-1 text-danger text-decoration-none" aria-label={`Remove ${uploaded.fileName}`} onClick={() => onChange(value.filter((item) => item.id !== uploaded.id))}>x</Button></span>)}
      <label className={`btn btn-sm btn-outline-primary mb-0${uploading ? " disabled" : ""}`}>{uploading ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-upload me-1" aria-hidden="true" />Upload Itinerary</>}<input type="file" hidden accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" disabled={uploading} onChange={pick} /></label>
    </div>
    <div className="small text-muted mt-1">PDF, Word or image, up to 10 MB.</div>
  </>;
}

function BookingDateTimeField({ label, value, minDate = "", minDateTime = "", onChange }) {
  const parsed = parseInputDateTime(value);
  const date = parsed.date || minDate || todayInputDate();
  const minuteOptions = minuteSelectOptions(parsed.minute);
  const update = (part, nextValue) => {
    const next = { date, hour12: parsed.hour12 || "12", minute: parsed.minute || "00", period: parsed.period || "PM", [part]: nextValue };
    onChange(inputPartsToDateTime(next.date, next.hour12, next.minute, next.period));
  };
  const current = inputPartsToDateTime(date, parsed.hour12 || "12", parsed.minute || "00", parsed.period || "PM");
  const invalid = minDateTime && current && new Date(current) <= new Date(minDateTime);
  return <Col md={6}><Form.Label>{label}</Form.Label><div className="d-flex gap-2 flex-wrap"><Form.Control type="date" min={minDate || undefined} value={date} onChange={(e) => update("date", e.target.value)} required style={{ flex: "1 1 155px", minWidth: 155 }} /><Form.Select value={parsed.hour12 || "12"} onChange={(e) => update("hour12", e.target.value)} required aria-label={`${label} hour`} style={{ flex: "0 0 76px", minWidth: 76 }}>{Array.from({ length: 12 }, (_, i) => String(i + 1)).map((hour) => <option key={hour}>{hour}</option>)}</Form.Select><Form.Select value={parsed.minute || "00"} onChange={(e) => update("minute", e.target.value)} required aria-label={`${label} minute`} style={{ flex: "0 0 82px", minWidth: 82 }}>{minuteOptions.map((minute) => <option key={minute}>{minute}</option>)}</Form.Select><Form.Select value={parsed.period || "PM"} onChange={(e) => update("period", e.target.value)} required aria-label={`${label} AM or PM`} style={{ flex: "0 0 92px", minWidth: 92 }}><option>AM</option><option>PM</option></Form.Select></div>{invalid && <div className="small text-danger mt-1">Vehicle drop date and time must be after vehicle pickup date and time.</div>}</Col>;
}
function filterLabel(filter) { if (isFlagFilter(filter)) return flagFilterLabel(filter); if (filter === "noDataReceived") return "No Data Received"; if (String(filter || "").startsWith("noDataReceived:")) return `No Data Received · ${filter.slice("noDataReceived:".length)}`; if (filter === "detailsSent") return "Details Sent"; if (filter === "callNeeded") return "Call Needed"; if (filter === "notYetCalled") return "Not Yet Called"; return filter; }
// Mirrors the backend rule: once any call has been made (answered or not), "Details Sent" (the
// "Not Yet Called" queue) is no longer true, so the employee must move the lead to another status.
// "vsk-call" for a single converter; "vsk-call (60%), chandu-call (40%)" when the credit was shared.
function conversionCreditsText(credits) {
  const rows = (credits || []).filter((row) => row?.memberName);
  if (!rows.length) return "";
  if (rows.length === 1) return rows[0].memberName;
  return rows.map((row) => `${row.memberName} (${Number(row.contributionPercent || 0)}%)`).join(", ");
}
function calledLeadStillDetailsSent(lead) { return callCount(lead, "successful") + callCount(lead, "unanswered") > 0 && String(lead?.interestStatus || "").trim().toLowerCase() === "details sent"; }
function callCount(lead, type) { return Number(type === "successful" ? (lead?.successfulCalls ?? lead?.frequency ?? lead?.actualCallsFrequency ?? 0) : (lead?.unansweredCalls ?? lead?.unresponsiveCallFrequency ?? 0)); }
function hasCallAttempt(lead) { return callCount(lead, "successful") > 0 || callCount(lead, "unanswered") > 0; }
// Exported for the trip page's employee-only "copy room images" - same sheet, same header.
export function videoFileExtension(contentType, url) {
  const byType = { "video/webm": "webm", "video/quicktime": "mov", "video/x-m4v": "m4v", "video/mp4": "mp4" };
  if (byType[contentType]) return byType[contentType];
  const match = String(url || "").split("?")[0].match(/\.([a-z0-9]{2,5})$/i);
  return match?.[1]?.toLowerCase() || "mp4";
}
// Phone photos carry an EXIF orientation tag. Browsers apply it when showing an <img>, but
// createImageBitmap has historically defaulted to ignoring it, which pastes the photo on its side.
// Asking for it explicitly keeps this the same in every browser.
const decodeUprightBitmap = (blob) => createImageBitmap(blob, { imageOrientation: "from-image" });

export async function roomImagesToSheet(blobs, hotel, category) {
  const bitmaps = await Promise.all(blobs.map((blob) => decodeUprightBitmap(blob)));
  const logoBitmap = await loadPublicImageBitmap("/img/icon.png");
  try {
    const columns = bitmaps.length === 4 ? 2 : Math.min(3, bitmaps.length);
    const rows = Math.ceil(bitmaps.length / columns);
    const margin = 40;
    const gap = 28;
    const titleHeight = 142;
    const labelHeight = 42;
    const tileWidth = 520;
    const tileHeight = 368;
    const cardPadding = 14;
    const cardWidth = tileWidth + cardPadding * 2;
    const cardHeight = labelHeight + tileHeight + cardPadding * 2;
    const canvas = document.createElement("canvas");
    canvas.width = margin * 2 + columns * cardWidth + (columns - 1) * gap;
    canvas.height = margin * 2 + titleHeight + rows * cardHeight + (rows - 1) * gap;
    const ctx = canvas.getContext("2d");
    const background = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    background.addColorStop(0, "#f8fbff");
    background.addColorStop(0.48, "#ffffff");
    background.addColorStop(1, "#fff7ed");
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const headerGradient = ctx.createLinearGradient(margin, margin, canvas.width - margin, margin + 106);
    headerGradient.addColorStop(0, "#0f172a");
    headerGradient.addColorStop(0.55, "#1d4ed8");
    headerGradient.addColorStop(1, "#f59e0b");
    drawRoundedRect(ctx, margin, margin, canvas.width - margin * 2, 106, 22, headerGradient);
    ctx.fillStyle = "rgba(255,255,255,0.16)";
    ctx.beginPath();
    ctx.arc(canvas.width - margin - 40, margin + 26, 96, 0, Math.PI * 2);
    ctx.fill();
    const logoSize = 64;
    const logoX = canvas.width - margin - logoSize - 26;
    const logoY = margin + 21;
    ctx.fillStyle = "rgba(255,255,255,0.94)";
    ctx.beginPath();
    ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
    ctx.fill();
    if (logoBitmap) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2 - 3, 0, Math.PI * 2);
      ctx.clip();
      drawCoverImage(ctx, logoBitmap, logoX + 3, logoY + 3, logoSize - 6, logoSize - 6);
      ctx.restore();
    } else {
      ctx.fillStyle = "#0f172a";
      ctx.font = "700 22px Arial, sans-serif";
      ctx.fillText("IO", logoX + 18, logoY + 40);
    }
    ctx.fillStyle = "#ffffff";
    drawFitText(ctx, `Hotel: ${hotel?.customerCode || "-"}`, margin + 28, margin + 42, logoX - margin - 54, 34, "700");
    drawFitText(
      ctx,
      `Room Category: ${category?.categoryName || "-"}   |   City: ${hotel?.city || "-"}`,
      margin + 28,
      margin + 80,
      logoX - margin - 54,
      24,
      "500",
    );
    ctx.font = "700 18px Arial, sans-serif";
    ctx.fillText("Indian Odyssey", logoX - 146, margin + 61);

    bitmaps.forEach((bitmap, index) => {
      const row = Math.floor(index / columns);
      const col = index % columns;
      const itemsInRow =
        row === rows - 1 ? bitmaps.length - row * columns || columns : columns;
      const rowWidth = itemsInRow * cardWidth + (itemsInRow - 1) * gap;
      const rowStartX = (canvas.width - rowWidth) / 2;
      const x = rowStartX + col * (cardWidth + gap);
      const y = margin + titleHeight + row * (cardHeight + gap);
      ctx.shadowColor = "rgba(15, 23, 42, 0.14)";
      ctx.shadowBlur = 18;
      ctx.shadowOffsetY = 8;
      drawRoundedRect(ctx, x, y, cardWidth, cardHeight, 18, "#ffffff");
      ctx.shadowColor = "transparent";
      ctx.fillStyle = "#111827";
      ctx.font = "700 21px Arial, sans-serif";
      ctx.fillText(`Image ${index + 1}`, x + cardPadding, y + 28);
      ctx.fillStyle = "#2563eb";
      ctx.fillRect(x + cardPadding, y + 35, 76, 4);
      drawEnhancedImageTile(
        ctx,
        bitmap,
        x + cardPadding,
        y + cardPadding + labelHeight,
        tileWidth,
        tileHeight,
      );
    });
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Could not create image sheet"));
      }, "image/png");
    });
  } finally {
    bitmaps.forEach((bitmap) => bitmap.close?.());
    logoBitmap?.close?.();
  }
}
export async function roomImageWithCompactHeader(blob, hotel, category) {
  const bitmap = await decodeUprightBitmap(blob);
  const maxOutputWidth = 1600;
  const maxOutputHeight = 1200;
  const scale = Math.min(1, maxOutputWidth / bitmap.width, maxOutputHeight / bitmap.height);
  const imageWidth = Math.max(1, Math.round(bitmap.width * scale));
  const imageHeight = Math.max(1, Math.round(bitmap.height * scale));
  const headerHeight = Math.min(72, Math.max(48, Math.round(imageHeight * 0.08)));
  const canvas = document.createElement("canvas");
  canvas.width = imageWidth;
  canvas.height = imageHeight + headerHeight;
  const ctx = canvas.getContext("2d");
  try {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, headerHeight);
    ctx.fillStyle = "#0f172a";
    const padding = Math.max(12, Math.round(headerHeight * 0.22));
    const titleSize = Math.max(14, Math.round(headerHeight * 0.3));
    const subtitleSize = Math.max(11, Math.round(headerHeight * 0.21));
    drawFitText(ctx, `Hotel: ${hotel?.customerCode || "-"}`, padding, Math.round(headerHeight * 0.42), canvas.width - padding * 2, titleSize, "700");
    drawFitText(ctx, `Room Category: ${category?.categoryName || "-"}   |   City: ${hotel?.city || "-"}`, padding, Math.round(headerHeight * 0.78), canvas.width - padding * 2, subtitleSize, "500");
    ctx.drawImage(bitmap, 0, headerHeight, imageWidth, imageHeight);
    return await new Promise((resolve, reject) => {
      canvas.toBlob((pngBlob) => {
        if (pngBlob) resolve(pngBlob);
        else reject(new Error("Could not create image"));
      }, "image/png");
    });
  } finally {
    bitmap.close?.();
  }
}
async function loadPublicImageBitmap(src) {
  try {
    const response = await fetch(src);
    if (!response.ok) return null;
    return await createImageBitmap(await response.blob());
  } catch {
    return null;
  }
}
function drawFitText(ctx, text, x, y, maxWidth, fontSize, weight = "500") {
  const family = "Arial, sans-serif";
  let size = fontSize;
  ctx.font = `${weight} ${size}px ${family}`;
  while (size > 15 && ctx.measureText(text).width > maxWidth) {
    size -= 1;
    ctx.font = `${weight} ${size}px ${family}`;
  }
  if (ctx.measureText(text).width <= maxWidth) {
    ctx.fillText(text, x, y);
    return;
  }
  let clipped = text;
  while (clipped.length > 4 && ctx.measureText(`${clipped}...`).width > maxWidth) {
    clipped = clipped.slice(0, -1);
  }
  ctx.fillText(`${clipped}...`, x, y);
}
function drawRoundedRect(ctx, x, y, width, height, radius, fillStyle) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.fillStyle = fillStyle;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}
function drawEnhancedImageTile(ctx, image, x, y, width, height) {
  ctx.save();
  roundedClip(ctx, x, y, width, height, 14);
  ctx.fillStyle = "#eff6ff";
  ctx.fillRect(x, y, width, height);
  ctx.globalAlpha = 0.34;
  ctx.filter = "blur(14px) brightness(1.18) saturate(1.18)";
  drawCoverImage(ctx, image, x - 16, y - 16, width + 32, height + 32);
  ctx.globalAlpha = 1;
  ctx.filter = "brightness(1.08) contrast(1.05) saturate(1.1)";
  drawContainedImage(ctx, image, x, y, width, height);
  ctx.restore();
  ctx.strokeStyle = "rgba(37, 99, 235, 0.14)";
  ctx.lineWidth = 2;
  roundedStroke(ctx, x, y, width, height, 14);
}
function drawContainedImage(ctx, image, x, y, width, height) {
  const scale = Math.min(width / image.width, height / image.height);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;
  const drawX = x + (width - drawWidth) / 2;
  const drawY = y + (height - drawHeight) / 2;
  ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
}
function drawCoverImage(ctx, image, x, y, width, height) {
  const scale = Math.max(width / image.width, height / image.height);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;
  const drawX = x + (width - drawWidth) / 2;
  const drawY = y + (height - drawHeight) / 2;
  ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
}
function roundedClip(ctx, x, y, width, height, radius) {
  roundedPath(ctx, x, y, width, height, radius);
  ctx.clip();
}
function roundedStroke(ctx, x, y, width, height, radius) {
  roundedPath(ctx, x, y, width, height, radius);
  ctx.stroke();
}
function roundedPath(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
function SortableTh({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  const direction = active ? (sort.dir === "asc" ? "ascending" : "descending") : "none";
  const icon = active ? (sort.dir === "asc" ? "bi-sort-up" : "bi-sort-down") : "bi-arrow-down-up";
  return <th aria-sort={direction}><button type="button" className="btn btn-link btn-sm p-0 fw-semibold text-decoration-none text-reset d-inline-flex align-items-center gap-1" onClick={() => onSort(sortKey)} title={`Sort by ${label}`} aria-label={`Sort by ${label}${active ? `, currently ${direction}` : ""}`}><span>{label}</span><i className={`bi ${icon} ${active ? "text-primary" : "text-secondary"}`} aria-hidden="true" /></button></th>;
}
function canCallLead(member, lead) {
  return true;
}
function searchablePhoneDigits(value) {
  let digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length >= 12 && digits.startsWith("91")) digits = digits.slice(2);
  if (digits.length >= 11 && digits.startsWith("0")) digits = digits.slice(1);
  return digits;
}
function inputType(field) { if (["numberOfPersons", "numberOfKids"].includes(field)) return "number"; if (field === "nextFollowUpDateTime") return "datetime-local"; if (field === "plannedDates") return "date"; return "text"; }
function hotelFacilityCostBehavior(facility) {
  if (facility?.costBehavior === "INCLUDED_EXTERNAL") return "INCLUDED_EXTERNAL";
  if (facility?.costBehavior === "OPTIONAL_PAID" || facility?.optionalWithCost === true) return "OPTIONAL_PAID";
  if (Number(facility?.extraCost || 0) > 0) return "INCLUDED_EXTERNAL";
  return "INCLUDED_BY_HOTEL";
}
function mergeHotelFacilityCostBehavior(existing, next) {
  const existingBehavior = hotelFacilityCostBehavior(existing);
  const nextBehavior = hotelFacilityCostBehavior(next);
  if (existingBehavior === "OPTIONAL_PAID" || nextBehavior === "OPTIONAL_PAID") return "OPTIONAL_PAID";
  if (existingBehavior === "INCLUDED_EXTERNAL" || nextBehavior === "INCLUDED_EXTERNAL") return "INCLUDED_EXTERNAL";
  return "INCLUDED_BY_HOTEL";
}
function paymentReceiptFingerprint(form) {
  return JSON.stringify({ customerName: form.bookingCustomerName || "", customerNumber: form.customerNumber || "", tripId: form.bookingTripId || "", quote: form.dynamicQuoteRequest || null, packageTotal: Number(form.dynamicPackageTotal || 0), customizationRequired: form.customizationRequired, customizationDescription: form.customizationDescription || "", customizationPrice: Number(form.customizationPrice || 0), customizationPriceAdjustment: form.customizationPriceAdjustment || "ADD", advancePayment: Number(form.advancePayment || 0), bookingNotes: form.bookingNotes || "", cashbackAmountPerPerson: Number(form.cashbackAmountPerPerson || 0), totalCashbackAmount: Number(form.totalCashbackAmount || 0), pickupDateTime: form.pickupDateTime || "", dropDateTime: form.dropDateTime || "", plannedDates: form.plannedDates || "", adults: Number(form.numberOfPersons || 0), kids: Number(form.numberOfKids || 0), refreshmentNeeded: Boolean(form.refreshmentNeeded), refreshmentRoomId: form.refreshmentRoomId || "", driverLanguages: form.driverLanguages || [] });
}
function createPaymentReceiptNumber(form) {
  const year = new Date().getFullYear();
  const leadNumber = String(form.leadId || form.id || "").replace(/\D/g, "") || String(Date.now()).slice(-6);
  return `IO-${year}-${leadNumber}`;
}
/** The confirmation save: the lead, the booking, and the itinerary files already uploaded for it. */
export function bookingConversionSaveRequest(lead, form) {
  return {
    lead,
    booking: bookingConversionPayload(form),
    itineraryDocumentIds: (form.itineraryDocuments || []).map((document) => document.id).filter(Boolean),
  };
}

export function bookingConversionPayload(form) {
  return {
    customerName: form.bookingCustomerName.trim(),
    tripId: Number(form.bookingTripId),
    quote: form.dynamicQuoteRequest,
    customizationRequired: form.customizationRequired === "Yes",
    customizationDescription: form.customizationRequired === "Yes" ? form.customizationDescription.trim() : null,
    customizationPrice: form.customizationRequired === "Yes" ? Number(form.customizationPrice) : 0,
    customizationPriceAdjustment: form.customizationRequired === "Yes" ? (form.customizationPriceAdjustment || "ADD") : "ADD",
    advancePayment: Number(form.advancePayment),
    bookingNotes: form.bookingNotes?.trim() || null,
    cashbackAmountPerPerson: Number(form.cashbackAmountPerPerson || 0),
    receiptNumber: form.receiptNumber,
    paymentReceiptShared: Boolean(form.paymentReceiptShared),
    travelPolicyShared: Boolean(form.travelPolicyShared),
    pickupDateTime: form.pickupDateTime,
    dropDateTime: form.dropDateTime,
    refreshmentNeeded: Boolean(form.refreshmentNeeded),
    refreshmentRoomId: form.refreshmentNeeded && form.refreshmentRoomId ? Number(form.refreshmentRoomId) : null,
    driverLanguages: form.driverLanguages || [],
    conversionCredits: normalizeConversionCreditPayload(form.conversionCredits),
  };
}
export function normalizeLead(form, team) {
  // frequency/unresponsiveCallFrequency (Successful/Unanswered Calls) are excluded here on
  // purpose: they're now captured automatically from resolved CloudConnect call outcomes, not
  // edited through this form. Submitting the stale value this form loaded them with would let a
  // later "Save Lead" click silently revert an auto-increment that happened while the modal was
  // open, since a one-step difference passes the backend's "changed by exactly one" validation
  // the same way a real undo click would.
  const { conclusion, finalStatus, followUpCategory, followUpStatus, isCallDone, detailsSent, actualCalls, actualCallsFrequency, successfulCalls, unansweredCalls, unresponsiveCalls, frequency, unresponsiveCallFrequency, bookingCustomerName, bookingCategoryId, bookingTripId, dynamicQuoteRequest, dynamicPackageTotal, customizationRequired, customizationDescription, customizationPrice, customizationPriceAdjustment, advancePayment, bookingNotes, pickupDateTime, dropDateTime, refreshmentNeeded, refreshmentRoomId, driverLanguages, receiptNumber, receiptDownloadedFingerprint, paymentReceiptShared, policyDownloadedFingerprint, travelPolicyShared, cashbackAmountPerPerson, totalCashbackAmount, flagRequested, flagNote, flagRemindAt, flags, incomingCallAnswered, callNeededMarkedBy, callNeededMarkedAt, extensionVehicleRequired, extensionVehicleAcknowledged, dailyUpdateSuggestionId, ...payload } = form;
  const kids = form.numberOfKids === "" ? 0 : Number(form.numberOfKids);
  const kidsAge = kids > 0 ? parseKidsAgeValue(form.kidsAge).slice(0, kids).filter((age) => age !== "").join(", ") : "";
  const categoryId = form.categoryId || form.bookingCategoryId;
  return { ...payload, categoryId: categoryId ? Number(categoryId) : null, numberOfPersons: form.numberOfPersons === "" ? null : Number(form.numberOfPersons), numberOfKids: kids, kidsAge, nextFollowUpDateTime: form.nextFollowUpDateTime || null, callNeededRequestedAt: form.callNeeded && form.callNeededRequestedAt ? form.callNeededRequestedAt : null };
}
function leadEditorOnlyCallNeededChanged(initial, form, member, categories, interestStatuses, team) {
  if (!initial?.id) return false;
  const initialForm = leadEditorInitialForm(initial, member, categories, interestStatuses, team);
  const ignored = new Set(["callNeededMarkedBy", "callNeededMarkedAt"]);
  const callNeededFields = new Set(["callNeeded", "callNeededNote", "callNeededRequestedAt"]);
  const changed = Object.keys(form || {}).filter((key) => {
    if (ignored.has(key)) return false;
    return leadCompareValue(form[key]) !== leadCompareValue(initialForm[key]);
  });
  return changed.length > 0 && changed.every((key) => callNeededFields.has(key));
}
function leadEditorInitialForm(initial, member, categories, interestStatuses, team) {
  const staffName = member?.username || member?.name || "";
  const defaultInterestStatus = interestStatuses[0] || blankLead.interestStatus;
  const inferredCategoryId = findCategoryIdForDestination(categories, initial.destinationName);
  const initialInterestStatus = interestStatuses.includes(initial.interestStatus) ? initial.interestStatus : defaultInterestStatus;
  return {
    ...blankLead,
    ...initial,
    categoryId: initial.categoryId || inferredCategoryId,
    staffName: initial.staffName || staffName,
    interestStatus: initialInterestStatus,
    dailyUpdates: "",
    dailyUpdateSuggestionId: "",
    chatTeamPersonNotes: team === "CHAT" || team === "ADMIN" ? "" : initial.chatTeamPersonNotes,
    plannedDates: toDateInput(initial.plannedDates),
    nextFollowUpDateTime: toInputDateTime(initial.nextFollowUpDateTime),
    flagRequested: Boolean(myLeadFlag(initial, member?.memberId)),
    flagNote: myLeadFlag(initial, member?.memberId)?.note || "",
    flagRemindAt: toReminderInput(myLeadFlag(initial, member?.memberId)?.remindAt),
    callNeededRequestedAt: toReminderInput(initial.callNeededRequestedAt),
    conversionCredits: normalizeConversionCredits(initial.conversionCredits, member),
  };
}
function defaultConversionCredits(member) {
  const memberId = member?.memberId || member?.id || "";
  return memberId ? [{ memberId: String(memberId), contributionPercent: 100 }] : [{ memberId: "", contributionPercent: 100 }];
}
function normalizeConversionCredits(rows, member = null) {
  const normalized = (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      memberId: row?.memberId != null ? String(row.memberId) : "",
      contributionPercent: row?.contributionPercent ?? "",
    }))
    .filter((row) => row.memberId || row.contributionPercent !== "");
  return normalized.length ? normalized : defaultConversionCredits(member);
}
function normalizeConversionCreditPayload(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      memberId: Number(row?.memberId || 0),
      contributionPercent: Number(row?.contributionPercent || 0),
    }));
}
function conversionCreditTotal(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + Number(row?.contributionPercent || 0), 0);
}
function conversionMemberLabel(member) {
  const name = member?.name || member?.username || `Member ${member?.id || ""}`;
  return member?.team ? `${name} (${member.team})` : name;
}
function leadCompareValue(value) {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (Array.isArray(value) || typeof value === "object") return JSON.stringify(value);
  return String(value);
}
function toInputDateTime(value) { return value ? String(value).slice(0, 16) : ""; }
function toDateInput(value) { return value && /^\d{4}-\d{2}-\d{2}/.test(String(value)) ? String(value).slice(0, 10) : value || ""; }
function badgeFor(category) { if (category === "Overdue") return "danger"; if (category === "Due Today") return "warning"; if (category === "Closed") return "secondary"; return "success"; }
function attendanceTypeLabel(value) { return value === "WEEK_OFF" ? "Week Off" : "Leave"; }
function attendanceDayLabel(value) { return value === "HALF_DAY" ? "Half Day" : "Full Day"; }
function attendanceStatusLabel(value) {
  const text = String(value || "PENDING").toLowerCase().replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}
function attendanceStatusVariant(value) {
  if (value === "APPROVED") return "success";
  if (value === "REJECTED") return "danger";
  if (value === "CANCELLED") return "secondary";
  return "warning";
}
function pad2(value) { return String(value).padStart(2, "0"); }
function formatDateOnly(value) {
  if (!value) return "-";
  const text = String(value);
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return text;
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
}
function formatDateTime(value) {
  if (!value) return "No follow-up date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const hours = date.getHours();
  const period = hours >= 12 ? "PM" : "AM";
  return `${formatDateOnly(value)} ${pad2(hours % 12 || 12)}:${pad2(date.getMinutes())} ${period}`;
}
function formatLeadViewValue(lead, key) {
  if (key === "leadWorkTotalSeconds") return <>{formatSeconds(lead.leadWorkTotalSeconds || 0)}{lead.leadWorkActive && <Badge bg="primary" className="ms-2">Active</Badge>}</>;
  if (key === "actualCallSeconds") return formatSeconds(lead.actualCallSeconds || 0);
  if (key === "callNeeded") return lead.callNeeded ? "Yes" : "No";
  if (key === "callNeededMarkedAt" || key === "callNeededRequestedAt") return lead[key] ? formatDateTime(lead[key]) : "-";
  if (key === "plannedDates" || key === "createdDate") return formatDateOnly(lead[key]);
  if (key.includes("Time") || key === "nextFollowUpDateTime") return formatDateTime(lead[key]);
  return lead[key] ?? "-";
}
function formatSeconds(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
}
function elapsedSecondsSince(startedAt, now = Date.now()) {
  if (!startedAt) return null;
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return null;
  return Math.max(0, Math.floor((Number(now) - start) / 1000));
}
function formatMinutes(totalMinutes) {
  const minutes = Math.max(0, Number(totalMinutes || 0));
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours > 0) return `${hours}h ${remainingMinutes}m`;
  return `${remainingMinutes}m`;
}
function statusConfigByName(name, statuses = []) {
  const wanted = String(name || "").trim().toLowerCase();
  if (!wanted) return null;
  return (statuses || []).find((status) => String(status?.name || "").trim().toLowerCase() === wanted) || null;
}
function suggestedFollowUpForStatus(name, statuses = []) {
  const status = statusConfigByName(name, statuses);
  const amount = Number(status?.followUpAfter || 0);
  if (!amount || amount <= 0 || !status?.followUpUnit || status?.statusAction === "CLOSE") return "";
  const date = new Date();
  if (status.followUpUnit === "DAYS") date.setDate(date.getDate() + amount);
  else if (status.followUpUnit === "MINUTES") date.setMinutes(date.getMinutes() + amount);
  else date.setHours(date.getHours() + amount);
  return toInputDateTimeFromDate(date);
}
function followUpAfterDays(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toInputDateTimeFromDate(date);
}
function toInputDateTimeFromDate(date) { return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`; }
function isCloseStatus(name, statuses = []) {
  return statusConfigByName(name, statuses)?.statusAction === "CLOSE";
}
function todayInputDate() { const date = new Date(); return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`; }
function addDaysToDateInput(dateInput, days) {
  if (!dateInput) return "";
  const date = new Date(`${dateInput}T00:00:00`);
  date.setDate(date.getDate() + Number(days || 0));
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function defaultPickupDateTime(pickupDate) { return pickupDate ? `${pickupDate}T06:00` : ""; }
function defaultDropDateTime(dropDate) { return dropDate ? `${dropDate}T22:00` : ""; }
function timeOfDateTime(value, fallback) {
  const match = String(value || "").match(/T(\d{2}:\d{2})/);
  return match ? match[1] : fallback;
}
function isTodayOrFutureDate(value) { return /^\d{4}-\d{2}-\d{2}$/.test(String(value || "")) && String(value) >= todayInputDate(); }
function isFutureDateTime(value) { const date = new Date(value); return !Number.isNaN(date.getTime()) && date.getTime() > Date.now(); }
function isFuturePart(date, hour12, minute, period) { const value = inputPartsToDateTime(date, hour12, minute, period); return value ? isFutureDateTime(value) : false; }
function nowLocalDateTime() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
}
function minuteSelectOptions(current = "00") { const base = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")); const normalized = String(Number(current || 0)).padStart(2, "0"); return base.includes(normalized) ? base : [...base, normalized].sort(); }
function inputPartsToDateTime(date, hour12, minute, period) {
  if (!date) return "";
  let hour = Number(hour12 || 12) % 12;
  if (period === "PM") hour += 12;
  return `${date}T${String(hour).padStart(2, "0")}:${minute || "00"}`;
}
function parseInputDateTime(value) {
  if (!value) return {};
  const [date, time = ""] = String(value).slice(0, 16).split("T");
  const [hourText = "12", minute = "00"] = time.split(":");
  const hour24 = Number(hourText);
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = String(hour24 % 12 || 12);
  return { date, hour12, minute: String(Number(minute || 0)).padStart(2, "0"), period };
}
// Seats this group needs in `vehicle`, worked out the way the pricing server does: a kid within the
// vehicle's concession age rides without a seat unless one was asked for, up to the vehicle's limit.
function vehicleSeatsNeeded(vehicle, quote) {
  const ageLimit = Number(vehicle?.childConcessionAgeLimit ?? 0);
  const maxWithoutSeat = Math.max(0, Number(vehicle?.maxKidsWithoutSeat ?? 0));
  const ages = (quote?.kidAges || []).map(Number);
  let withoutSeat = 0;
  ages.forEach((age, index) => {
    const asked = quote?.kidVehicleSeatNeeded?.[index];
    const seatWanted = asked == null ? age > ageLimit : Boolean(asked);
    if (!seatWanted && age <= ageLimit && withoutSeat < maxWithoutSeat) withoutSeat += 1;
  });
  const kids = Math.max(Number(quote?.kids || 0), ages.length);
  return Math.max(0, Number(quote?.adults || 0)) + kids - withoutSeat;
}
function vehicleFitsGroup(vehicle, quote) {
  return vehicleSeatsNeeded(vehicle, quote) <= Number(vehicle?.capacityMax || 0);
}
// A fixed price pickup needs the vendor's extension charge for every vehicle, added ones included.
function extensionVehicleAmountsEntered(quote) {
  return Number(quote?.extensionVehicleAmount || 0) > 0
    && (quote?.additionalVehicles || []).every((item) => Number(item.extensionVehicleAmount || 0) > 0);
}
function parseKidsAgeValue(value) { return String(value || "").split(",").map((age) => age.trim()).filter((age, index, values) => age !== "" || index < values.length - 1); }
function ensureCallFollowUpStatus(names, team) {
  if (team !== "CALL" || names.some((name) => String(name).trim().toLowerCase() === "follow-up with customer")) return names;
  return [...names, "Follow-up with Customer"];
}
function findCategoryIdForDestination(categories, destinationName) {
  const wanted = String(destinationName || "").trim().toLowerCase();
  if (!wanted) return "";
  const category = (categories || []).find((item) =>
    (item.trips || []).some((trip) => String(trip.title || "").trim().toLowerCase() === wanted)
  );
  return category?.id ? String(category.id) : "";
}

const customerMonthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function customerDate(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(value || "").trim();
  return `${Number(match[3])} ${customerMonthNames[Number(match[2]) - 1]} ${match[1]}`;
}
function customerDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const hours = date.getHours();
  return `${customerDate(toLocalDateValue(date))}, ${hours % 12 || 12}:${pad2(date.getMinutes())} ${hours >= 12 ? "PM" : "AM"}`;
}
function customerTravellers(lead) {
  const adults = Number(lead?.numberOfPersons || 0);
  const kids = Number(lead?.numberOfKids || 0);
  const parts = [];
  if (adults > 0) parts.push(`${adults} adult${adults === 1 ? "" : "s"}`);
  if (kids > 0) {
    const ages = parseKidsAgeValue(lead.kidsAge).slice(0, kids).filter((age) => age !== "");
    parts.push(`${kids} kid${kids === 1 ? "" : "s"}${ages.length ? ` (age${ages.length === 1 ? "" : "s"} ${ages.join(", ")})` : ""}`);
  }
  return parts.join(", ");
}
function customerCategoryName(lead, categories) {
  const categoryId = lead?.categoryId || findCategoryIdForDestination(categories, lead?.destinationName);
  const category = (categories || []).find((item) => String(item.id) === String(categoryId));
  return String(category?.title || category?.name || "").trim();
}
// Only what the customer should see. Internal fields (assignee, daily updates, notes, flags, call
// counters) never reach this message. Values are compared as displayed text so format-only
// differences between the opened lead and the saved response do not show up as changes.
const customerMessageFields = [
  ["Status", (lead) => String(lead?.interestStatus || "").trim()],
  ["Category", customerCategoryName],
  ["Destination", (lead) => String(lead?.destinationName || "").trim()],
  ["Planned date", (lead) => customerDate(lead?.plannedDates)],
  ["Travellers", customerTravellers],
  ["Pickup", (lead) => String(lead?.pickupType || "").trim()],
  ["Add-ons", (lead) => String(lead?.addons || "").split(",").map((item) => item.trim()).filter(Boolean).join(", ")],
  // Listed in the details only: a moved follow-up time is internal scheduling, not news for the customer.
  ["Our next follow-up", (lead) => customerDateTime(lead?.nextFollowUpDateTime), { showChange: false }],
];
// The customer message is only offered for leads in an active conversation with the customer.
const customerMessageStatuses = new Set(["follow-up with customer", "advance pending", "customisation request"]);
export function leadStatusNeedsCustomerMessage(status) {
  const normalized = String(status || "").trim().toLowerCase().replace(/customization/g, "customisation").replace(/requests$/, "request");
  return customerMessageStatuses.has(normalized);
}
// `before` is the lead as it was opened (null for a new lead); `after` is what was saved.
// `repName`, when given, names the employee in the opening line.
export function buildLeadCustomerMessage(before, after, categories = [], repName = "") {
  const rows = customerMessageFields.map(([label, read, options = {}]) => ({
    label,
    showChange: options.showChange !== false,
    previous: before ? read(before, categories) : "",
    current: read(after, categories),
  }));
  const changes = before ? rows.filter((row) => row.showChange && row.current && row.previous !== row.current) : [];
  const name = String(repName || "").trim();
  const lines = [name
    ? `Hello, this is ${name} from Indian Odyssey Trips. ${before ? "Here's an update on your trip enquiry." : "Thank you for your trip enquiry."}`
    : before ? "Hi! Here's an update on your trip enquiry with Indian Odyssey Trips." : "Hi! Thank you for your trip enquiry with Indian Odyssey Trips."];
  if (changes.length) {
    lines.push("", "*What's updated*", ...changes.map((row) => `• ${row.label}: ${row.previous ? `${row.previous} → ${row.current}` : row.current}`));
  }
  lines.push("", "*Your trip details*", ...rows.filter((row) => row.current).map((row) => `• ${row.label}: ${row.current}`));
  lines.push("", "Feel free to reply here with any questions.");
  return lines.join("\n");
}

// The company number, always given to the customer.
const COMPANY_PHONE_NUMBER = "+91 9121943442";
// The direct number behind each employee extension. Customers do not know what "802" means, so the
// message gives them this number instead.
const EXTENSION_DIRECT_NUMBERS = {
  801: "+91 80 3723 7327",
  802: "+91 80 3723 7328",
  803: "+91 80 3723 7329",
  804: "+91 80 3723 7330",
};

// The picked daily update, while the update text still begins with it and it has a customer message.
export function selectedDailyUpdateTemplate(form, suggestions = []) {
  if (!form?.dailyUpdateSuggestionId) return null;
  const suggestion = suggestions.find((item) => String(item?.id) === String(form.dailyUpdateSuggestionId));
  const text = String(suggestion?.text || "").trim();
  if (!text || !String(suggestion.customerMessage || "").trim()) return null;
  return String(form.dailyUpdates || "").trim().startsWith(text) ? suggestion : null;
}

export function dailyUpdateTemplateNeedsCallbackTime(suggestion) {
  return /\{\{\s*callback_time\s*\}\}/i.test(String(suggestion?.customerMessage || ""));
}

// The employee is named in the message's opening line ("Hello, this is …"), so it is clear who sent it.
export function fillCustomerMessageTemplate(template, { repName = "", extension = "", callbackTime = "" } = {}) {
  const directNumber = EXTENSION_DIRECT_NUMBERS[String(extension || "").trim()] || "";
  // "{{phone_number}}, extension {{extension}}" reads "+91 9121943442 or +91 80 3723 7328".
  // Without a direct number the clause goes, so the sentence still reads naturally.
  const text = String(template || "").replace(/,?\s*extension\s*\{\{\s*extension\s*\}\}/gi, directNumber ? " or {{extension}}" : "");
  const values = { rep_name: repName, phone_number: COMPANY_PHONE_NUMBER, extension: directNumber, callback_time: callbackTime };
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => (key in values ? String(values[key] || "") : match)).trim();
}

export function LeadCustomerMessageModal({ message, customerNumber = "", onHide }) {
  const [text, setText] = useState(message || "");
  useEffect(() => { setText(message || ""); }, [message]);
  const copyText = async (value, success) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(success);
    } catch {
      toast.error("Could not copy. Select it and copy it manually.");
    }
  };
  const copy = () => copyText(text, "Message copied. Paste it in WhatsApp.");
  return <Modal show={Boolean(message)} onHide={onHide} centered><Modal.Header closeButton><Modal.Title>Lead saved</Modal.Title></Modal.Header><Modal.Body>{customerNumber && <div className="d-flex align-items-center justify-content-between gap-2 border rounded px-3 py-2 mb-3"><div><div className="small">Customer number</div><div className="fw-semibold fs-5">{customerNumber}</div></div><Button type="button" variant="outline-primary" size="sm" onClick={() => copyText(customerNumber, "Number copied.")}><i className="bi bi-clipboard me-1" />Copy number</Button></div>}<div className="small mb-2">Copy this message and send it to the customer on WhatsApp. You can edit it before copying.</div><Form.Control as="textarea" rows={14} value={text} onChange={(e) => setText(e.target.value)} /></Modal.Body><Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button><Button variant="success" onClick={copy} disabled={!text.trim()}><i className="bi bi-clipboard me-1" />Copy customer message</Button></Modal.Footer></Modal>;
}
